import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { DotLottieReact } from '@lottiefiles/dotlottie-react';
import { notificar } from '../utils/notify';
import ContratoAnexo from '../components/ContratoAnexo';
import FinalizacaoManual from '../components/FinalizacaoManual';
import VinculoBadge from '../components/VinculoBadge';
import PrazoBadge from '../components/PrazoBadge';
import EditorEmail from '../components/EditorEmail';
import { contratoExigido } from '../utils/contrato';
import { copiarParaClipboard } from '../utils/credenciaisBpoRh';
import ComplementoGuiaExame from '../components/ComplementoGuiaExame';
import PlanoSaudeRegistro from '../components/PlanoSaudeRegistro';
import AvisoExameColaborador from '../components/AvisoExameColaborador';
import '../styles/bporh_admissoes.css';

// Multi-seleção com busca e checkboxes (espelha a "Unidade(s) de alocação" do
// TiqueTaque). options: [{id, nome}]; selected: array de ids; onChange(ids).
const MultiSelectBusca = ({ options, selected, onChange, placeholder = 'Selecione...' }) => {
  const [open, setOpen] = useState(false);
  const [busca, setBusca] = useState('');
  const ref = useRef(null);
  useEffect(() => {
    const fora = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', fora);
    return () => document.removeEventListener('mousedown', fora);
  }, []);
  const toggle = (id) => onChange(
    selected.includes(id) ? selected.filter(v => v !== id) : [...selected, id]);
  const nomes = options.filter(o => selected.includes(o.id)).map(o => o.nome);
  const rotulo = nomes.length === 0 ? placeholder
    : nomes.length === 1 ? nomes[0] : `${nomes.length} selecionadas`;
  const filtradas = options.filter(o =>
    o.nome.toLowerCase().includes(busca.toLowerCase()));
  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button type="button" onClick={() => setOpen(o => !o)}
        className="form-select text-start"
        style={{ background: selected.length ? '#e8f4ff' : undefined,
                 borderColor: selected.length ? '#0d6efd' : undefined }}>
        <span className={selected.length ? '' : 'text-muted'}>{rotulo}</span>
      </button>
      {open && (
        <div style={{ position: 'absolute', zIndex: 1060, background: '#fff',
          border: '1px solid #dee2e6', borderRadius: '.375rem',
          boxShadow: '0 4px 16px rgba(0,0,0,.15)', width: '100%',
          maxHeight: '260px', overflowY: 'auto', top: 'calc(100% + 2px)', left: 0 }}>
          <div className="p-2 border-bottom position-sticky top-0 bg-white">
            <input type="text" className="form-control form-control-sm" placeholder="Pesquisar..."
              value={busca} autoFocus onChange={(e) => setBusca(e.target.value)} />
          </div>
          {selected.length > 0 && (
            <div onClick={() => onChange([])} className="px-3 py-2 border-bottom text-danger"
              style={{ cursor: 'pointer', fontSize: '.82rem' }}>
              <i className="fas fa-times me-1"></i> Limpar seleção
            </div>
          )}
          {filtradas.map(o => (
            <label key={o.id} className="d-flex align-items-center px-3 py-2 gap-2 m-0"
              style={{ cursor: 'pointer', fontSize: '.85rem', borderBottom: '1px solid #f8f9fa' }}>
              <input type="checkbox" checked={selected.includes(o.id)}
                onChange={() => toggle(o.id)} />
              {o.nome}
            </label>
          ))}
          {filtradas.length === 0 && (
            <div className="px-3 py-2 text-muted" style={{ fontSize: '.82rem' }}>Nada encontrado</div>
          )}
        </div>
      )}
    </div>
  );
};

// Página de Admissões do BPO RH — espelho da Convenia (tabelas convenia_*).
// Fluxo: empresas com admissões → colaboradores da empresa → detalhe do colaborador.

// Janela de dias por visão: a lista de empresas abre com o histórico completo
// (visão de carteira); ao abrir uma empresa o foco é a operação corrente, então
// cai para 30 dias. O operador pode trocar no seletor a qualquer momento.
// valor em real, formato brasileiro (1.234,56)
const brl = (v) => (v == null ? '—'
  : Number(v).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }));

const DIAS_EMPRESAS = 36500;
const DIAS_EMPRESA_ABERTA = 30;

// Etapas do kanban de admissão da Convenia, com as cores das faixas de lá
const COR_ETAPA_CONVENIA = {
  'Aguardando preenchimento': { bg: '#fff5da', cor: '#8a6d00', borda: '#f1be48' },
  'Conferência': { bg: '#f1e8ff', cor: '#5b21b6', borda: '#8b5cf6' },
  'Preenchimento manual': { bg: '#e3f6fb', cor: '#0e6e86', borda: '#00b5cc' },
  'Concluído': { bg: '#e6f7ef', cor: '#116b47', borda: '#1fa971' },
};

// Normaliza para comparar nome de cargo/equipe entre sistemas: sem acento,
// sem caixa, espaços colapsados.
const normNome = (s) => (s || '').toLowerCase()
  .normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/\s+/g, ' ').trim();

// Equipe/departamento aceita aproximação — o nome varia entre os sistemas e
// errar a equipe não muda o contrato do colaborador.
const acharPorNome = (lista, alvo) => {
  const a = normNome(alvo);
  if (!a) return '';
  const exato = (lista || []).find(o => normNome(o.nome) === a);
  if (exato) return String(exato.id);
  const parcial = (lista || []).find(o => normNome(o.nome).includes(a) || a.includes(normNome(o.nome)));
  return parcial ? String(parcial.id) : '';
};

// CARGO exige correspondência EXATA com o catálogo do PontoMais: é o cargo que
// vai no contrato e no espelho de ponto, então aproximar ('Analista' →
// 'Analista de Dados') criaria o colaborador no cargo errado em silêncio.
const acharCargoPontoMais = (lista, alvo) => {
  const a = normNome(alvo);
  if (!a) return '';
  const exato = (lista || []).find(o => normNome(o.nome) === a);
  return exato ? String(exato.id) : '';
};

// Cargo parecido, só para orientar quem vai cadastrar no PontoMais
const cargoParecido = (lista, alvo) => {
  const a = normNome(alvo);
  if (!a) return null;
  const p = (lista || []).find(o => normNome(o.nome).includes(a) || a.includes(normNome(o.nome)));
  return p ? p.nome : null;
};

// PIN do relógio = 4 últimos dígitos do CPF (regra da operação)
const pin4DoCpf = (cpf) => {
  const d = String(cpf || '').replace(/\D/g, '');
  return d.length >= 4 ? d.slice(-4) : '';
};

const BpoRhAdmissoes = () => {
  const [empresas, setEmpresas] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [mensagem, setMensagem] = useState('');
  const [dias, setDias] = useState(DIAS_EMPRESAS);   // abre em "Todo o período"
  // a lista abre só com as PENDENTES (checklist incompleto); as concluídas entram
  // pelo filtro "Mostrar concluídas", dentro do período escolhido (07/10/2026)
  const [verConcluidas, setVerConcluidas] = useState(false);

  useEffect(() => { notificar(mensagem); }, [mensagem]);

  // Empresa selecionada (visão de colaboradores) e colaborador em detalhe (modal)
  const [empresaSelecionada, setEmpresaSelecionada] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const [colaboradorDetalhe, setColaboradorDetalhe] = useState(null);
  const [busca, setBusca] = useState('');

  // Filtros
  const [filtroOperador, setFiltroOperador] = useState('');   // visão de empresas
  const [filtroCargo, setFiltroCargo] = useState('');          // visão de colaboradores
  const [filtroDepto, setFiltroDepto] = useState('');
  const [filtroStatus, setFiltroStatus] = useState('');
  // prazo: pergunta diferente do status — 'o que vai vencer' x 'o que falta fazer'
  const [filtroPrazo, setFiltroPrazo] = useState('');
  // Período por data de admissão (AAAA-MM-DD dos inputs type=date)
  const [filtroDe, setFiltroDe] = useState('');
  const [filtroAte, setFiltroAte] = useState('');

  const periodoAtivo = Boolean(filtroDe || filtroAte);

  useEffect(() => {  // troca de empresa limpa os filtros de colaborador
    setFiltroCargo(''); setFiltroDepto(''); setFiltroStatus(''); setBusca('');
    setFiltroDe(''); setFiltroAte('');
  }, [empresaSelecionada?.cliente_id]); // eslint-disable-line react-hooks/exhaustive-deps

  // O intervalo de datas filtra o que o backend já devolveu. Com a janela em 30
  // dias, buscar datas antigas viria vazio — então usar as datas força a janela
  // para 'Todo o período', que é o único estado em que o filtro alcança tudo.
  useEffect(() => {
    if (periodoAtivo && dias !== DIAS_EMPRESAS) setDias(DIAS_EMPRESAS);
  }, [periodoAtivo, dias]);

  // Solicitações do formulário público (/admissao) pendentes de processamento
  const [solicitacoes, setSolicitacoes] = useState([]);
  const [solicitacaoDetalhe, setSolicitacaoDetalhe] = useState(null);

  // Formulário de edição da solicitação (o operador pode corrigir antes de processar)
  const [solForm, setSolForm] = useState(null);
  const [salvandoSol, setSalvandoSol] = useState(false);

  // Cargos/departamentos/centros de custo reais do cliente na Convenia — a admissão
  // só é criada corretamente se esses valores existirem lá (são entidades com UUID).
  const [refsCliente, setRefsCliente] = useState(null);

  useEffect(() => {
    if (!solicitacaoDetalhe?.bporh_cliente_id) { setRefsCliente(null); return; }
    axios
      .get(`${process.env.REACT_APP_API_BASE}/api/bporh/referencias/`, {
        params: { cliente_id: solicitacaoDetalhe.bporh_cliente_id },
        headers: { Authorization: `Bearer ${localStorage.getItem('access')}` },
      })
      .then((res) => setRefsCliente(res.data))
      .catch(() => setRefsCliente(null));
  }, [solicitacaoDetalhe]);

  useEffect(() => {
    if (!solicitacaoDetalhe) { setSolForm(null); return; }
    setSolForm({
      colaborador: solicitacaoDetalhe.colaborador || '',
      empresa: solicitacaoDetalhe.empresa || '',
      data_admissao: solicitacaoDetalhe.data_admissao_iso || '',
      funcao: solicitacaoDetalhe.funcao || '',
      departamento: solicitacaoDetalhe.departamento || '',
      centro_custo: solicitacaoDetalhe.centro_custo || '',
      salario: solicitacaoDetalhe.salario || '',
      gestor: solicitacaoDetalhe.gestor || '',
      categoria_trabalhador: solicitacaoDetalhe.categoria_trabalhador || '',
      periodo_experiencia: solicitacaoDetalhe.periodo_experiencia || '',
      horas_mensais: solicitacaoDetalhe.horas_mensais || '',
      horario_trabalho: solicitacaoDetalhe.horario_trabalho || '',
      jornada_trabalho: solicitacaoDetalhe.jornada_trabalho || '',
      filial: solicitacaoDetalhe.filial || '',
      email_colaborador: solicitacaoDetalhe.email_colaborador || '',
      telefone_colaborador: solicitacaoDetalhe.telefone_colaborador || '',
      informacoes_adicionais: solicitacaoDetalhe.informacoes_adicionais || '',
    });
  }, [solicitacaoDetalhe]);

  const setSol = (campo) => (e) => setSolForm((f) => ({ ...f, [campo]: e.target.value }));
  const [criandoConvenia, setCriandoConvenia] = useState(false);

  // Salva as edições e cria a admissão na Convenia (envia o e-mail de preenchimento
  // ao colaborador → processo entra em 'Aguardando preenchimento')
  const criarNaConvenia = async () => {
    if (!solForm || !solicitacaoDetalhe) return;
    setCriandoConvenia(true);
    const accessToken = localStorage.getItem('access');
    const headers = { Authorization: `Bearer ${accessToken}` };
    const base = `${process.env.REACT_APP_API_BASE}/api/bporh/solicitacoes-admissao/${solicitacaoDetalhe.id}`;
    try {
      await axios.patch(`${base}/`, solForm, { headers });          // garante os dados editados
      const res = await axios.post(`${base}/criar-convenia/`, {}, { headers });
      setMensagem(`${res.data.mensagem}`);
      setSolicitacaoDetalhe(null);
      carregarSolicitacoes();
      carregar();                                                    // etapa já aparece na lista
    } catch (error) {
      setMensagem(`${error.response?.data?.mensagem || 'Não foi possível criar a admissão na Convenia'}`);
    } finally {
      setCriandoConvenia(false);
    }
  };

  const salvarSolicitacao = () => {
    if (!solForm) return;
    setSalvandoSol(true);
    const accessToken = localStorage.getItem('access');
    axios
      .patch(`${process.env.REACT_APP_API_BASE}/api/bporh/solicitacoes-admissao/${solicitacaoDetalhe.id}/`,
        solForm,
        { headers: { Authorization: `Bearer ${accessToken}` } })
      .then(() => {
        setMensagem('Solicitação atualizada com sucesso');
        setSolicitacaoDetalhe(null);
        carregarSolicitacoes();
      })
      .catch((error) => {
        setMensagem(`${error.response?.data?.mensagem || 'Não foi possível salvar as alterações'}`);
      })
      .finally(() => setSalvandoSol(false));
  };

  // Descarta a solicitação do formulário (teste, duplicata, envio errado).
  // Só apaga o pedido — o que já foi criado na Convenia não é afetado.
  const [confirmandoExclusaoSol, setConfirmandoExclusaoSol] = useState(false);
  const [excluindoSol, setExcluindoSol] = useState(false);
  useEffect(() => { setConfirmandoExclusaoSol(false); }, [solicitacaoDetalhe]);

  const excluirSolicitacao = () => {
    if (!solicitacaoDetalhe) return;
    setExcluindoSol(true);
    const accessToken = localStorage.getItem('access');
    axios
      .delete(`${process.env.REACT_APP_API_BASE}/api/bporh/solicitacoes-admissao/${solicitacaoDetalhe.id}/`,
        { headers: { Authorization: `Bearer ${accessToken}` } })
      .then((res) => {
        setMensagem(`${res.data.mensagem}`);
        setSolicitacaoDetalhe(null);
        carregarSolicitacoes();
      })
      .catch((error) => {
        setMensagem(`${error.response?.data?.mensagem || 'Não foi possível excluir a solicitação'}`);
      })
      .finally(() => setExcluindoSol(false));
  };

  const carregarSolicitacoes = useCallback(() => {
    const accessToken = localStorage.getItem('access');
    axios
      .get(`${process.env.REACT_APP_API_BASE}/api/bporh/solicitacoes-admissao/`, {
        params: { status: 'Nova' },
        headers: { Authorization: `Bearer ${accessToken}` }
      })
      .then((res) => setSolicitacoes(res.data.solicitacoes || []))
      .catch(() => {});
  }, []);

  const carregar = useCallback((silencioso = false) => {
    if (!silencioso) setCarregando(true);
    const accessToken = localStorage.getItem('access');
    axios
      .get(`${process.env.REACT_APP_API_BASE}/api/bporh/admissoes/`, {
        params: { dias, ...(verConcluidas ? { concluidas: 1 } : {}) },
        headers: { Authorization: `Bearer ${accessToken}` }
      })
      .then((res) => {
        setEmpresas(res.data.empresas || []);
        setCarregando(false);
      })
      .catch((error) => {
        if (silencioso) return;   // falha do refresh de fundo não vira toast
        const errorMsg =
          error.response?.data?.erro ||
          error.response?.data?.mensagem ||
          error.message ||
          'Erro ao carregar as admissões';
        setMensagem(`${errorMsg}`);
        setCarregando(false);
      });
  }, [dias, verConcluidas]);

  useEffect(() => { carregar(); }, [carregar]);

  // A etapa do kanban muda por webhook enquanto a página está aberta (ex.:
  // 'Aguardando preenchimento' -> 'Conferência' quando o formulário é salvo).
  // Refresh silencioso a cada 60s traz isso sem o operador precisar recarregar.
  useEffect(() => {
    const t = setInterval(() => carregar(true), 60000);
    return () => clearInterval(t);
  }, [carregar]);
  useEffect(() => { carregarSolicitacoes(); }, [carregarSolicitacoes]);

  // Abrir/fechar uma empresa escreve na URL (?cliente=) — o estado segue a URL
  // no efeito abaixo, então o VOLTAR do navegador também fecha a empresa em vez
  // de sair para outra rota. A janela de dias acompanha (carteira ↔ operação).
  const abrirEmpresa = (emp) => setSearchParams({ cliente: String(emp.cliente_id) });
  const voltarParaEmpresas = () => setSearchParams({});

  useEffect(() => {
    const id = searchParams.get('cliente');
    if (!id) {
      if (empresaSelecionada) {          // fechou (botão ou voltar do navegador)
        setEmpresaSelecionada(null);
        setBusca('');
        setDias(DIAS_EMPRESAS);
      }
      return;
    }
    if (!empresaSelecionada || String(empresaSelecionada.cliente_id) !== id) {
      const emp = empresas.find(e => String(e.cliente_id) === id);
      if (emp) {
        setEmpresaSelecionada(emp);
        // vindo do e-mail de finalização a admissão pode ser antiga: período todo
        setDias(searchParams.get('finalizar') ? DIAS_EMPRESAS : DIAS_EMPRESA_ABERTA);
        if (searchParams.get('finalizar')) setVerConcluidas(true);
      }
    }
  }, [searchParams, empresas]); // eslint-disable-line react-hooks/exhaustive-deps

  // link do e-mail de "Solicitar finalização": ?cliente=X&finalizar=<convenia_id>
  // abre direto o checklist daquele colaborador
  useEffect(() => {
    const conv = searchParams.get('finalizar');
    if (!conv || !empresaSelecionada) return;
    const c = (empresaSelecionada.colaboradores || []).find(x => String(x.convenia_id) === conv);
    if (c && (!colaboradorDetalhe || String(colaboradorDetalhe.convenia_id) !== conv)) {
      setAbaDetalhe('checklist');
      setColaboradorDetalhe(c);
    }
  }, [searchParams, empresaSelecionada]); // eslint-disable-line react-hooks/exhaustive-deps

  // Mantém a empresa selecionada atualizada quando a janela de dias muda.
  // Se a nova janela não trouxer admissões dela, a empresa continua aberta com a
  // lista vazia — sair da tela sozinho confundiria quem acabou de clicar no card.
  useEffect(() => {
    if (!empresaSelecionada) return;
    const atualizada = empresas.find(e => e.cliente_id === empresaSelecionada.cliente_id);
    setEmpresaSelecionada(atualizada || { ...empresaSelecionada, colaboradores: [], total: 0 });
  }, [empresas]); // eslint-disable-line react-hooks/exhaustive-deps

  // Visão de empresas: filtro por operador
  const empresasFiltradas = React.useMemo(() =>
    filtroOperador ? empresas.filter(e => e.operador === filtroOperador) : empresas,
    [empresas, filtroOperador]);

  const operadoresDisponiveis = React.useMemo(() =>
    [...new Set(empresas.map(e => e.operador).filter(Boolean))].sort(),
    [empresas]);

  const totalColaboradores = empresasFiltradas.reduce((s, e) => s + (e.total || 0), 0);

  // Resumo da carteira: concluídas, pendentes e em qual atividade estão paradas
  const resumo = React.useMemo(() => {
    let concluidas = 0, pendentes = 0;
    const porTarefa = {};
    empresasFiltradas.forEach(e => (e.colaboradores || []).forEach(c => {
      const pend = (c.tarefas || []).filter(t => t.estado === 'pendente');
      if (pend.length === 0) {
        concluidas += 1;
      } else {
        pendentes += 1;
        // conta a próxima atividade de cada admissão travada (onde ela está parada)
        porTarefa[pend[0].rotulo] = (porTarefa[pend[0].rotulo] || 0) + 1;
      }
    }));
    return {
      concluidas,
      pendentes,
      tarefas: Object.entries(porTarefa).sort((a, b) => b[1] - a[1]),
    };
  }, [empresasFiltradas]);

  // ─── Exportar para Excel (CSV com BOM — abre direto no Excel) ───
  // Exporta o que está na tela: na visão de empresas, todas as admissões da
  // janela (respeitando operador e o intervalo de datas); dentro de uma
  // empresa, exatamente a lista filtrada.
  const exportarExcel = () => {
    const de = isoOrdenavel(filtroDe);
    const ate = isoOrdenavel(filtroAte);
    const noPeriodo = (c) => {
      if (!de && !ate) return true;
      const d = dataOrdenavel(c.data_admissao);
      if (!d) return false;
      if (de && d < de) return false;
      if (ate && d > ate) return false;
      return true;
    };

    const linhas = empresaSelecionada
      ? colaboradoresFiltrados.map(c => ({ empresa: empresaSelecionada.cliente, operador: empresaSelecionada.operador, c }))
      : empresasFiltradas.flatMap(e =>
          e.colaboradores.filter(noPeriodo).map(c => ({ empresa: e.cliente, operador: e.operador, c })));

    if (linhas.length === 0) {
      setMensagem('Nada para exportar com os filtros atuais');
      return;
    }

    const esc = (v) => `"${String(v ?? '').replaceAll('"', '""')}"`;
    const csv = [
      ['Empresa', 'Operador', 'Colaborador', 'Cargo', 'Departamento', 'Centro de Custo',
       'Data Admissão', 'Fim Experiência', 'Etapa Convenia', 'Status Checklist',
       'Tarefas Concluídas', 'Tarefas Total', 'Concluído em', 'E-mail'].map(esc).join(';'),
      ...linhas.map(({ empresa, operador, c }) => {
        const st = statusChecklist(c);
        return [
          empresa, operador || '', nomeCompleto(c), c.cargo || '', c.departamento || '',
          c.centro_custo || '', c.data_admissao || '', c.fim_experiencia || '',
          etapaExibida(c)?.nome || '', st.rotulo,
          c.tarefas_concluidas ?? '', c.tarefas_total ?? '',
          st.tipo === 'ok' ? (st.data || '') : '', c.email || '',
        ].map(esc).join(';');
      }),
    ].join('\r\n');

    const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    const periodo = (filtroDe || filtroAte)
      ? `${filtroDe || 'inicio'}_a_${filtroAte || 'hoje'}`
      : 'todo_periodo';
    a.download = `relatorio_admissoes_${periodo}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
    setMensagem(`Relatório exportado (${linhas.length} admissão${linhas.length > 1 ? 'ões' : ''})`);
  };

  // Etapa Convenia exibida: checklist 100% concluído = admissão finalizada.
  // Mostra a etapa final mesmo que o card na Convenia não tenha sido movido
  // (lá o kanban é manual e costuma ficar para trás).
  const etapaExibida = (c) => {
    if (!c.etapa_admissao) return null;
    if (c.etapa_admissao !== 'Concluído' && statusChecklist(c).tipo === 'ok') {
      return { nome: 'Concluído', inferida: true };
    }
    return { nome: c.etapa_admissao, inferida: false };
  };

  // dd/mm/aaaa -> número ordenável (sem data vai para o fim)
  const dataOrdenavel = (d) => {
    if (!d) return 0;
    const p = String(d).split('/');
    return p.length === 3 ? parseInt(p[2] + p[1] + p[0], 10) : 0;
  };

  // Status do checklist: 'Concluído' só quando TODAS as tarefas estiverem concluídas;
  // caso contrário, mostra a próxima tarefa pendente (na ordem do template).
  const statusChecklist = (c) => {
    const tarefas = c.tarefas || [];
    const pendentes = tarefas.filter(t => t.estado === 'pendente');
    if (!c.tem_checklist || c.tarefas_total === 0) {
      return { tipo: 'sem', rotulo: 'Sem checklist' };
    }
    if (pendentes.length === 0) {
      // data da conclusão: a real da Convenia (quando a admissão foi processada);
      // se não houver, a da última tarefa marcada aqui.
      const datas = tarefas.map(t => t.concluida_em).filter(Boolean);
      const ultima = datas.sort((a, b) => dataOrdenavel(a) - dataOrdenavel(b)).pop();
      return {
        tipo: 'ok',
        rotulo: 'Concluído',
        data: c.admissao_criada_em || c.etapa_admissao_em || ultima,
        por: c.admissao_criada_por,
      };
    }
    return {
      tipo: 'pendente',
      rotulo: pendentes[0].rotulo,
      restantes: pendentes.length - 1,
      total_pendentes: pendentes.length,
    };
  };

  // memoizado porque alimenta as dependências de dois useMemo abaixo
  const statusDoColab = React.useCallback((c) => statusChecklist(c).rotulo,
    []); // eslint-disable-line react-hooks/exhaustive-deps

  // 'AAAA-MM-DD' (input date) -> mesmo número comparável de dataOrdenavel
  const isoOrdenavel = (d) => (d ? parseInt(d.replaceAll('-', ''), 10) : 0);

  const colaboradoresFiltrados = React.useMemo(() => {
    if (!empresaSelecionada) return [];
    const b = busca.trim().toLowerCase();
    const de = isoOrdenavel(filtroDe);
    const ate = isoOrdenavel(filtroAte);
    return empresaSelecionada.colaboradores
      .filter(c => {
        if (filtroCargo && c.cargo !== filtroCargo) return false;
        if (filtroDepto && c.departamento !== filtroDepto) return false;
        if (filtroStatus && statusDoColab(c) !== filtroStatus) return false;
        if (filtroPrazo) {
          const sit = c.prazo?.situacao;
          if (filtroPrazo === 'pendente' && sit === 'entregue') return false;
          if (filtroPrazo === 'vencendo' && !['atrasado', 'hoje', 'critico', 'alerta'].includes(sit)) return false;
          if (filtroPrazo !== 'pendente' && filtroPrazo !== 'vencendo'
              && sit !== filtroPrazo) return false;
        }
        if (de || ate) {
          // sem data de admissão fica fora quando há filtro de período
          const d = dataOrdenavel(c.data_admissao);
          if (!d) return false;
          if (de && d < de) return false;
          if (ate && d > ate) return false;
        }
        if (b && ![c.nome, c.sobrenome, c.cargo, c.departamento, c.email]
          .filter(Boolean).join(' ').toLowerCase().includes(b)) return false;
        return true;
      })
      .sort((a, c) => dataOrdenavel(c.data_admissao) - dataOrdenavel(a.data_admissao));
  }, [empresaSelecionada, busca, filtroCargo, filtroDepto, filtroStatus, filtroPrazo,
      filtroDe, filtroAte, statusDoColab]);

  const opcoesCargo = React.useMemo(() =>
    [...new Set((empresaSelecionada?.colaboradores || []).map(c => c.cargo).filter(Boolean))].sort(),
    [empresaSelecionada]);
  const opcoesDepto = React.useMemo(() =>
    [...new Set((empresaSelecionada?.colaboradores || []).map(c => c.departamento).filter(Boolean))].sort(),
    [empresaSelecionada]);
  const opcoesStatus = React.useMemo(() =>
    [...new Set((empresaSelecionada?.colaboradores || []).map(statusDoColab))].sort(),
    [empresaSelecionada, statusDoColab]);

  const nomeCompleto = (c) =>
    [c.nome, c.sobrenome].filter(Boolean).join(' ') || c.nome_social || '-';

  const formatarMoeda = (v) =>
    v == null ? '-' : `R$ ${Number(v).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`;

  // Renderização recursiva do registro completo da Convenia (dados_completos):
  // objetos viram blocos rotulados, listas viram itens numerados, vazios são omitidos.
  const valorVazio = (v) =>
    v == null || v === '' ||
    (Array.isArray(v) && v.length === 0) ||
    (typeof v === 'object' && !Array.isArray(v) && Object.keys(v).length === 0);

  const RenderValor = ({ valor, nivel = 0 }) => {
    if (typeof valor === 'boolean') return <span>{valor ? 'Sim' : 'Não'}</span>;
    if (typeof valor !== 'object') return <span style={{ wordBreak: 'break-word' }}>{String(valor)}</span>;
    if (Array.isArray(valor)) {
      return (
        <div>
          {valor.map((item, i) => (
            <div key={i} className="border rounded p-2 mb-1 bg-white">
              <RenderValor valor={item} nivel={nivel + 1} />
            </div>
          ))}
        </div>
      );
    }
    return (
      <div className="row g-1">
        {Object.entries(valor).filter(([, v]) => !valorVazio(v)).map(([k, v]) => (
          <div key={k} className={typeof v === 'object' ? 'col-12' : 'col-md-4'}>
            <span className="small text-muted">{k}: </span>
            {typeof v === 'object' ? (
              <div className="ps-3 border-start ms-1 mt-1">
                <RenderValor valor={v} nivel={nivel + 1} />
              </div>
            ) : (
              <span className="fw-semibold small"><RenderValor valor={v} nivel={nivel + 1} /></span>
            )}
          </div>
        ))}
      </div>
    );
  };

  const [mostrarCompleto, setMostrarCompleto] = useState(false);
  useEffect(() => { setMostrarCompleto(false); }, [colaboradorDetalhe]);

  // Aba do modal do colaborador: linha da tabela abre o CHECKLIST;
  // o botão "Detalhes" (olho) abre os DADOS do colaborador.
  const [abaDetalhe, setAbaDetalhe] = useState('checklist');

  // Formulário público que originou a admissão (solicitacao_admissao): buscado
  // pelo cliente e casado por nome — aparece na aba Dados do detalhe
  const [solicitacaoOrigem, setSolicitacaoOrigem] = useState(null);

  useEffect(() => {
    setSolicitacaoOrigem(null);
    if (!colaboradorDetalhe || !empresaSelecionada) return;
    const norm = (v) => (v || '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').trim();
    const alvo = norm([colaboradorDetalhe.nome, colaboradorDetalhe.sobrenome].filter(Boolean).join(' '));
    const accessToken = localStorage.getItem('access');
    axios
      .get(`${process.env.REACT_APP_API_BASE}/api/bporh/solicitacoes-admissao/`, {
        params: { cliente_id: empresaSelecionada.cliente_id },
        headers: { Authorization: `Bearer ${accessToken}` },
      })
      .then((res) => {
        const todas = res.data.solicitacoes || [];
        // nome exato primeiro; senão, um contido no outro (nome abreviado)
        const achada = todas.find(x => norm(x.colaborador) === alvo)
          || todas.find(x => alvo && (norm(x.colaborador).includes(alvo) || alvo.includes(norm(x.colaborador))));
        setSolicitacaoOrigem(achada || null);
      })
      .catch(() => {});
  }, [colaboradorDetalhe, empresaSelecionada]);

  // Exclusão da admissão (só do banco do Saber — na Convenia é manual)
  const [confirmandoExclusao, setConfirmandoExclusao] = useState(false);
  const [excluindoAdmissao, setExcluindoAdmissao] = useState(false);
  useEffect(() => { setConfirmandoExclusao(false); }, [colaboradorDetalhe]);

  const excluirAdmissao = () => {
    if (!colaboradorDetalhe || !empresaSelecionada) return;
    setExcluindoAdmissao(true);
    const accessToken = localStorage.getItem('access');
    axios
      .delete(`${process.env.REACT_APP_API_BASE}/api/bporh/admissoes/excluir/`, {
        params: {
          cliente_id: empresaSelecionada.cliente_id,
          convenia_id: colaboradorDetalhe.convenia_id,
        },
        headers: { Authorization: `Bearer ${accessToken}` },
      })
      .then((res) => {
        setMensagem(`${res.data.mensagem}`);
        setColaboradorDetalhe(null);
        carregar();
      })
      .catch((error) => {
        setMensagem(`${error.response?.data?.mensagem || 'Não foi possível excluir a admissão'}`);
      })
      .finally(() => setExcluindoAdmissao(false));
  };

  // Marcação de tarefa do checklist (salva na hora em bporh_admissao_tarefas)
  const [salvandoTarefa, setSalvandoTarefa] = useState(null);

  // ─── Automação: aviso ao gestor por e-mail ───
  const [avisoPrevia, setAvisoPrevia] = useState(null);   // { ...dados da prévia }
  const [avisoForm, setAvisoForm] = useState(null);       // campos editáveis
  const [carregandoPrevia, setCarregandoPrevia] = useState(null);  // campo da tarefa em carregamento
  const [enviandoAviso, setEnviandoAviso] = useState(false);
  const [verPreviaEmail, setVerPreviaEmail] = useState(false);

  // automações de e-mail por tarefa: rota da API + rótulo do modal
  const EMAIL_AUTOMACOES = {
    aviso_gestor: { rota: 'aviso-gestor', titulo: 'Aviso ao Gestor', paraQuem: 'quem preencheu o formulário' },
    email_contratos: { rota: 'email-contratos', titulo: 'E-mail Contratos', paraQuem: 'o colaborador' },
    email_ponto: { rota: 'email-ponto', titulo: 'E-mail do Ponto', paraQuem: 'o colaborador' },
  };

  // ─── Automação do Exame Admissional: pedido à clínica com a guia em PDF ───
  // Mesmo endpoint e mesmos dados da guia do Exame Mensal (bporh_exame_config);
  // ao enviar, a tarefa vira 'Solicitado' sozinha.
  // e-mail do exame admissional ao colaborador (data, horário, clínica, endereço + guia)
  const [avisoExameAdm, setAvisoExameAdm] = useState(null);   // colaborador
  const [exameAdm, setExameAdm] = useState(null);   // {colab, remetentes, config, operador_id, data, assunto, corpo, historico, destinatario}
  const [exameAdmCarregando, setExameAdmCarregando] = useState(false);
  const [exameAdmEnviando, setExameAdmEnviando] = useState(false);
  const abrirExameAdm = (colab) => {
    const cliente_id = empresaSelecionada?.cliente_id || colab.cliente_id;
    setExameAdmCarregando(true);
    axios.get(`${process.env.REACT_APP_API_BASE}/api/bporh/exames/solicitar/`, {
      params: { cliente_id, convenia_id: colab.convenia_id, tipo: 'admissional' },
      headers: { Authorization: `Bearer ${localStorage.getItem('access')}` },
    })
      .then((res) => {
        const d = res.data;
        setExameAdm({
          colab, cliente_id, remetentes: d.remetentes || [], config: d.config || {},
          operador_id: (d.remetentes || [])[0]?.id || '', data: '',
          assunto: d.email?.assunto || '', corpo: d.email?.corpo || '',
          historico: d.historico || [], destinatario: d.config?.clinica_email || '', destSalvando: false,
          faltando: d.faltando || [],
        });
      })
      .catch((err) => setMensagem(`❌ ${err.response?.data?.mensagem || 'Não foi possível montar a solicitação do exame'}`))
      .finally(() => setExameAdmCarregando(false));
  };
  // 'Visualizar e-mail': o e-mail montado (moldura Comece, de/para/cc) + a guia PDF,
  // exatamente como vai sair — todo envio passa por prévia
  const [emailPreviewAdm, setEmailPreviewAdm] = useState(null);
  const visualizarEmailAdm = () => {
    if (!exameAdm) return;
    setEmailPreviewAdm({ carregando: true });
    const cab = { headers: { Authorization: `Bearer ${localStorage.getItem('access')}` } };
    Promise.all([
      axios.post(`${process.env.REACT_APP_API_BASE}/api/bporh/exames/solicitar/`, {
        cliente_id: Number(exameAdm.cliente_id), convenia_id: exameAdm.colab.convenia_id, visualizar: true,
        tipo_exame: 'admissional', data_exame: exameAdm.data || null, operador_id: exameAdm.operador_id || null,
        assunto: exameAdm.assunto, corpo: exameAdm.corpo,
      }, cab),
      axios.get(`${process.env.REACT_APP_API_BASE}/api/bporh/exames/solicitar/`, {
        params: { cliente_id: exameAdm.cliente_id, convenia_id: exameAdm.colab.convenia_id, pdf: 1,
                  tipo: 'admissional', data_exame: exameAdm.data || '' },
        ...cab, responseType: 'blob' }),
    ])
      .then(([email, pdf]) => setEmailPreviewAdm({
        ...email.data, carregando: false,
        pdfUrl: URL.createObjectURL(new Blob([pdf.data], { type: 'application/pdf' })),
      }))
      .catch((err) => {
        setEmailPreviewAdm(null);
        setMensagem(err.response?.data?.mensagem || 'Não foi possível montar a prévia do e-mail');
      });
  };
  const fecharPreviewAdm = () => {
    if (emailPreviewAdm?.pdfUrl) URL.revokeObjectURL(emailPreviewAdm.pdfUrl);
    setEmailPreviewAdm(null);
  };
  const verGuiaAdm = () => {
    if (!exameAdm) return;
    axios.get(`${process.env.REACT_APP_API_BASE}/api/bporh/exames/solicitar/`, {
      params: { cliente_id: exameAdm.cliente_id, convenia_id: exameAdm.colab.convenia_id, pdf: 1,
                tipo: 'admissional', data_exame: exameAdm.data || '' },
      headers: { Authorization: `Bearer ${localStorage.getItem('access')}` }, responseType: 'blob',
    })
      .then((res) => window.open(URL.createObjectURL(res.data), '_blank'))
      .catch(() => setMensagem('❌ Não foi possível gerar a guia'));
  };
  const salvarDestinatarioAdm = () => {
    if (!exameAdm) return;
    const email = (exameAdm.destinatario || '').trim();
    if (!email) { setMensagem('Informe o e-mail da clínica'); return; }
    setExameAdm(x => ({ ...x, destSalvando: true }));
    axios.post(`${process.env.REACT_APP_API_BASE}/api/bporh/exames/solicitar/`,
      { cliente_id: Number(exameAdm.cliente_id), config: { ...exameAdm.config, clinica_email: email } },
      { headers: { Authorization: `Bearer ${localStorage.getItem('access')}` } })
      .then((res) => setExameAdm(x => ({ ...x, config: res.data.config, destinatario: res.data.config?.clinica_email || email, destSalvando: false })))
      .catch((err) => { setMensagem(`❌ ${err.response?.data?.mensagem || 'Não foi possível salvar o destinatário'}`); setExameAdm(x => ({ ...x, destSalvando: false })); });
  };
  // depois do pedido (por e-mail ou pelo portal), a tarefa do checklist vira 'Solicitado'
  const concluirPedidoExame = (mensagemOk) => {
    const colab = exameAdm.colab;
    const tarefa = (colab.tarefas || []).find(t => t.campo === 'ex_admissional');
    setExameAdm(null);
    setEmailPreviewAdm(null);
    setMensagem(`✅ ${mensagemOk}`);
    if (tarefa && tarefa.estado !== 'concluida') marcarTarefa(colab, tarefa, 'Solicitado');
  };
  const enviarExameAdm = () => {
    if (!exameAdm) return;
    if (!exameAdm.operador_id) { setMensagem('Escolha o operador que assina o envio'); return; }
    if (!exameAdm.config?.clinica_email) { setMensagem('Informe e salve o e-mail da clínica antes de enviar'); return; }
    setExameAdmEnviando(true);
    axios.post(`${process.env.REACT_APP_API_BASE}/api/bporh/exames/solicitar/`, {
      cliente_id: Number(exameAdm.cliente_id), convenia_ids: [exameAdm.colab.convenia_id],
      operador_id: exameAdm.operador_id, tipo_exame: 'admissional', data_exame: exameAdm.data || null,
      assunto: exameAdm.assunto, corpo: exameAdm.corpo,
    }, { headers: { Authorization: `Bearer ${localStorage.getItem('access')}` } })
      .then((res) => concluirPedidoExame(res.data.mensagem || 'Solicitação enviada à clínica'))
      .catch((err) => setMensagem(`❌ ${err.response?.data?.mensagem || 'Não foi possível enviar'}`))
      .finally(() => setExameAdmEnviando(false));
  };
  const registrarExamePortalAdm = () => {
    if (!exameAdm) return;
    setExameAdmEnviando(true);
    axios.post(`${process.env.REACT_APP_API_BASE}/api/bporh/exames/solicitar/`, {
      cliente_id: Number(exameAdm.cliente_id), convenia_ids: [exameAdm.colab.convenia_id],
      tipo_exame: 'admissional', data_exame: exameAdm.data || null, via: 'portal',
    }, { headers: { Authorization: `Bearer ${localStorage.getItem('access')}` } })
      .then((res) => concluirPedidoExame(res.data.mensagem || 'Pedido registrado'))
      .catch((err) => setMensagem(`❌ ${err.response?.data?.mensagem || 'Não foi possível registrar'}`))
      .finally(() => setExameAdmEnviando(false));
  };

  // ─── Automação da tarefa Ponto: criar o colaborador no PontoMais ───
  // (API interna do PontoMais — só para clientes com Ponto Mais em Serviços)
  const [pontoAdmit, setPontoAdmit] = useState(null);       // formulário do modal
  const [pontoOpcoes, setPontoOpcoes] = useState(null);     // {equipes,cargos,turnos}
  const [salvandoPontoAdmit, setSalvandoPontoAdmit] = useState(false);
  const [carregandoPontoAdmit, setCarregandoPontoAdmit] = useState(false);

  const dataISOAdmissao = (v) => {
    // 'dd/mm/aaaa' (Convenia) -> 'aaaa-mm-dd' (input date); vazio se não parsear
    const m = /^(\d{2})\/(\d{2})\/(\d{4})/.exec(v || '');
    return m ? `${m[3]}-${m[2]}-${m[1]}` : '';
  };

  const abrirPontoAdmit = (colab) => {
    if (!empresaSelecionada) return;
    setCarregandoPontoAdmit(true);
    const auth = { headers: { Authorization: `Bearer ${localStorage.getItem('access')}` } };
    const base = `${process.env.REACT_APP_API_BASE}/api/bpo-ponto/${empresaSelecionada.cliente_id}/pontomais`;
    Promise.all([
      axios.get(`${base}/opcoes/`, auth),
      // cadastro AO VIVO da Convenia — o espelho local pode estar defasado se o
      // colaborador acabou de preencher; se falhar, cai no espelho local
      axios.get(`${base}/prefill/`, { ...auth, params: { convenia_employee_id: colab.convenia_id } })
        .then((r) => r.data.cadastro).catch(() => null),
    ])
      .then(([opc, vivo]) => {
        setPontoOpcoes(opc.data);
        const c = vivo || {};
        const cpf = c.cpf || colab.cpf || '';
        // O CARGO é o da Convenia — ela é a fonte da verdade do cadastro. Aqui só
        // procuramos o correspondente no catálogo do PontoMais; a busca é EXATA
        // (sem acento/caixa) de propósito: casar 'Analista' com 'Analista de
        // Dados' criaria o colaborador no cargo errado sem ninguém perceber.
        const alvoCargo = c.cargo || colab.cargo || '';
        const idCargo = acharCargoPontoMais(opc.data.cargos, alvoCargo);
        setPontoAdmit({
          first_name: (c.first_name || colab.nome || '').trim(),
          last_name: (c.last_name || colab.sobrenome || '').trim(),
          cpf,
          pis: c.pis || colab.pis || '',
          email: c.email || colab.email_corporativo || colab.email || '',
          matricula: c.matricula || colab.matricula || '',
          is_clt: true,
          admission_date: (c.admission_date && /^\d{4}-\d{2}-\d{2}$/.test(c.admission_date))
            ? c.admission_date : dataISOAdmissao(colab.data_admissao),
          initial_date: '',
          team_id: acharPorNome(opc.data.equipes, c.equipe || colab.time || colab.departamento),
          job_title_id: idCargo,
          cargo_convenia: alvoCargo,
          shift_id: '',
          // QR Code e PIN: o padrão vem de como a conta do cliente já opera
          // (padrao_registro, lido do espelho) — hoje só o Rubeus usa os dois.
          use_qrcode: Boolean(opc.data.padrao_registro?.usar_qrcode),
          pin: (opc.data.padrao_registro?.usar_qrcode || opc.data.padrao_registro?.usar_pin)
            ? pin4DoCpf(cpf) : '',
        });
        if (!cpf) {
          notificar('Este cadastro ainda está sem CPF na Convenia — complete lá ou digite abaixo');
        }
      })
      .catch((error) => notificar(`${error.response?.data?.erro
        || 'Este cliente não tem Ponto Mais configurado em Serviços → Ponto'}`))
      .finally(() => setCarregandoPontoAdmit(false));
  };

  // Recarrega o catálogo do PontoMais e tenta casar o cargo da Convenia de novo
  // — é o botão usado depois de cadastrar o cargo que faltava lá.
  const recarregarCargosPonto = () => {
    if (!empresaSelecionada) return;
    setCarregandoPontoAdmit(true);
    axios
      .get(`${process.env.REACT_APP_API_BASE}/api/bpo-ponto/${empresaSelecionada.cliente_id}/pontomais/opcoes/`,
        { headers: { Authorization: `Bearer ${localStorage.getItem('access')}` },
          params: { _t: Date.now() } })
      .then((res) => {
        setPontoOpcoes(res.data);
        setPontoAdmit(m => {
          if (!m) return m;
          const id = acharCargoPontoMais(res.data.cargos, m.cargo_convenia);
          notificar(id
            ? `Cargo "${m.cargo_convenia}" encontrado no PontoMais`
            : `"${m.cargo_convenia}" ainda não existe no PontoMais`);
          return { ...m, job_title_id: id || m.job_title_id };
        });
      })
      .catch((error) => notificar(`${error.response?.data?.erro || 'Não foi possível recarregar os cargos'}`))
      .finally(() => setCarregandoPontoAdmit(false));
  };

  const salvarPontoAdmit = () => {
    const m = pontoAdmit;
    if (!m.first_name || !m.last_name || !m.cpf || !m.pis || !m.admission_date
        || !m.team_id || !m.shift_id) {
      notificar('Preencha nome, sobrenome, CPF, PIS, admissão, equipe e turno');
      return;
    }
    // O cargo tem de existir no PontoMais com o MESMO nome da Convenia. Sem isso
    // não subimos o colaborador: ou entraria sem cargo, ou num cargo parecido.
    if (!m.job_title_id) {
      notificar(`O cargo "${m.cargo_convenia || '(vazio na Convenia)'}" não existe no `
        + `PontoMais. Cadastre-o lá com esse nome e use "Recarregar cargos".`);
      return;
    }
    if (m.pin && String(m.pin).replace(/\D/g, '').length !== 4) {
      notificar('O PIN do relógio precisa ter 4 dígitos');
      return;
    }
    setSalvandoPontoAdmit(true);
    axios
      .post(`${process.env.REACT_APP_API_BASE}/api/bpo-ponto/${empresaSelecionada.cliente_id}/pontomais/admitir/`,
        m, { headers: { Authorization: `Bearer ${localStorage.getItem('access')}` } })
      .then((res) => {
        notificar(`${res.data.nome} criado no PontoMais`);
        setPontoAdmit(null);
      })
      .catch((error) => {
        const d = error.response?.data;
        const det = d?.detalhes ? ` — ${typeof d.detalhes === 'string' ? d.detalhes : JSON.stringify(d.detalhes)}` : '';
        notificar(`${d?.erro || 'Não foi possível criar no PontoMais'}${det}`);
      })
      .finally(() => setSalvandoPontoAdmit(false));
  };

  // ─── Sistema de ponto escolhido para lançar o colaborador (Gauss tem 2:
  //     TiqueTaque e Kairos). Kairos/RHiD são manuais (só tag, sem Cadastrar).
  // padrão de tag: azulzinho (mesmo do "Ponto Mais") em todas as tarefas
  const TAG_AZUL = { bg: '#e3f2fd', cor: '#0a6ea1', borda: '#0a6ea1' };
  const SIS_PONTO_COR = {
    pontomais: TAG_AZUL, tiquetaque: TAG_AZUL, kairos: TAG_AZUL, rhid: TAG_AZUL,
  };
  const [pontoSistema, setPontoSistema] = useState('');
  useEffect(() => {
    const sis = empresaSelecionada?.sistemas_ponto || [];
    const auto = sis.find(s => !s.manual);   // prefere um com Cadastrar por API
    setPontoSistema((auto || sis[0])?.tipo || '');
  }, [empresaSelecionada?.cliente_id]); // eslint-disable-line react-hooks/exhaustive-deps

  // tarefa do checklist -> categoria de serviço (bporh_servico). Usado para
  // mostrar em cada tarefa qual fornecedor/sistema o cliente usa. Ponto tem
  // tratamento próprio (seletor/tag), então fica de fora deste mapa.
  const CAMPO_CATEGORIA = {
    vt: 'Vale Transporte', vr_va: 'Benefícios', pla_saude: 'Plano de Saúde e Odonto',
    ex_admissional: 'Medicina do Trabalho', academia: 'Academia',
    contratos: 'Assinador Digital', arquivo: 'Controle de documentos',
    email_corporativo: 'Email',
  };
  // portais do fornecedor para a tarefa (bporh_servico.portal): vira o botão
  // "Abrir <sistema>" em quem não tem automação própria. Ponto só quando o
  // sistema é manual (Kairos/Aghora) — os outros têm o botão Cadastrar.
  const portaisDaTarefa = (campo) => {
    let cat = CAMPO_CATEGORIA[campo];
    if (campo === 'ponto' && ['kairos', 'aghora'].includes(pontoSistema)) cat = 'Ponto';
    if (!cat) return [];
    let lista = empresaSelecionada?.portais_categoria?.[cat] || [];
    // 'Benefícios' também guarda convênio de farmácia e afins — na tarefa de VA
    // só interessa a plataforma do vale
    if (campo === 'vr_va') lista = lista.filter(x => !/farm[aá]cia|alura|secury|wellhub/i.test(x.propriedade));
    if (campo === 'ponto') {
      const rot = (empresaSelecionada?.sistemas_ponto || []).find(x => x.tipo === pontoSistema)?.rotulo || '';
      const so = lista.filter(x => x.propriedade.toLowerCase().includes(rot.split(' ')[0].toLowerCase()));
      return so.length ? so : lista;
    }
    return lista;
  };
  // login/senha do portal: a listagem só diz se existe senha; revelar busca UMA
  // por vez no bporh_credencial (fica registrado quem viu)
  const [credPortal, setCredPortal] = useState({});   // {servicoId: {aberto, senha, carregando}}
  const alternarCredPortal = (pt) => {
    const atual = credPortal[pt.id] || {};
    if (atual.aberto) { setCredPortal(c => ({ ...c, [pt.id]: { ...atual, aberto: false } })); return; }
    if (atual.senha != null || !pt.tem_senha) { setCredPortal(c => ({ ...c, [pt.id]: { ...atual, aberto: true } })); return; }
    setCredPortal(c => ({ ...c, [pt.id]: { ...atual, aberto: true, carregando: true } }));
    axios.get(`${process.env.REACT_APP_API_BASE}/api/bporh-credencial/`,
      { params: { tipo: 'servico', id: pt.id, campo: 'senha' },
        headers: { Authorization: `Bearer ${localStorage.getItem('access')}` } })
      .then((res) => setCredPortal(c => ({ ...c, [pt.id]: { aberto: true, senha: res.data.valor || '', carregando: false } })))
      .catch(() => { setMensagem('❌ Não foi possível obter a senha'); setCredPortal(c => ({ ...c, [pt.id]: { aberto: false } })); });
  };
  const copiarCred = (texto, rotulo) => {
    copiarParaClipboard(texto).then(() => setMensagem(`${rotulo} copiado`)).catch(() => setMensagem('❌ Não foi possível copiar'));
  };

  // ─── Automação da tarefa Ponto: criar o funcionário no TiqueTaque ───
  // (API pública oficial — POST /employees com BasicAuth public:token)
  const [ttAdmit, setTtAdmit] = useState(null);
  const [ttOpcoes, setTtOpcoes] = useState(null);        // {escalas, empregadores}
  const [salvandoTtAdmit, setSalvandoTtAdmit] = useState(false);
  const [carregandoTtAdmit, setCarregandoTtAdmit] = useState(false);
  const [ttCidades, setTtCidades] = useState([]);        // municípios da UF (IBGE)

  const UFS = ['AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA', 'MT', 'MS',
    'MG', 'PA', 'PB', 'PR', 'PE', 'PI', 'RJ', 'RN', 'RS', 'RO', 'RR', 'SC', 'SP', 'SE', 'TO'];

  // municípios oficiais da UF via API pública do IBGE (com CORS liberado);
  // se a cidade pré-preenchida da Convenia existir na lista, normaliza para o
  // nome oficial — senão o select fica em 'Selecione...'
  const carregarCidadesTt = (uf, cidadeAtual) => {
    setTtCidades([]);
    if (!uf) return;
    axios
      .get(`https://servicodados.ibge.gov.br/api/v1/localidades/estados/${uf}/municipios?orderBy=nome`)
      .then((res) => {
        const nomes = (res.data || []).map(mn => mn.nome);
        setTtCidades(nomes);
        if (cidadeAtual) {
          const normIbge = (s) => (s || '').toLowerCase()
            .normalize('NFD').replace(/[̀-ͯ]/g, '').trim();
          const oficial = nomes.find(n => normIbge(n) === normIbge(cidadeAtual));
          setTtAdmit(m => (m ? { ...m, city: oficial || '' } : m));
        }
      })
      .catch(() => setTtCidades([]));   // IBGE fora do ar: campo vira texto livre
  };

  const abrirTtAdmit = (colab) => {
    if (!empresaSelecionada) return;
    setCarregandoTtAdmit(true);
    axios
      .get(`${process.env.REACT_APP_API_BASE}/api/bpo-ponto/${empresaSelecionada.cliente_id}/tiquetaque/opcoes/`,
        { headers: { Authorization: `Bearer ${localStorage.getItem('access')}` },
          params: { _t: Date.now() } })   // evita resposta cacheada sem unidade/banco
      .then((res) => {
        setTtOpcoes(res.data);
        const unico = (res.data.empregadores || []).length === 1
          ? res.data.empregadores[0].id : '';
        setTtAdmit({
          full_name: [colab.nome, colab.sobrenome].filter(Boolean).join(' ').trim(),
          cpf: colab.cpf || '', pis: colab.pis || '',
          email: colab.email_corporativo || colab.email || '',
          celular: colab.celular || colab.telefone || '',
          city: colab.cidade || '', state: colab.uf || '',
          matricula: colab.matricula || '',
          admission_date: dataISOAdmissao(colab.data_admissao),
          records_start_date: '',
          birth_date: dataISOAdmissao(colab.data_nascimento),
          department: colab.departamento || '',
          job_role: colab.cargo || '',
          salario: colab.salario != null ? String(colab.salario) : '',
          horas_mensais: '220',
          work_schedule: '', payment_source: unico, manager: '',
          sites: [], hours_bank_configuration: '',
          send_app_link: true,
        });
        const ufIni = (colab.uf || '').toUpperCase().trim();
        if (ufIni) carregarCidadesTt(ufIni, colab.cidade);
      })
      .catch((error) => notificar(`${error.response?.data?.erro
        || 'Este cliente não tem TiqueTaque configurado em Serviços → Ponto'}`))
      .finally(() => setCarregandoTtAdmit(false));
  };

  // número em formato pt-BR ("3.500,00", "3.500") ou americano ("3500.50");
  // trocar só a vírgula fazia "3.500,00" virar 3.5 e o valor hora ia ~1000x menor
  const numeroBr = (v) => {
    let s = String(v ?? '').trim().replace(/[^\d.,-]/g, '');
    if (!s) return NaN;
    if (s.includes(',')) {
      s = s.replace(/\./g, '').replace(',', '.');
    } else {
      const partes = s.split('.');
      // sem vírgula, ponto seguido de 3 dígitos é separador de milhar ("3.500")
      if (partes.length > 2 || (partes.length === 2 && partes[1].length === 3)) {
        s = partes.join('');
      }
    }
    return parseFloat(s);
  };

  // valor hora em centavos = salário mensal ÷ horas mensais (exigido pelo TiqueTaque)
  const valorHoraTt = (m) => {
    const sal = numeroBr(m?.salario);
    const horas = numeroBr(m?.horas_mensais);
    if (!sal || !horas || horas <= 0) return 0;
    return Math.round((sal / horas) * 100);
  };

  const salvarTtAdmit = () => {
    const m = ttAdmit;
    const cents = valorHoraTt(m);
    if (!m.full_name || !m.cpf || !m.pis || !m.city || !m.state
        || !m.work_schedule || !m.payment_source) {
      notificar('Preencha nome, CPF, NIS/PIS, cidade, UF, escala e empregador');
      return;
    }
    if ((ttOpcoes?.unidades || []).length > 0 && (m.sites || []).length === 0) {
      notificar('Selecione ao menos uma unidade de alocação');
      return;
    }
    if (cents <= 0) {
      notificar('Informe o salário mensal e as horas mensais para calcular o valor hora');
      return;
    }
    setSalvandoTtAdmit(true);
    axios
      .post(`${process.env.REACT_APP_API_BASE}/api/bpo-ponto/${empresaSelecionada.cliente_id}/tiquetaque/admitir/`,
        { ...m, hour_rate_cents: cents },
        { headers: { Authorization: `Bearer ${localStorage.getItem('access')}` } })
      .then((res) => {
        notificar(`${res.data.nome} criado no TiqueTaque${m.send_app_link ? ' — instruções do app enviadas por e-mail' : ''}`);
        setTtAdmit(null);
      })
      .catch((error) => {
        const d = error.response?.data;
        const det = d?.detalhes ? ` — ${typeof d.detalhes === 'string' ? d.detalhes : JSON.stringify(d.detalhes)}` : '';
        notificar(`${d?.erro || 'Não foi possível criar no TiqueTaque'}${det}`);
      })
      .finally(() => setSalvandoTtAdmit(false));
  };

  // ─── Automação da tarefa Ponto: criar a pessoa no RHiD (Control iD) ───
  // API oficial POST /person — cadastro básico do ponto (cargo/centro no portal)
  const [rhidAdmit, setRhidAdmit] = useState(null);
  const [rhidOpcoes, setRhidOpcoes] = useState(null);      // {empresas, departamentos}
  const [salvandoRhidAdmit, setSalvandoRhidAdmit] = useState(false);
  const [carregandoRhidAdmit, setCarregandoRhidAdmit] = useState(false);

  const abrirRhidAdmit = (colab) => {
    if (!empresaSelecionada) return;
    setCarregandoRhidAdmit(true);
    axios
      .get(`${process.env.REACT_APP_API_BASE}/api/bpo-ponto/${empresaSelecionada.cliente_id}/rhid/opcoes/`,
        { headers: { Authorization: `Bearer ${localStorage.getItem('access')}` } })
      .then((res) => {
        setRhidOpcoes(res.data);
        const umaEmpresa = (res.data.empresas || []).length === 1 ? res.data.empresas[0].id : '';
        // casa cargo/departamento da Convenia com o catálogo do RHiD (sem acento/caixa)
        const norm = (s) => (s || '').toLowerCase()
          .normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/\s+/g, ' ').trim();
        const achar = (lista, alvo) => {
          const a = norm(alvo); if (!a) return '';
          const ex = (lista || []).find(o => norm(o.nome) === a);
          if (ex) return String(ex.id);
          const p = (lista || []).find(o => norm(o.nome).includes(a) || a.includes(norm(o.nome)));
          return p ? String(p.id) : '';
        };
        setRhidAdmit({
          name: [colab.nome, colab.sobrenome].filter(Boolean).join(' ').trim(),
          cpf: colab.cpf || '', pis: colab.pis || '',
          registration: colab.matricula || '',
          idCompany: umaEmpresa,
          idDepartment: achar(res.data.departamentos, colab.departamento || colab.time),
          cargo: achar(res.data.cargos, colab.cargo),
          centro_custo: '', superior: '', horario: '', horario_hc: '',
          admission_date: dataISOAdmissao(colab.data_admissao),
          num_folha: '', ctps: '', obs: '',
          status: true, password: '', rfid: '', code: '', isAdmin: false,
        });
      })
      .catch((error) => notificar(`${error.response?.data?.erro
        || 'Este cliente não tem RHiD configurado em Serviços → Ponto'}`))
      .finally(() => setCarregandoRhidAdmit(false));
  };

  // PIS formatado como o portal mostra: 000.00000.00-0
  const pisFmt = (v) => {
    const d = String(v || '').replace(/\D/g, '');
    return d.length === 11
      ? `${d.slice(0, 3)}.${d.slice(3, 8)}.${d.slice(8, 10)}-${d.slice(10)}` : (v || '');
  };

  const salvarRhidAdmit = () => {
    const m = rhidAdmit;
    if (!m.name || !m.idCompany) {
      notificar('Preencha o nome e a empresa');
      return;
    }
    // o RHiD recusa a criação sem PIS (erro "Nullable object must have a value")
    if (!String(m.pis || '').replace(/\D/g, '')) {
      notificar('O RHiD não cria funcionário sem PIS/NIS — informe o PIS antes de salvar');
      return;
    }
    setSalvandoRhidAdmit(true);
    axios
      .post(`${process.env.REACT_APP_API_BASE}/api/bpo-ponto/${empresaSelecionada.cliente_id}/rhid/admitir/`,
        m, { headers: { Authorization: `Bearer ${localStorage.getItem('access')}` } })
      .then((res) => {
        notificar(`${res.data.nome} criado no RHiD. Finalize o cadastro no portal do `
          + `RHiD: cargo, centro de custo, data de admissão, horário e superior direto.`);
        setRhidAdmit(null);
      })
      .catch((error) => {
        const d = error.response?.data;
        const det = d?.detalhes ? ` — ${typeof d.detalhes === 'string' ? d.detalhes : JSON.stringify(d.detalhes)}` : '';
        notificar(`${d?.erro || 'Não foi possível criar no RHiD'}${det}`);
      })
      .finally(() => setSalvandoRhidAdmit(false));
  };

  // `sistema` (só no E-mail do Ponto): empresa com dois pontos (GaussFleet:
  // TiqueTaque e Kairos) escolhe o modelo; `manter` preserva remetente e
  // destinatário que o operador já ajustou quando ele troca de modelo
  const abrirEmailAutomacao = (colab, campo, sistema = null, manter = null) => {
    if (!empresaSelecionada) return;
    const aut = EMAIL_AUTOMACOES[campo];
    if (!aut) return;
    setCarregandoPrevia(campo);
    axios
      .get(`${process.env.REACT_APP_API_BASE}/api/bporh/${aut.rota}/previa/`, {
        params: { cliente_id: empresaSelecionada.cliente_id, convenia_employee_id: colab.convenia_id,
                  ...(sistema ? { sistema } : {}) },
        headers: { Authorization: `Bearer ${localStorage.getItem('access')}` },
      })
      .then((res) => {
        const d = res.data;
        setAvisoPrevia({ ...d, colab, campo, ...aut });
        setAvisoForm({
          destinatario: manter?.destinatario ?? (d.destinatario || ''),
          assunto: d.assunto || '',
          corpo_html: d.corpo_html || '',              // enviado (logo via cid:)
          corpo_previa: d.corpo_previa || d.corpo_html || '',  // exibido (logo em base64)
          // miolo editável: o operador mexe só no texto, a moldura vem do backend
          conteudo_html: d.conteudo_html || '',
          operador_id: manter?.operador_id ?? (d.operador_sugerido_id || (d.operadores?.[0]?.id ?? '')),
          // o envio memoriza a edição no modelo DESTE sistema
          ...(d.sistema ? { sistema: d.sistema } : {}),
        });
      })
      .catch((error) => setMensagem(`${error.response?.data?.mensagem || 'Não foi possível montar a prévia'}`))
      .finally(() => setCarregandoPrevia(null));
  };

  const enviarAvisoGestor = () => {
    if (!avisoForm || !avisoPrevia) return;
    setEnviandoAviso(true);
    axios
      .post(`${process.env.REACT_APP_API_BASE}/api/bporh/${avisoPrevia.rota}/enviar/`, {
        cliente_id: empresaSelecionada.cliente_id,
        convenia_employee_id: avisoPrevia.colab.convenia_id,
        ...avisoForm,
      }, { headers: { Authorization: `Bearer ${localStorage.getItem('access')}` } })
      .then((res) => {
        setMensagem(`${res.data.mensagem}`);
        setAvisoPrevia(null); setAvisoForm(null);
        carregar();   // reflete a tarefa marcada como concluída
      })
      .catch((error) => setMensagem(`${error.response?.data?.mensagem || 'Não foi possível enviar o e-mail'}`))
      .finally(() => setEnviandoAviso(false));
  };

  // obs por tarefa: {campo, texto} da linha em edição (null = nenhuma aberta)
  const [obsEditando, setObsEditando] = useState(null);
  // há PDF do contrato anexado? enquanto não houver, o botão "Concluído" da
  // tarefa "Contrato assinado" fica travado (o ContratoAnexo avisa por onEstado)
  const [temContrato, setTemContrato] = useState(false);
  // PJ, sócio, estagiário e autônomo não têm contrato de emprego: o botão
  // 'Concluído' não pode ficar travado esperando um PDF que não existe
  const [exigeContrato, setExigeContrato] = useState(true);

  // ─── Benefícios da admissão ao financeiro: VA/VT/home office do mês da admissão
  // (complemento do pedido mensal), e-mail com prévia; ao enviar, a tarefa conclui ───
  const URL_BENEF_ADM = `${process.env.REACT_APP_API_BASE}/api/bporh/admissao/beneficios-financeiro/`;
  const [benefFin, setBenefFin] = useState(null);   // {colab, dados, para, copia, operador_id, anexo, previa, carregando, enviando}
  const [carregandoBenef, setCarregandoBenef] = useState(false);
  // plano de saúde (22/10/2026): resposta do colaborador + anexo; o item do checklist
  // só conclui com o registro completo nas empresas que aderem na admissão
  const [planoColab, setPlanoColab] = useState(null);
  const authHdr = () => ({ Authorization: `Bearer ${localStorage.getItem('access')}` });
  const abrirBenefFin = (colab) => {
    const cliente_id = empresaSelecionada?.cliente_id || colab.cliente_id;
    setCarregandoBenef(true);
    axios.get(URL_BENEF_ADM, { params: { cliente_id, convenia_id: colab.convenia_id }, headers: authHdr() })
      .then((res) => {
        const d = res.data;
        const novo = { colab, cliente_id, dados: d, para: d.para || '', copia: d.copia || '',
                       operador_id: String(d.operador_sugerido_id || ''), anexo: null, previa: null };
        setBenefFin(novo);
        if ((d.itens || []).length) previaBenefFin(novo);
      })
      .catch((err) => setMensagem(err.response?.data?.mensagem || 'Não foi possível montar os benefícios'))
      .finally(() => setCarregandoBenef(false));
  };
  const corpoBenefFin = (b, extra) => {
    const fd = new FormData();
    Object.entries({ acao: 'email', cliente_id: b.cliente_id, convenia_id: b.colab.convenia_id,
                     destinatarios: b.para, copia: b.copia, operador_id: b.operador_id || '', ...extra })
      .forEach(([k, val]) => fd.append(k, val == null ? '' : val));
    if (b.anexo) fd.append('anexo', b.anexo);
    return fd;
  };
  const previaBenefFin = (b) => {
    setBenefFin((x) => ({ ...(x || b), carregando: true }));
    axios.post(URL_BENEF_ADM, corpoBenefFin(b, { enviar: 'false' }), { headers: authHdr() })
      .then((res) => setBenefFin((x) => x && ({ ...x, previa: res.data.previa, carregando: false })))
      .catch((err) => { setBenefFin((x) => x && ({ ...x, carregando: false }));
        setMensagem(err.response?.data?.mensagem || 'Não foi possível montar o e-mail'); });
  };
  const mudarBenefFin = (campo, valor) => setBenefFin((x) => ({ ...x, [campo]: valor, previa: null }));
  const salvarEmailsBenefFin = () => {
    axios.post(URL_BENEF_ADM, { acao: 'salvar_emails', cliente_id: benefFin.cliente_id,
                                destinatarios: benefFin.para, copia: benefFin.copia }, { headers: authHdr() })
      .then((res) => setMensagem(res.data.mensagem))
      .catch((err) => setMensagem(err.response?.data?.mensagem || 'Não foi possível salvar os e-mails'));
  };
  const enviarBenefFin = () => {
    const b = benefFin;
    setBenefFin((x) => ({ ...x, enviando: true }));
    axios.post(URL_BENEF_ADM, corpoBenefFin(b, { enviar: 'true', assunto: b.previa?.assunto || '',
                                                corpo_html: b.previa?.corpo_html || '' }),
      { headers: authHdr(), timeout: 120000 })
      .then((res) => {
        setMensagem(res.data.mensagem);
        setBenefFin(null);
        // garante o registro do checklist (o template vira registro) e atualiza o card
        marcarTarefa(b.colab, { campo: 'beneficios_financeiro' }, 'Concluído');
      })
      .catch((err) => { setBenefFin((x) => x && ({ ...x, enviando: false }));
        setMensagem(err.response?.data?.mensagem || 'Não foi possível enviar'); });
  };

  // ─── VA do recém-admitido: gera o arquivo da plataforma só com ele e
  // registra o crédito no Controle de VA (mesma tabela de pagamento) ───
  const [vaModal, setVaModal] = useState(null);   // {colaborador, ate, custo_dia, previa}
  const [carregandoVa, setCarregandoVa] = useState(false);
  const [gerandoVa, setGerandoVa] = useState(false);

  // padrão: até o fim do MÊS DA ADMISSÃO (pedido de 07/10/2026 — antes era o fim do
  // mês seguinte a hoje, e o crédito saía na competência errada). Sem data de
  // admissão válida, cai no fim do mês seguinte, como era.
  const fimDoMesDaAdmissao = (colab) => {
    const iso = dataISOAdmissao(colab?.data_admissao);
    const m = /^(\d{4})-(\d{2})-\d{2}$/.exec(iso || '');
    const h = new Date();
    const d = m ? new Date(Number(m[1]), Number(m[2]), 0) : new Date(h.getFullYear(), h.getMonth() + 2, 0);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  };

  // modo 'dia' = custo/dia × dias úteis; 'integral' = valor fechado do período
  const buscarPreviaVa = (colab, ate, custoDia, modo = 'dia', valorTotal = '') => {
    const accessToken = localStorage.getItem('access');
    return axios
      .get(`${process.env.REACT_APP_API_BASE}/api/bporh/admissao/va/`, {
        params: {
          cliente_id: empresaSelecionada?.cliente_id || colab.cliente_id,
          convenia_id: colab.convenia_id,
          ate,
          ...(modo !== 'integral' && custoDia ? { custo_dia: custoDia } : {}),
          ...(modo === 'integral' && valorTotal ? { valor_total: valorTotal } : {}),
        },
        headers: { Authorization: `Bearer ${accessToken}` },
      })
      .then((res) => res.data.previa);
  };

  const abrirVaAdmissao = (colab) => {
    const ate = fimDoMesDaAdmissao(colab);
    setCarregandoVa(true);
    buscarPreviaVa(colab, ate)
      .then((previa) => setVaModal({
        colab, ate, custo_dia: previa.custo_dia ?? '', modo: 'dia',
        valor_total: previa.custo_mes != null ? String(previa.custo_mes).replace('.', ',') : '', previa,
      }))
      .catch((error) => setMensagem(
        `${error.response?.data?.mensagem || 'Não foi possível montar o VA deste colaborador'}`))
      .finally(() => setCarregandoVa(false));
  };

  // recalcula a prévia quando o operador muda a data ou o custo/dia
  const recalcularVa = (campo, valor) => {
    setVaModal((v) => v && ({ ...v, [campo]: valor }));
    const alvo = { ...vaModal, [campo]: valor };
    if (!alvo.ate) return;
    // no integral, sem valor digitado ainda não há o que recalcular
    if (alvo.modo === 'integral' && !alvo.valor_total) return;
    buscarPreviaVa(alvo.colab, alvo.ate, alvo.custo_dia, alvo.modo, alvo.valor_total)
      .then((previa) => setVaModal((v) => v && ({ ...v, previa })))
      .catch(() => {});
  };

  const gerarArquivoVa = () => {
    if (!vaModal) return;
    setGerandoVa(true);
    const accessToken = localStorage.getItem('access');
    axios
      .post(`${process.env.REACT_APP_API_BASE}/api/bporh/admissao/va/`, {
        cliente_id: empresaSelecionada?.cliente_id || vaModal.colab.cliente_id,
        convenia_id: vaModal.colab.convenia_id,
        ate: vaModal.ate,
        ...(vaModal.modo !== 'integral' && vaModal.custo_dia ? { custo_dia: vaModal.custo_dia } : {}),
        ...(vaModal.modo === 'integral' && vaModal.valor_total ? { valor_total: vaModal.valor_total } : {}),
      }, { headers: { Authorization: `Bearer ${accessToken}` }, responseType: 'blob' })
      .then((res) => {
        const nome = (res.headers['content-disposition'] || '')
          .match(/filename="?([^"]+)"?/)?.[1] || 'va_admissao';
        const a = document.createElement('a');
        a.href = URL.createObjectURL(res.data);
        a.download = nome;
        a.click();
        URL.revokeObjectURL(a.href);
        setMensagem(`Arquivo de VA gerado e crédito registrado em ${vaModal.previa?.competencia_rotulo}`);
        setVaModal(null);
      })
      .catch(async (error) => {
        // erro vem como blob quando responseType é blob
        let msg = 'Não foi possível gerar o arquivo de VA';
        try {
          const txt = JSON.parse(await error.response.data.text());
          msg = txt.mensagem || txt.erro || msg;
        } catch (e) { /* mantém a mensagem padrão */ }
        setMensagem(`${msg}`);
      })
      .finally(() => setGerandoVa(false));
  };

  // ao trocar de colaborador o anexo é outro — não herdar o estado do anterior
  useEffect(() => { setTemContrato(false); }, [colaboradorDetalhe]);
  const [salvandoObsTarefa, setSalvandoObsTarefa] = useState(false);

  const salvarObsTarefa = (colab, tarefa) => {
    const texto = (obsEditando?.texto || '').trim();
    setSalvandoObsTarefa(true);
    const accessToken = localStorage.getItem('access');
    axios
      .patch(`${process.env.REACT_APP_API_BASE}/api/bporh/admissao-checklist/`, {
        cliente_id: empresaSelecionada.cliente_id,
        convenia_employee_id: colab.convenia_id,
        campo: tarefa.campo,
        obs: texto,          // '' remove a observação
      }, { headers: { Authorization: `Bearer ${accessToken}` } })
      .then(() => {
        const aplicar = (c) => c.convenia_id !== colab.convenia_id ? c : ({
          ...c,
          tarefas: c.tarefas.map(t => t.campo === tarefa.campo
            ? { ...t, obs: texto || null } : t),
        });
        setEmpresas(prev => prev.map(e => e.cliente_id !== empresaSelecionada.cliente_id ? e : ({
          ...e, colaboradores: e.colaboradores.map(aplicar),
        })));
        setColaboradorDetalhe(prev => prev ? aplicar(prev) : prev);
        setObsEditando(null);
      })
      .catch((error) => setMensagem(`${error.response?.data?.mensagem || 'Não foi possível salvar a observação'}`))
      .finally(() => setSalvandoObsTarefa(false));
  };

  const marcarTarefa = (colab, tarefa, valor) => {
    if (!empresaSelecionada) return;
    setSalvandoTarefa(tarefa.campo);

    // atualização otimista: recalcula o card e o progresso na hora
    const aplicar = (c) => {
      if (c.convenia_id !== colab.convenia_id) return c;
      const tarefas = c.tarefas.map(t => t.campo === tarefa.campo
        ? { ...t, valor, estado: valor === 'Concluído' ? 'concluida' : valor === 'Não se aplica' ? 'na' : 'pendente' }
        : t);
      const contam = tarefas.filter(t => t.estado !== 'na');
      return {
        ...c, tarefas,
        tarefas_total: contam.length,
        tarefas_concluidas: contam.filter(t => t.estado === 'concluida').length,
        tem_checklist: true,
      };
    };
    setColaboradorDetalhe(aplicar);
    setEmpresas((lista) => lista.map(e => e.cliente_id !== empresaSelecionada.cliente_id
      ? e
      : { ...e, colaboradores: e.colaboradores.map(aplicar) }));

    axios
      .patch(`${process.env.REACT_APP_API_BASE}/api/bporh/admissao-checklist/`, {
        cliente_id: empresaSelecionada.cliente_id,
        convenia_employee_id: colab.convenia_id,
        campo: tarefa.campo,
        valor,
      }, { headers: { Authorization: `Bearer ${localStorage.getItem('access')}` } })
      .then(() => setMensagem(`${tarefa.rotulo}: ${valor}`))
      .catch((error) => {
        setMensagem(`${error.response?.data?.mensagem || 'Não foi possível salvar a tarefa'}`);
        carregar();   // recarrega para desfazer a atualização otimista
      })
      .finally(() => setSalvandoTarefa(null));
  };

  // Linha rotulada do modal de detalhe
  const Campo = ({ rotulo, valor, icone }) => (
    <div className="col-md-4 mb-2">
      <div className="small text-muted">
        {icone && <i className={`fas ${icone} me-1`}></i>}
        {rotulo}
      </div>
      <div className="fw-semibold" style={{ wordBreak: 'break-word' }}>{valor || '-'}</div>
    </div>
  );

  return (
    <div className="bporh-admissoes-page">
      <div className="container-fluid mt-4 shadow p-4 rounded bg-white">
        {/* Cabeçalho */}
        <div className="header-wrapper d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
          <div className="d-flex align-items-center">
            <i className="fas fa-user-plus header-icon me-2"></i>
            <h2 className="titulo-pagina m-0">
              BPO RH — Admissões
              {empresaSelecionada && <span className="text-muted"> · {empresaSelecionada.cliente}</span>}
            </h2>
          </div>
          <div className="d-flex align-items-center gap-2">
            <Link to="/BpoRhAtividadesAdmissao?tipo=Admissão" className="btn btn-outline-primary"
              title="Cadastrar o template de atividades de admissão por cliente">
              <i className="fas fa-list-check me-2"></i>
              Atividades
            </Link>
            {empresaSelecionada && (
              <button className="btn btn-outline-secondary" onClick={voltarParaEmpresas}>
                <i className="fas fa-arrow-left me-2"></i>
                Empresas
              </button>
            )}
            <select
              className="form-select"
              style={{ width: 'auto' }}
              value={dias}
              onChange={(e) => setDias(parseInt(e.target.value))}
              disabled={periodoAtivo}
              title={periodoAtivo
                ? 'Travado em "Todo o período" enquanto houver filtro de datas — é o que garante que o intervalo alcance todas as admissões'
                : 'Janela de admissões'}
            >
              <option value={30}>Últimos 30 dias</option>
              <option value={60}>Últimos 60 dias</option>
              <option value={90}>Últimos 90 dias</option>
              <option value={180}>Últimos 180 dias</option>
              <option value={365}>Últimos 12 meses</option>
              <option value={36500}>Todo o período</option>
            </select>
            <div className="form-check form-switch mb-0 ms-1 align-self-center"
              title="A lista mostra só as admissões pendentes. Ligue para ver também as concluídas do período escolhido.">
              <input className="form-check-input" type="checkbox" role="switch" id="adm-ver-concluidas"
                checked={verConcluidas} onChange={(e) => setVerConcluidas(e.target.checked)} />
              <label className="form-check-label small text-nowrap" htmlFor="adm-ver-concluidas">
                Mostrar concluídas
              </label>
            </div>

            {/* Intervalo por data de admissão — só na visão de colaboradores,
                onde existe uma lista de pessoas para filtrar */}
            {empresaSelecionada && (
              <div className="d-flex align-items-center gap-1 px-2 py-1 rounded"
                style={{ backgroundColor: '#f2f4f8' }}
                title="Filtra pela data de admissão do colaborador">
                <i className="fas fa-calendar-plus text-muted" style={{ fontSize: '.82rem' }}></i>
                <input type="date" className="form-control form-control-sm" style={{ width: 'auto' }}
                  value={filtroDe} max={filtroAte || undefined}
                  onChange={(e) => setFiltroDe(e.target.value)} aria-label="Admissão de" />
                <span className="text-muted small">até</span>
                <input type="date" className="form-control form-control-sm" style={{ width: 'auto' }}
                  value={filtroAte} min={filtroDe || undefined}
                  onChange={(e) => setFiltroAte(e.target.value)} aria-label="Admissão até" />
                {periodoAtivo && (
                  <button className="btn btn-sm btn-light p-1" title="Limpar período"
                    onClick={() => { setFiltroDe(''); setFiltroAte(''); }}>
                    <i className="fas fa-times text-danger"></i>
                  </button>
                )}
              </div>
            )}

            <button className="btn btn-outline-success" onClick={exportarExcel} disabled={carregando}
              title="Exporta o relatório de admissões do período filtrado (abre no Excel)">
              <i className="fas fa-file-excel me-2"></i>
              Exportar Excel
            </button>
            <button className="btn btn-outline-primary" onClick={() => carregar()} disabled={carregando} title="Atualizar">
              <i className={`fas fa-sync-alt ${carregando ? 'fa-spin' : ''}`}></i>
            </button>
          </div>
        </div>

        {carregando ? (
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status"></div>
            <p className="text-muted mt-2 mb-0">Carregando admissões...</p>
          </div>
        ) : !empresaSelecionada ? (
          <>
            {/* ─── VISÃO 1: EMPRESAS COM ADMISSÕES ─── */}
            <div className="d-flex align-items-center gap-2 mb-3 flex-wrap">
              <span className="badge fs-6"
                style={{ backgroundColor: '#00205b', fontVariantNumeric: 'tabular-nums' }}>
                <i className="fas fa-user-plus me-1"></i>
                Total: {totalColaboradores} admissõe{totalColaboradores === 1 ? '' : 's'} no período
              </span>
              <span className="badge bg-secondary" style={{ fontVariantNumeric: 'tabular-nums' }}>
                {empresasFiltradas.length} empresas
              </span>
              <select
                className="form-select form-select-sm ms-auto"
                style={{ maxWidth: '220px' }}
                value={filtroOperador}
                onChange={(e) => setFiltroOperador(e.target.value)}
                title="Filtrar por operador"
              >
                <option value="">Todos os operadores</option>
                {operadoresDisponiveis.map(o => <option key={o} value={o}>{o}</option>)}
              </select>
            </div>

            {/* ─── RESUMO DA CARTEIRA ─── */}
            {empresasFiltradas.length > 0 && (
              <div className="adm-resumo">
                <div className="adm-kpi">
                  <span className="v">{totalColaboradores}</span>
                  <span className="k">Admissões</span>
                </div>
                <div className="adm-resumo-sep" />
                <div className="adm-kpi ok">
                  <span className="v">{resumo.concluidas}</span>
                  <span className="k">Concluídas</span>
                </div>
                <div className="adm-kpi pend">
                  <span className="v">{resumo.pendentes}</span>
                  <span className="k">Pendentes</span>
                </div>
                <div className="adm-resumo-sep" />
                <div className="adm-resumo-pend">
                  <div className="titulo">
                    {resumo.pendentes > 0 ? 'Paradas nesta atividade' : 'Situação da carteira'}
                  </div>
                  <div className="adm-chips">
                    {resumo.tarefas.length > 0 ? (
                      resumo.tarefas.map(([tarefa, qtd]) => (
                        <span key={tarefa} className="adm-chip">
                          <i className="fas fa-clock" style={{ fontSize: '.7rem' }}></i>
                          {tarefa} <b>{qtd}</b>
                        </span>
                      ))
                    ) : (
                      <span className="adm-chip tudo-ok">
                        <i className="fas fa-circle-check" style={{ fontSize: '.72rem' }}></i>
                        Nenhuma admissão pendente
                      </span>
                    )}
                  </div>
                </div>
              </div>
            )}

            {empresasFiltradas.length === 0 ? (
              <div className="alert alert-info d-flex flex-wrap align-items-center gap-2">
                <span>
                  <i className="fas fa-info-circle me-2"></i>
                  {filtroOperador
                    ? <>Nenhuma admissão para <b>{filtroOperador}</b> nesta janela.</>
                    : <>Nenhuma admissão no período selecionado.</>}
                </span>
                {filtroOperador && (
                  <button className="btn btn-sm btn-outline-secondary"
                    onClick={() => setFiltroOperador('')}>
                    <i className="fas fa-users me-2"></i>Ver todos os operadores
                  </button>
                )}
                {dias !== DIAS_EMPRESAS && (
                  <button className="btn btn-sm btn-outline-primary"
                    onClick={() => setDias(DIAS_EMPRESAS)}>
                    <i className="fas fa-calendar-days me-2"></i>Ver todo o período
                  </button>
                )}
              </div>
            ) : (
              <div className="adm-grid">
                {empresasFiltradas.map((emp, i) => {
                  // o dado que importa: quantas admissões ainda têm tarefa pendente
                  const comPendencia = emp.colaboradores.filter(
                    c => (c.tarefas || []).some(t => t.estado === 'pendente')
                  ).length;
                  const concluidas = emp.total - comPendencia;
                  const pct = emp.total > 0 ? Math.round((concluidas / emp.total) * 100) : 0;
                  // atividade que mais trava as admissões desta empresa
                  const travas = {};
                  emp.colaboradores.forEach(c => {
                    const p = (c.tarefas || []).filter(t => t.estado === 'pendente');
                    if (p.length) travas[p[0].rotulo] = (travas[p[0].rotulo] || 0) + 1;
                  });
                  const principal = Object.entries(travas).sort((a, b) => b[1] - a[1])[0];
                  return (
                    <div
                      key={emp.cliente_id}
                      className={`adm-card ${comPendencia > 0 ? 'tem-pendencia' : 'tudo-ok'}`}
                      style={{ '--i': i }}
                      onClick={() => abrirEmpresa(emp)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => { if (e.key === 'Enter') abrirEmpresa(emp); }}
                    >
                      <div className="adm-card-top">
                        <h3 className="adm-empresa">{emp.cliente}</h3>
                        <span className="adm-total" title="Admissões no período">{emp.total}</span>
                      </div>

                      <div className="adm-operador">
                        <i className="fas fa-user-tie"></i>
                        {emp.operador || 'Sem operador'}
                      </div>

                      <div className="adm-progresso" aria-hidden="true">
                        <span style={{ width: `${pct}%` }} />
                      </div>

                      <div className="adm-legenda">
                        {comPendencia > 0 ? (
                          <div>
                            <span className="pend">
                              <i className="fas fa-clock me-1"></i>
                              {comPendencia} pendente{comPendencia > 1 ? 's' : ''}
                            </span>
                            <span className="text-muted mx-1">·</span>
                            <span className="text-muted">{concluidas} concluída{concluidas === 1 ? '' : 's'}</span>
                            {principal && (
                              <span className="adm-pendencia-tarefa">
                                em <b>{principal[0]}</b>
                                {principal[1] > 1 && ` (${principal[1]})`}
                              </span>
                            )}
                          </div>
                        ) : (
                          <span className="ok">
                            <i className="fas fa-circle-check me-1"></i>
                            Tudo concluído
                          </span>
                        )}
                      </div>

                      <div className="adm-card-acao">
                        Ver colaboradores <i className="fas fa-arrow-right"></i>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* ─── SOLICITAÇÕES RECEBIDAS PELO FORMULÁRIO PÚBLICO ─── */}
            {solicitacoes.length === 0 ? (
              <div className="mt-4">
                <div className="d-flex align-items-center gap-2 mb-2">
                  <h5 className="fw-bold m-0" style={{ color: '#212529' }}>
                    <i className="fas fa-inbox me-2" style={{ color: '#f1be48' }}></i>
                    Solicitações de Admissão
                  </h5>
                  <span className="text-muted small">— enviadas pelo formulário do cliente</span>
                </div>
                <div className="adm-vazio">
                  <div className="adm-vazio-anim">
                    <DotLottieReact
                      src="https://lottie.host/fa9e0439-56c4-47b9-984e-03b20bb60b9f/lfrt4Xnm2p.lottie"
                      loop
                      autoplay
                    />
                  </div>
                  <h3 className="adm-vazio-titulo">Nenhuma admissão nova</h3>
                  <p className="adm-vazio-texto">
                    Nenhuma solicitação aguardando cadastro. Quando um cliente preencher o
                    formulário de admissão, ela aparece aqui para você processar.
                  </p>
                </div>
              </div>
            ) : (
              <div className="mt-4">
                <div className="d-flex align-items-center gap-2 mb-2">
                  <h5 className="fw-bold m-0" style={{ color: '#212529' }}>
                    <i className="fas fa-inbox me-2" style={{ color: '#f1be48' }}></i>
                    Solicitações de Admissão
                  </h5>
                  <span className="badge bg-warning text-dark">{solicitacoes.length} nova{solicitacoes.length > 1 ? 's' : ''}</span>
                  <span className="text-muted small">— enviadas pelo formulário do cliente</span>
                </div>
                <div className="p-3 rounded border border-warning bg-warning bg-opacity-10">
                <div className="d-flex flex-wrap gap-2">
                  {solicitacoes.map((s) => (
                    <button
                      key={s.id}
                      className="btn btn-sm btn-light border d-flex align-items-center gap-2"
                      onClick={() => setSolicitacaoDetalhe(s)}
                      title="Ver dados da solicitação"
                    >
                      <i className="fas fa-user-plus text-warning"></i>
                      <span className="fw-bold">{s.colaborador}</span>
                      <span className="text-muted">· {s.cliente || s.empresa}</span>
                      {s.data_admissao && <span className="badge bg-warning text-dark">{s.data_admissao}</span>}
                    </button>
                  ))}
                </div>
                </div>
              </div>
            )}
          </>
        ) : (
          <>
            {/* ─── VISÃO 2: COLABORADORES EM ADMISSÃO DA EMPRESA ─── */}
            <div className="d-flex align-items-center gap-2 mb-3 flex-wrap">
              <span className="badge bg-primary fs-6">
                <i className="fas fa-building me-1"></i> {empresaSelecionada.cliente}
              </span>
              <span className="badge bg-secondary">
                <i className="fas fa-user-tie me-1"></i> {empresaSelecionada.operador || 'Sem operador'}
              </span>
              <span className="badge fs-6" style={{ backgroundColor: '#00205b' }}>
                <i className="fas fa-user-plus me-1"></i>
                {colaboradoresFiltrados.length} admissõe{colaboradoresFiltrados.length === 1 ? '' : 's'} no período
              </span>
            </div>

            {/* Filtros da tabela */}
            <div className="row g-2 mb-2">
              <div className="col-md-3">
                <label className="form-label small fw-bold mb-1 text-muted">Cargo</label>
                <select className="form-select form-select-sm" value={filtroCargo}
                  onChange={(e) => setFiltroCargo(e.target.value)}>
                  <option value="">Todos os cargos</option>
                  {opcoesCargo.map(o => <option key={o} value={o}>{o}</option>)}
                </select>
              </div>
              <div className="col-md-3">
                <label className="form-label small fw-bold mb-1 text-muted">Departamento</label>
                <select className="form-select form-select-sm" value={filtroDepto}
                  onChange={(e) => setFiltroDepto(e.target.value)}>
                  <option value="">Todos os departamentos</option>
                  {opcoesDepto.map(o => <option key={o} value={o}>{o}</option>)}
                </select>
              </div>
              <div className="col-md-3">
                <label className="form-label small fw-bold mb-1 text-muted">Status</label>
                <select className="form-select form-select-sm" value={filtroStatus}
                  onChange={(e) => setFiltroStatus(e.target.value)}>
                  <option value="">Todos os status</option>
                  {opcoesStatus.map(o => <option key={o} value={o}>{o}</option>)}
                </select>
              </div>
              <div className="col-md-3">
                <label className="form-label small fw-bold mb-1 text-muted">Prazo</label>
                <select className="form-select form-select-sm" value={filtroPrazo}
                  onChange={(e) => setFiltroPrazo(e.target.value)}>
                  <option value="">Todos os prazos</option>
                  <option value="pendente">Ainda não entregue</option>
                  <option value="vencendo">Vencendo ou atrasado</option>
                  <option value="critico">Crítico (faltam 2 dias ou menos)</option>
                  <option value="atrasado">Só atrasados</option>
                  <option value="hoje">Vence hoje</option>
                  <option value="entregue">Entregues</option>
                </select>
              </div>
              <div className="col-md-3">
                <label className="form-label small fw-bold mb-1 text-muted">Buscar</label>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  placeholder="Nome, cargo, e-mail..."
                  value={busca}
                  onChange={(e) => setBusca(e.target.value)}
                />
              </div>
            </div>

            {/* Atalhos que preenchem os dois campos de data do cabeçalho */}
            <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
              <span className="small text-muted fw-bold">Período rápido:</span>
              {[
                { rotulo: 'Este mês', meses: 0 },
                { rotulo: 'Últimos 3 meses', meses: 3 },
                { rotulo: 'Este ano', ano: true },
              ].map((a) => (
                <button key={a.rotulo} className="btn btn-sm btn-outline-secondary"
                  onClick={() => {
                    const hoje = new Date();
                    const iso = (d) => d.toISOString().slice(0, 10);
                    if (a.ano) {
                      setFiltroDe(`${hoje.getFullYear()}-01-01`);
                    } else if (a.meses === 0) {
                      setFiltroDe(iso(new Date(hoje.getFullYear(), hoje.getMonth(), 1)));
                    } else {
                      setFiltroDe(iso(new Date(hoje.getFullYear(), hoje.getMonth() - a.meses, hoje.getDate())));
                    }
                    setFiltroAte(iso(hoje));
                  }}>
                  {a.rotulo}
                </button>
              ))}
            </div>

            {periodoAtivo && (
              <div className="alert alert-info py-2 small">
                <i className="fas fa-filter me-2"></i>
                Mostrando admissões
                {filtroDe && <> a partir de <b>{filtroDe.split('-').reverse().join('/')}</b></>}
                {filtroAte && <> até <b>{filtroAte.split('-').reverse().join('/')}</b></>}
                {' '}sobre <b>todo o período</b> (a janela do cabeçalho fica travada
                enquanto o filtro de datas estiver ativo, para não esconder admissões
                mais antigas). Colaboradores sem data de admissão ficam fora deste filtro.
              </div>
            )}

            <div className="table-responsive">
              <table className="table table-striped table-hover align-middle">
                <thead>
                  <tr>
                    <th><i className="fas fa-user me-2"></i>Colaborador</th>
                    <th><i className="fas fa-briefcase me-2"></i>Cargo</th>
                    <th><i className="fas fa-sitemap me-2"></i>Departamento</th>
                    <th><i className="fas fa-file-signature me-2"></i>Vínculo</th>
                    <th><i className="fas fa-calendar-plus me-2"></i>Admissão</th>
                    <th><i className="fas fa-hourglass-half me-2"></i>Fim Experiência</th>
                    <th><i className="fas fa-columns me-2"></i>Etapa Convenia</th>
                    <th><i className="fas fa-stopwatch me-2"></i>Prazo</th>
                    <th><i className="fas fa-flag me-2"></i>Status</th>
                    <th><i className="fas fa-list-check me-2"></i>Tarefas</th>
                    <th className="text-center">Detalhes</th>
                  </tr>
                </thead>
                <tbody>
                  {colaboradoresFiltrados.map((c) => (
                    <tr key={c.convenia_id} style={{ cursor: 'pointer' }}
                      onClick={() => { setAbaDetalhe('checklist'); setColaboradorDetalhe(c); }}>
                      <td className="fw-bold">{nomeCompleto(c)}</td>
                      <td>{c.cargo || '-'}</td>
                      <td>{c.departamento || '-'}</td>
                      <td><VinculoBadge vinculo={c.vinculo} origem={c.vinculo_origem} /></td>
                      <td>{c.data_admissao || '-'}</td>
                      <td>{c.fim_experiencia || '-'}</td>
                      <td style={{ whiteSpace: 'nowrap' }}>
                        {(() => {
                          const et = etapaExibida(c);
                          if (!et) return <span className="text-muted">-</span>;
                          const e = COR_ETAPA_CONVENIA[et.nome] || {};
                          return (
                            <span className="badge fw-semibold"
                              title={et.inferida
                                ? `Checklist 100% concluído — admissão finalizada (na Convenia o card ainda está em "${c.etapa_admissao}")`
                                : undefined}
                              style={{ backgroundColor: e.bg || '#f2f4f8', color: e.cor || '#475467',
                                border: `1.5px solid ${e.borda || '#cbd5e1'}` }}>
                              {et.nome}
                            </span>
                          );
                        })()}
                      </td>
                      <td><PrazoBadge prazo={c.prazo} /></td>
                      <td>
                        {(() => {
                          const st = statusChecklist(c);
                          if (st.tipo === 'ok') {
                            return (
                              <span className="badge bg-success"
                                title={st.por ? `Admissão processada por ${st.por}` : undefined}>
                                <i className="fas fa-check me-1"></i>
                                Concluído{st.data && <> · {st.data}</>}
                              </span>
                            );
                          }
                          if (st.tipo === 'pendente') {
                            return (
                              <span
                                className="badge bg-warning text-dark"
                                title={`${st.total_pendentes} tarefa(s) pendente(s)`}
                              >
                                <i className="fas fa-clock me-1"></i>
                                {st.rotulo}
                                {st.restantes > 0 && <> +{st.restantes}</>}
                              </span>
                            );
                          }
                          return <span className="badge bg-light text-muted border">{st.rotulo}</span>;
                        })()}

                      </td>
                      <td>
                        {c.tem_checklist ? (
                          <span
                            className={`badge ${
                              c.tarefas_total > 0 && c.tarefas_concluidas === c.tarefas_total
                                ? 'bg-success'
                                : c.tarefas_concluidas > 0 ? 'bg-warning text-dark' : 'bg-secondary'
                            }`}
                            title={c.checklist_pj
                              ? 'PJ não tem checklist de admissão — a única etapa é o cadastro na Convenia'
                              : (c.checklist_status || 'Progresso do checklist de admissão')}
                          >
                            <i className={`fas ${
                              c.tarefas_total > 0 && c.tarefas_concluidas === c.tarefas_total
                                ? 'fa-check-double' : 'fa-list-check'
                            } me-1`}></i>
                            {c.tarefas_concluidas}/{c.tarefas_total}
                            {c.checklist_pj && <span className="ms-1 fw-normal">· PJ, só Convenia</span>}
                          </span>
                        ) : (
                          <span className="badge bg-light text-muted border">sem checklist</span>
                        )}
                      </td>
                      <td className="text-center">
                        <button
                          className="btn btn-sm btn-outline-primary"
                          onClick={(e) => { e.stopPropagation(); setAbaDetalhe('dados'); setColaboradorDetalhe(c); }}
                          title="Ver dados do colaborador"
                        >
                          <i className="fas fa-eye"></i>
                        </button>
                      </td>
                    </tr>
                  ))}
                  {colaboradoresFiltrados.length === 0 && (
                    <tr>
                      <td colSpan={11} className="text-center text-muted py-4">
                        <i className="fas fa-inbox me-2"></i>
                        {(filtroDe || filtroAte) ? (
                          <>Nenhuma admissão no período de datas informado. Use
                            <b> Limpar período</b> para ver todas.</>
                        ) : (
                          <>Nenhuma admissão nesta janela. Amplie o período no seletor acima
                            {dias === DIAS_EMPRESA_ABERTA && <> (hoje em <b>últimos 30 dias</b>)</>}.</>
                        )}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      {/* ─── MODAL: DADOS DO COLABORADOR ─── */}
      {colaboradorDetalhe && (
        <div
          className="modal"
          tabIndex="-1"
          style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 9999 }}
        >
          {/* maxWidth inline: theme.css limita todo .modal-dialog a 800px e anula o modal-xl */}
          <div className="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable"
            style={{ maxWidth: '1080px' }}>
            <div className="modal-content shadow-lg">
              <div className="modal-header" style={{ backgroundColor: '#ffffff', borderBottom: '1px solid #e9ecef' }}>
                <div>
                  <h5 className="modal-title mb-0 fw-bold" style={{ color: '#212529' }}>
                    <i className="fas fa-id-card me-2"></i>
                    {nomeCompleto(colaboradorDetalhe)}
                  </h5>
                  <small style={{ color: '#6c757d' }}>
                    {empresaSelecionada?.cliente} · dados espelhados da Convenia
                    {colaboradorDetalhe.sincronizado_em && ` · sincronizado em ${colaboradorDetalhe.sincronizado_em}`}
                  </small>
                </div>
                <button type="button" className="btn-close" onClick={() => setColaboradorDetalhe(null)}></button>
              </div>

              <div className="modal-body">
                {/* Abas: Checklist (clique na linha) / Dados (botão Detalhes) */}
                <ul className="nav nav-pills nav-fill mb-3">
                  <li className="nav-item">
                    <button
                      type="button"
                      className={`nav-link ${abaDetalhe === 'checklist' ? 'active' : ''}`}
                      onClick={() => setAbaDetalhe('checklist')}
                    >
                      <i className="fas fa-list-check me-2"></i>Checklist da admissão
                    </button>
                  </li>
                  <li className="nav-item">
                    <button
                      type="button"
                      className={`nav-link ${abaDetalhe === 'dados' ? 'active' : ''}`}
                      onClick={() => setAbaDetalhe('dados')}
                    >
                      <i className="fas fa-id-card me-2"></i>Dados do colaborador
                    </button>
                  </li>
                </ul>

                {abaDetalhe === 'checklist' && (<>
                <FinalizacaoManual tipo="admissao" clienteId={empresaSelecionada?.cliente_id}
                  conveniaId={colaboradorDetalhe.convenia_id}
                  pendentes={(colaboradorDetalhe.tarefas || []).filter(t => t.estado === 'pendente').map(t => t.rotulo)}
                  onMudou={() => { setColaboradorDetalhe(null); carregar(); }} />
                {/* ─── CHECKLIST DE TAREFAS DA ADMISSÃO ─── */}
                {colaboradorDetalhe.tem_checklist && (() => {
                  const feitas = colaboradorDetalhe.tarefas_concluidas;
                  const total = colaboradorDetalhe.tarefas_total;
                  const pct = total > 0 ? Math.round((feitas / total) * 100) : 0;
                  const completo = total > 0 && feitas === total;
                  return (
                    <div className="mb-3">
                      <div className="d-flex justify-content-between align-items-center mb-1 flex-wrap gap-2">
                        <span className="fw-semibold" style={{ color: '#212529' }}>
                          <i className="fas fa-list-check me-2 text-muted"></i>
                          {feitas}/{total} tarefas concluídas
                          {colaboradorDetalhe.checklist_status && (
                            <span className="text-muted fw-normal"> · {colaboradorDetalhe.checklist_status}</span>
                          )}
                        </span>
                        <span className={`badge ${completo ? 'bg-success' : 'bg-warning text-dark'}`}>{pct}%</span>
                      </div>
                      <div className="progress" style={{ height: '6px' }}>
                        <div
                          className={`progress-bar ${completo ? 'bg-success' : 'bg-warning'}`}
                          style={{ width: `${pct}%` }}
                          role="progressbar"
                        />
                      </div>
                    </div>
                  );
                })()}

                {colaboradorDetalhe.tem_checklist ? (
                  <div className="border rounded overflow-hidden mb-3">
                    {colaboradorDetalhe.tarefas.map((t, i) => {
                      const OPCOES = [
                        { valor: 'Concluído', icone: 'fa-check', classe: 'success', titulo: 'Concluído' },
                        { valor: 'Solicitado', icone: 'fa-paper-plane', classe: 'info', titulo: 'Solicitado — pedido feito, aguardando terceiro (corretora, banco...)' },
                        // o exame tem um passo a mais: solicitou → AGENDOU → realizou
                        ...(t.campo === 'ex_admissional'
                          ? [{ valor: 'Agendado', icone: 'fa-calendar-check', classe: 'primary', titulo: 'Exame agendado na clínica' }]
                          : []),
                        { valor: 'Não iniciada', icone: 'fa-clock', classe: 'warning', titulo: 'Não iniciada' },
                        // admissão recente EXIGE o contrato: sem a saída
                        // 'Não se aplica', a única forma de fechar é anexando
                        ...(t.campo === 'contrato_assinado' && contratoExigido(colaboradorDetalhe?.data_admissao,
                            colaboradorDetalhe?.contratacao || colaboradorDetalhe?.vinculo)
                          ? []
                          : [{ valor: 'Não se aplica', icone: 'fa-minus', classe: 'secondary', titulo: 'Não se aplica' }]),
                      ];
                      // o valor salvo vence quando é uma das opções (distingue
                      // Solicitado de Não iniciada, que são o mesmo 'estado')
                      const atual = OPCOES.some(o => o.valor === t.valor) ? t.valor
                        : t.estado === 'concluida' ? 'Concluído'
                        : t.estado === 'na' ? 'Não se aplica' : 'Não iniciada';
                      const historico = t.valor && !OPCOES.some(o => o.valor === t.valor)
                        && !['Concluido', 'Não se Aplica'].includes(t.valor) ? t.valor : null;
                      return (
                        <div
                          key={t.campo}
                          className="d-flex align-items-center gap-3 px-3 py-2"
                          style={{
                            borderTop: i === 0 ? 'none' : '1px solid #f1f3f5',
                            background: t.estado === 'na' ? '#fbfbfc' : '#fff',
                          }}
                        >
                          <i className={`fas ${
                            t.estado === 'concluida' ? 'fa-circle-check text-success' :
                            t.estado === 'na' ? 'fa-circle-minus text-secondary opacity-50' :
                            t.valor === 'Solicitado' ? 'fa-circle-dot text-info' :
                            t.valor === 'Agendado' ? 'fa-circle-dot text-primary' : 'fa-circle text-warning opacity-75'
                          }`} style={{ fontSize: '0.9rem' }}></i>

                          {/* nome + data: largura fixa para não competir com os botões */}
                          <div style={{ minWidth: '210px', maxWidth: '300px' }}>
                            <div
                              className={t.estado === 'na' ? 'text-muted' : 'fw-semibold'}
                              style={{
                                fontSize: '0.92rem',
                                color: t.estado === 'na' ? undefined : '#212529',
                                display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '3px',
                              }}
                            >
                              <span style={{ whiteSpace: 'nowrap' }}>{t.rotulo}</span>
                              {t.campo === 'ponto' && (empresaSelecionada?.sistemas_ponto || []).length > 0 && (() => {
                                const sis = empresaSelecionada.sistemas_ponto;
                                const multi = sis.length > 1;
                                const atual = sis.find(s => s.tipo === pontoSistema) || sis[0];
                                const cor = SIS_PONTO_COR[atual?.tipo] || SIS_PONTO_COR.rhid;
                                if (multi) {
                                  return (
                                    <select
                                      className="form-select form-select-sm d-inline-block ms-2"
                                      style={{ width: 'auto', fontSize: '.72rem', padding: '1px 20px 1px 8px',
                                        background: cor.bg, color: cor.cor, borderColor: cor.borda, fontWeight: 700 }}
                                      value={pontoSistema}
                                      onClick={(e) => e.stopPropagation()}
                                      onChange={(e) => { e.stopPropagation(); setPontoSistema(e.target.value); }}
                                      title="Onde lançar o ponto deste colaborador">
                                      {sis.map(s => (
                                        <option key={s.tipo} value={s.tipo}>
                                          {s.rotulo}{s.manual ? ' (manual)' : ''}
                                        </option>
                                      ))}
                                    </select>
                                  );
                                }
                                // um sistema só: sem tag — o botão ao lado já diz o que fazer
                                return null;
                              })()}
                            </div>
                            {t.estado === 'concluida' && t.concluida_em && (
                              <div className="text-success" style={{ fontSize: '0.75rem' }}>
                                <i className="fas fa-calendar-check me-1"></i>{t.concluida_em}
                              </div>
                            )}
                            {t.valor === 'Solicitado' && (
                              <span className="badge mt-1"
                                style={{ backgroundColor: '#e3f6fb', color: '#0e6e86',
                                  border: '1.5px solid #00b5cc', fontSize: '.7rem' }}>
                                <i className="fas fa-paper-plane me-1"></i>Solicitado
                              </span>
                            )}
                            {t.valor === 'Agendado' && (
                              <span className="badge mt-1"
                                style={{ backgroundColor: '#eef3ff', color: '#1d4ed8',
                                  border: '1.5px solid #3b82f6', fontSize: '.7rem' }}>
                                <i className="fas fa-calendar-check me-1"></i>Agendado
                              </span>
                            )}
                            {historico && (
                              <div className="text-muted" style={{ fontSize: '0.74rem' }}>
                                registro anterior: {historico}
                              </div>
                            )}
                            {obsEditando?.campo === t.campo ? (
                              <div className="d-flex align-items-center gap-1 mt-1">
                                <input
                                  type="text"
                                  className="form-control form-control-sm"
                                  style={{ fontSize: '.78rem', maxWidth: '230px' }}
                                  maxLength={500}
                                  autoFocus
                                  placeholder="Observação desta atividade..."
                                  value={obsEditando.texto}
                                  disabled={salvandoObsTarefa}
                                  onChange={(e) => setObsEditando(o => ({ ...o, texto: e.target.value }))}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') salvarObsTarefa(colaboradorDetalhe, t);
                                    if (e.key === 'Escape') setObsEditando(null);
                                  }}
                                />
                                <button className="btn btn-sm btn-success p-1 px-2"
                                  disabled={salvandoObsTarefa}
                                  onClick={() => salvarObsTarefa(colaboradorDetalhe, t)}
                                  title="Salvar (Enter)">
                                  <i className={`fas ${salvandoObsTarefa ? 'fa-spinner fa-spin' : 'fa-check'}`}></i>
                                </button>
                                <button className="btn btn-sm btn-light border p-1 px-2"
                                  disabled={salvandoObsTarefa}
                                  onClick={() => setObsEditando(null)} title="Cancelar (Esc)">
                                  <i className="fas fa-times text-muted"></i>
                                </button>
                              </div>
                            ) : t.obs ? (
                              <div className="text-muted mt-1"
                                style={{ fontSize: '.76rem', cursor: 'pointer', maxWidth: '250px' }}
                                title="Clique para editar a observação"
                                onClick={() => setObsEditando({ campo: t.campo, texto: t.obs })}>
                                <i className="fas fa-comment-dots me-1" style={{ color: '#00b5cc' }}></i>
                                {t.obs}
                              </div>
                            ) : null}

                            {/* contrato assinado: PDF obrigatório para concluir */}
                            {t.campo === 'contrato_assinado' && (
                              <ContratoAnexo
                                tipo="admissao"
                                clienteId={empresaSelecionada?.cliente_id || colaboradorDetalhe.cliente_id}
                                conveniaId={colaboradorDetalhe.convenia_id}
                                colaborador={colaboradorDetalhe.nome_completo || colaboradorDetalhe.nome}
                                vinculo={colaboradorDetalhe.contratacao || colaboradorDetalhe.vinculo}
                                aviso={t.valor !== 'Concluído'}
                                onEstado={(e) => { setTemContrato(e.tem); setExigeContrato(e.exige !== false); }}
                                onMudou={() => carregar(true)}
                              />
                            )}
                          </div>

                          {/* automações de e-mail: largura fixa reservada em TODAS as linhas,
                              para os botões de status ficarem alinhados na mesma coluna */}
                          <div style={{ width: '132px', flexShrink: 0 }}>
                            {EMAIL_AUTOMACOES[t.campo] && (
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-primary w-100"
                                style={{ whiteSpace: 'nowrap', fontSize: '0.78rem', padding: '3px 6px' }}
                                disabled={carregandoPrevia === t.campo}
                                onClick={() => abrirEmailAutomacao(colaboradorDetalhe, t.campo,
                                  // E-mail do Ponto abre no mesmo sistema escolhido na tarefa Ponto
                                  t.campo === 'email_ponto' ? (pontoSistema || null) : null)}
                                title={`Enviar e-mail para ${EMAIL_AUTOMACOES[t.campo].paraQuem}`}
                              >
                                {carregandoPrevia === t.campo
                                  ? <><span className="spinner-border spinner-border-sm me-2"></span>Carregando...</>
                                  : <><i className="fas fa-paper-plane me-2"></i>Enviar e-mail</>}
                              </button>
                            )}
                            {t.campo === 'pla_saude' && (
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-primary w-100"
                                style={{ whiteSpace: 'nowrap', fontSize: '0.78rem', padding: '3px 6px' }}
                                onClick={() => setPlanoColab(colaboradorDetalhe)}
                                title="Registrar se o colaborador quer o plano de saúde e anexar o comprovante"
                              >
                                <i className="fas fa-heart-pulse me-2"></i>Registrar plano
                              </button>
                            )}
                            {t.campo === 'beneficios_financeiro' && (
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-primary w-100"
                                style={{ whiteSpace: 'nowrap', fontSize: '0.78rem', padding: '3px 6px' }}
                                disabled={carregandoBenef}
                                onClick={() => abrirBenefFin(colaboradorDetalhe)}
                                title="E-mail ao financeiro do cliente com o VA/VT do mês da admissão desta pessoa"
                              >
                                {carregandoBenef
                                  ? <><span className="spinner-border spinner-border-sm me-2"></span>Calculando...</>
                                  : <><i className="fas fa-paper-plane me-2"></i>Enviar ao financeiro</>}
                              </button>
                            )}
                            {t.campo === 'vr_va' && (
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-success w-100"
                                style={{ whiteSpace: 'nowrap', fontSize: '0.78rem', padding: '3px 6px' }}
                                disabled={carregandoVa}
                                onClick={() => abrirVaAdmissao(colaboradorDetalhe)}
                                title="Gerar o arquivo de vale-alimentação só deste colaborador e registrar o crédito"
                              >
                                {carregandoVa
                                  ? <><span className="spinner-border spinner-border-sm me-2"></span>Carregando...</>
                                  : <><i className="fas fa-utensils me-2"></i>Gerar VA</>}
                              </button>
                            )}
                            {t.campo === 'ponto' && pontoSistema === 'pontomais' && empresaSelecionada?.tem_pontomais && (
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-success w-100"
                                style={{ whiteSpace: 'nowrap', fontSize: '0.78rem', padding: '3px 6px' }}
                                disabled={carregandoPontoAdmit}
                                onClick={() => abrirPontoAdmit(colaboradorDetalhe)}
                                title="Criar este colaborador no PontoMais do cliente (dados já preenchidos da Convenia)"
                              >
                                {carregandoPontoAdmit
                                  ? <><span className="spinner-border spinner-border-sm me-2"></span>Carregando...</>
                                  : <><i className="fas fa-user-plus me-2"></i>Cadastrar</>}
                              </button>
                            )}
                            {t.campo === 'ponto' && pontoSistema === 'tiquetaque' && empresaSelecionada?.tem_tiquetaque && (
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-success w-100"
                                style={{ whiteSpace: 'nowrap', fontSize: '0.78rem', padding: '3px 6px' }}
                                disabled={carregandoTtAdmit}
                                onClick={() => abrirTtAdmit(colaboradorDetalhe)}
                                title="Criar este funcionário no TiqueTaque do cliente (dados já preenchidos da Convenia)"
                              >
                                {carregandoTtAdmit
                                  ? <><span className="spinner-border spinner-border-sm me-2"></span>Carregando...</>
                                  : <><i className="fas fa-user-plus me-2"></i>Cadastrar</>}
                              </button>
                            )}
                            {t.campo === 'ponto' && pontoSistema === 'rhid' && empresaSelecionada?.tem_rhid && (
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-success w-100"
                                style={{ whiteSpace: 'nowrap', fontSize: '0.78rem', padding: '3px 6px' }}
                                disabled={carregandoRhidAdmit}
                                onClick={() => abrirRhidAdmit(colaboradorDetalhe)}
                                title="Criar esta pessoa no RHiD do cliente (dados já preenchidos da Convenia)"
                              >
                                {carregandoRhidAdmit
                                  ? <><span className="spinner-border spinner-border-sm me-2"></span>Carregando...</>
                                  : <><i className="fas fa-user-plus me-2"></i>Cadastrar</>}
                              </button>
                            )}
                            {t.campo === 'ponto' && pontoSistema === 'kairos' && (
                              <span className="badge bg-light text-muted border w-100 py-2"
                                style={{ fontSize: '0.72rem', whiteSpace: 'normal' }}
                                title="Este sistema não tem criação por API — cadastre o colaborador no portal">
                                <i className="fas fa-hand-pointer me-1"></i>Cadastro manual
                              </span>
                            )}
                            {t.campo === 'ex_admissional' && (
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-primary w-100"
                                style={{ whiteSpace: 'nowrap', fontSize: '0.78rem', padding: '3px 6px' }}
                                disabled={exameAdmCarregando}
                                onClick={() => abrirExameAdm(colaboradorDetalhe)}
                                title="Pedir o exame admissional à clínica de medicina do trabalho, com a guia em PDF (mesmo fluxo do Exame Mensal)"
                              >
                                {exameAdmCarregando
                                  ? <><span className="spinner-border spinner-border-sm me-2"></span>Carregando...</>
                                  : <><i className="fas fa-stethoscope me-2"></i>Solicitar exame</>}
                              </button>
                            )}
                            {t.campo === 'ex_admissional' && empresaSelecionada && (
                              <button
                                type="button"
                                className={`btn btn-sm ${t.valor === 'Agendado' ? 'btn-primary' : 'btn-outline-primary'} w-100 mt-1`}
                                style={{ whiteSpace: 'nowrap', fontSize: '0.78rem', padding: '3px 6px' }}
                                onClick={() => setAvisoExameAdm(colaboradorDetalhe)}
                                title="Depois do agendamento: e-mail ao colaborador com data, horário, clínica, endereço e a guia do exame em anexo"
                              >
                                <i className="fas fa-envelope me-2"></i>E-mail ao colaborador
                              </button>
                            )}
                            {/* sem automação: abre o portal do fornecedor cadastrado em Serviços */}
                            {portaisDaTarefa(t.campo).map((pt, k) => {
                              const cred = credPortal[pt.id] || {};
                              const temCred = !!(pt.login || pt.tem_senha);
                              return (
                                <div key={`${pt.portal}-${k}`}
                                  className={k > 0 || EMAIL_AUTOMACOES[t.campo] || t.campo === 'ex_admissional' ? 'mt-1' : ''}>
                                  <div className="d-flex gap-1">
                                    <a href={pt.portal} target="_blank" rel="noreferrer"
                                      className="btn btn-sm btn-outline-secondary flex-grow-1"
                                      style={{ fontSize: '0.78rem', padding: '3px 6px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}
                                      title={`Abrir ${pt.propriedade} em nova aba (portal cadastrado em Serviços)`}>
                                      <i className="fas fa-arrow-up-right-from-square me-2"></i>{pt.propriedade}
                                    </a>
                                    {temCred && (
                                      <button type="button"
                                        className={`btn btn-sm ${cred.aberto ? 'btn-secondary' : 'btn-outline-secondary'} flex-shrink-0`}
                                        style={{ padding: '3px 7px', fontSize: '0.78rem' }}
                                        onClick={() => alternarCredPortal(pt)}
                                        title={cred.aberto ? 'Ocultar login e senha' : `Ver login e senha do ${pt.propriedade} (o acesso fica registrado)`}>
                                        <i className={`fas ${cred.carregando ? 'fa-spinner fa-spin' : 'fa-key'}`}></i>
                                      </button>
                                    )}
                                  </div>
                                  {cred.aberto && !cred.carregando && (
                                    <div className="border rounded px-2 py-1 mt-1 bg-light" style={{ fontSize: '0.74rem' }}>
                                      <div className="d-flex align-items-center gap-1">
                                        <span className="text-muted">login</span>
                                        <code className="text-truncate" style={{ maxWidth: 80 }} title={pt.login || ''}>{pt.login || '—'}</code>
                                        {pt.login && (
                                          <button type="button" className="btn btn-sm btn-light border p-0 px-1 ms-auto" onClick={() => copiarCred(pt.login, 'Login')} title="Copiar login">
                                            <i className="fas fa-copy text-muted" style={{ fontSize: '.68rem' }}></i>
                                          </button>
                                        )}
                                      </div>
                                      <div className="d-flex align-items-center gap-1">
                                        <span className="text-muted">senha</span>
                                        <code className="text-truncate" style={{ maxWidth: 80 }}>{pt.tem_senha ? (cred.senha || '—') : '—'}</code>
                                        {cred.senha && (
                                          <button type="button" className="btn btn-sm btn-light border p-0 px-1 ms-auto" onClick={() => copiarCred(cred.senha, 'Senha')} title="Copiar senha">
                                            <i className="fas fa-copy text-muted" style={{ fontSize: '.68rem' }}></i>
                                          </button>
                                        )}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>

                          {!t.obs && obsEditando?.campo !== t.campo && (
                            <button className="btn btn-sm btn-light border p-1 px-2 flex-shrink-0"
                              onClick={() => setObsEditando({ campo: t.campo, texto: '' })}
                              title="Adicionar observação a esta atividade">
                              <i className="far fa-comment text-muted" style={{ fontSize: '.8rem' }}></i>
                            </button>
                          )}
                          <div className="btn-group btn-group-sm flex-shrink-0" role="group">
                            {OPCOES.map(o => {
                              // 'Concluído' do contrato só destrava com o PDF anexado
                              const travado = t.campo === 'contrato_assinado'
                                && o.valor === 'Concluído' && exigeContrato && !temContrato;
                              return (
                              <button
                                key={o.valor}
                                type="button"
                                title={travado ? 'Anexe o PDF do contrato para concluir' : o.titulo}
                                disabled={salvandoTarefa === t.campo || travado}
                                // btn-sm é obrigatório: theme.css dá min-width:170px a
                                // '.d-flex .btn:not(.btn-sm)' e o grupo estouraria a linha
                                className={`btn btn-sm ${atual === o.valor ? `btn-${o.classe}` : 'btn-light border'}`}
                                style={{ padding: '5px 26px', minWidth: 'auto' }}
                                onClick={() => marcarTarefa(colaboradorDetalhe, t, o.valor)}
                              >
                                <i className={`fas ${o.icone}`} style={{
                                  fontSize: '0.85rem',
                                  color: atual === o.valor ? undefined : '#adb5bd',
                                }}></i>
                              </button>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="alert alert-light border mb-3 py-2 small">
                    <i className="fas fa-info-circle me-2"></i>
                    Sem checklist registrado para este colaborador.
                  </div>
                )}
                </>)}

                {abaDetalhe === 'dados' && (<>
                <h6 className="fw-bold text-muted border-bottom pb-1 mb-3">
                  <i className="fas fa-briefcase me-2"></i>Dados profissionais
                </h6>
                <div className="row">
                  <Campo rotulo="Cargo" valor={colaboradorDetalhe.cargo} icone="fa-briefcase" />
                  <Campo rotulo="Departamento" valor={colaboradorDetalhe.departamento} icone="fa-sitemap" />
                  <Campo rotulo="Time" valor={colaboradorDetalhe.time} icone="fa-people-group" />
                  <Campo rotulo="Centro de Custo" valor={colaboradorDetalhe.centro_custo} icone="fa-coins" />
                  <Campo rotulo="Supervisor" valor={colaboradorDetalhe.supervisor} icone="fa-user-tie" />
                  <Campo rotulo="Matrícula" valor={colaboradorDetalhe.matricula} icone="fa-hashtag" />
                  <Campo rotulo="Salário" valor={formatarMoeda(colaboradorDetalhe.salario)} icone="fa-money-bill-wave" />
                  <Campo rotulo="Data de Admissão" valor={colaboradorDetalhe.data_admissao} icone="fa-calendar-plus" />
                  <Campo rotulo="Exame Admissional" valor={colaboradorDetalhe.data_exame_adm} icone="fa-notes-medical" />
                  <Campo rotulo="Fim da Experiência" valor={colaboradorDetalhe.fim_experiencia} icone="fa-hourglass-half" />
                  <Campo rotulo="Status" valor={colaboradorDetalhe.status} icone="fa-flag" />
                </div>

                {solicitacaoOrigem && (<>
                  <h6 className="fw-bold border-bottom pb-1 mb-3 mt-3" style={{ color: '#8a6d00' }}>
                    <i className="fas fa-inbox me-2"></i>Formulário de solicitação
                    <span className="badge bg-light text-dark border ms-2 fw-normal">
                      enviado em {solicitacaoOrigem.created_at}
                      {solicitacaoOrigem.status && ` · ${solicitacaoOrigem.status}`}
                    </span>
                  </h6>
                  <div className="row">
                    <Campo rotulo="Solicitante (e-mail)" valor={solicitacaoOrigem.email_solicitante} icone="fa-envelope" />
                    <Campo rotulo="Gestor" valor={solicitacaoOrigem.gestor} icone="fa-user-tie" />
                    <Campo rotulo="Função informada" valor={solicitacaoOrigem.funcao} icone="fa-briefcase" />
                    <Campo rotulo="Departamento informado" valor={solicitacaoOrigem.departamento} icone="fa-sitemap" />
                    <Campo rotulo="Centro de custo" valor={solicitacaoOrigem.centro_custo} icone="fa-coins" />
                    <Campo rotulo="Salário informado" valor={solicitacaoOrigem.salario} icone="fa-money-bill-wave" />
                    <Campo rotulo="Vínculo" valor={solicitacaoOrigem.categoria_trabalhador} icone="fa-file-contract" />
                    <Campo rotulo="Período de experiência" valor={solicitacaoOrigem.periodo_experiencia} icone="fa-hourglass-half" />
                    <Campo rotulo="Horário de trabalho" valor={solicitacaoOrigem.horario_trabalho} icone="fa-clock" />
                    <Campo rotulo="Jornada (h/mês)" valor={solicitacaoOrigem.horas_mensais || solicitacaoOrigem.jornada_trabalho} icone="fa-business-time" />
                    <Campo rotulo="Filial" valor={solicitacaoOrigem.filial} icone="fa-map-marker-alt" />
                    <Campo rotulo="E-mail pessoal" valor={solicitacaoOrigem.email_colaborador} icone="fa-at" />
                    <Campo rotulo="Telefone" valor={solicitacaoOrigem.telefone_colaborador} icone="fa-mobile-alt" />
                  </div>
                  {solicitacaoOrigem.informacoes_adicionais && (
                    <div className="border rounded p-2 bg-light mb-2" style={{ whiteSpace: 'pre-wrap', fontSize: '.88rem' }}>
                      <i className="fas fa-comment-dots me-2 text-muted"></i>
                      {solicitacaoOrigem.informacoes_adicionais}
                    </div>
                  )}
                </>)}

                <h6 className="fw-bold text-muted border-bottom pb-1 mb-3 mt-3">
                  <i className="fas fa-user me-2"></i>Dados pessoais e contato
                </h6>
                <div className="row">
                  <Campo rotulo="CPF" valor={colaboradorDetalhe.cpf} icone="fa-id-card" />
                  <Campo rotulo="PIS" valor={colaboradorDetalhe.pis} icone="fa-id-badge" />
                  <Campo rotulo="RG" valor={colaboradorDetalhe.rg} icone="fa-address-card" />
                  <Campo rotulo="Nascimento" valor={colaboradorDetalhe.data_nascimento} icone="fa-cake-candles" />
                  <Campo rotulo="Gênero" valor={colaboradorDetalhe.genero} icone="fa-venus-mars" />
                  <Campo rotulo="Estado Civil" valor={colaboradorDetalhe.estado_civil} icone="fa-ring" />
                  <Campo rotulo="E-mail" valor={colaboradorDetalhe.email} icone="fa-envelope" />
                  <Campo rotulo="E-mail corporativo" valor={colaboradorDetalhe.email_corporativo} icone="fa-envelope-open" />
                  <Campo rotulo="Celular" valor={colaboradorDetalhe.celular} icone="fa-mobile-alt" />
                  <Campo rotulo="Telefone" valor={colaboradorDetalhe.telefone} icone="fa-phone" />
                  <Campo rotulo="CEP" valor={colaboradorDetalhe.cep} icone="fa-map-pin" />
                  <Campo rotulo="Cidade/UF" valor={[colaboradorDetalhe.cidade, colaboradorDetalhe.uf].filter(Boolean).join('/')} icone="fa-map-marker-alt" />
                </div>

                {/* ─── REGISTRO COMPLETO DA CONVENIA ─── */}
                <div className="mt-3">
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary w-100"
                    onClick={() => setMostrarCompleto(v => !v)}
                  >
                    <i className={`fas ${mostrarCompleto ? 'fa-chevron-up' : 'fa-chevron-down'} me-2`}></i>
                    {mostrarCompleto ? 'Ocultar' : 'Mostrar'} todos os dados da Convenia
                    (endereço, bancos, benefícios, CTPS, formações...)
                  </button>
                  {mostrarCompleto && (
                    <div className="border rounded p-3 mt-2 bg-light" style={{ maxHeight: '45vh', overflowY: 'auto' }}>
                      {valorVazio(colaboradorDetalhe.dados_completos) ? (
                        <span className="text-muted">Sem dados adicionais no espelho.</span>
                      ) : (
                        <RenderValor valor={colaboradorDetalhe.dados_completos} />
                      )}
                    </div>
                  )}
                </div>
                </>)}
              </div>

              <div className="modal-footer bg-light">
                {/* Exclusão em duas etapas: o clique arma a confirmação */}
                {confirmandoExclusao ? (
                  <div className="me-auto d-flex align-items-center gap-2 flex-wrap">
                    <span className="small text-danger fw-bold">
                      <i className="fas fa-triangle-exclamation me-1"></i>
                      Remove do Saber (dados + checklist). Na Convenia a exclusão é manual.
                    </span>
                    <button type="button" className="btn btn-sm btn-danger"
                      onClick={excluirAdmissao} disabled={excluindoAdmissao}>
                      {excluindoAdmissao
                        ? <span className="spinner-border spinner-border-sm"></span>
                        : <><i className="fas fa-trash me-1"></i>Confirmar exclusão</>}
                    </button>
                    <button type="button" className="btn btn-sm btn-secondary"
                      onClick={() => setConfirmandoExclusao(false)} disabled={excluindoAdmissao}>
                      Cancelar
                    </button>
                  </div>
                ) : (
                  <button type="button" className="btn btn-sm btn-outline-danger me-auto"
                    onClick={() => setConfirmandoExclusao(true)}
                    title="Remove esta admissão do banco do Saber (não altera a Convenia)">
                    <i className="fas fa-trash me-2"></i>
                    Deletar admissão
                  </button>
                )}
                <button type="button" className="btn btn-secondary" onClick={() => setColaboradorDetalhe(null)}>
                  <i className="fas fa-times me-2"></i>
                  Fechar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ─── MODAL: VA DO RECÉM-ADMITIDO ─── */}
      {/* ─── Modal: solicitar exame admissional à clínica ─── */}
      {exameAdm && (
        <div className="modal fade show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 10002 }}
          onClick={(e) => { if (e.target === e.currentTarget) setExameAdm(null); }}>
          <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
            <div className="modal-content">
              <div className="modal-header bg-white">
                <div>
                  <h5 className="modal-title" style={{ color: '#212529' }}>
                    <i className="fas fa-stethoscope me-2" style={{ color: '#00b5cc' }}></i>Solicitar exame admissional
                  </h5>
                  <div className="small" style={{ color: '#6c757d' }}>
                    {exameAdm.colab.nome_completo || exameAdm.colab.nome} · {empresaSelecionada?.cliente}
                    {exameAdm.config?.clinica_nome && <> · clínica <b>{exameAdm.config.clinica_nome}</b></>}
                  </div>
                </div>
                <button type="button" className="btn-close" onClick={() => setExameAdm(null)}></button>
              </div>
              <div className="modal-body">
                {!exameAdm.config?.empresa && (
                  <div className="alert alert-warning py-2 small">
                    <i className="fas fa-triangle-exclamation me-2"></i>
                    Este cliente ainda não tem os <b>dados da guia</b> (empresa, CNPJ, unidade, clínica). Cadastre em{' '}
                    <Link to={`/Dp/Exame%20Mensal?cliente=${exameAdm.cliente_id}`} target="_blank">Exame Mensal → Dados da guia</Link>{' '}
                    — a guia sai com o que houver.
                  </div>
                )}
                <div className="row g-3">
                  <div className="col-md-7">
                    <label className="form-label small fw-semibold mb-1">E-mail da clínica (destinatário)</label>
                    <div className="input-group input-group-sm">
                      <input type="text" className="form-control" placeholder="agendamento@clinica.com.br; coordenacao@..."
                        value={exameAdm.destinatario}
                        onChange={(e) => setExameAdm(x => ({ ...x, destinatario: e.target.value }))} />
                      {(exameAdm.destinatario || '').trim() !== (exameAdm.config?.clinica_email || '') && (
                        <button className="btn btn-outline-primary" type="button" disabled={exameAdm.destSalvando}
                          onClick={salvarDestinatarioAdm} title="Grava na configuração da guia deste cliente">
                          {exameAdm.destSalvando ? <span className="spinner-border spinner-border-sm"></span> : 'Salvar'}
                        </button>
                      )}
                    </div>
                    {!exameAdm.config?.clinica_email && (
                      <div className="form-text text-danger">Sem e-mail da clínica o envio é recusado — informe e salve.</div>
                    )}
                  </div>
                  <div className="col-md-5">
                    <label className="form-label small fw-semibold mb-1">Operador que assina</label>
                    <select className="form-select form-select-sm" value={exameAdm.operador_id}
                      onChange={(e) => setExameAdm(x => ({ ...x, operador_id: e.target.value }))}>
                      <option value="">Selecione…</option>
                      {exameAdm.remetentes.map(r => <option key={r.id} value={r.id}>{r.nome}{r.email ? ` · ${r.email}` : ''}</option>)}
                    </select>
                  </div>
                  <div className="col-md-4">
                    <label className="form-label small fw-semibold mb-1">Data do exame (opcional)</label>
                    <input type="date" className="form-control form-control-sm" value={exameAdm.data}
                      onChange={(e) => setExameAdm(x => ({ ...x, data: e.target.value }))} />
                  </div>
                  <div className="col-md-8">
                    <label className="form-label small fw-semibold mb-1">Assunto</label>
                    <input type="text" className="form-control form-control-sm" value={exameAdm.assunto}
                      onChange={(e) => setExameAdm(x => ({ ...x, assunto: e.target.value }))} />
                  </div>
                  <div className="col-12">
                    <label className="form-label small fw-semibold mb-1">Texto do e-mail</label>
                    <textarea className="form-control form-control-sm" rows={9} value={exameAdm.corpo}
                      onChange={(e) => setExameAdm(x => ({ ...x, corpo: e.target.value }))} />
                    <div className="form-text">A guia em PDF vai anexada; cópia ao grupo de e-mail do cliente, quando cadastrado.</div>
                  </div>
                </div>
                {/* a clínica não agenda com CPF/RG/nascimento/cargo em branco */}
                <ComplementoGuiaExame clienteId={exameAdm.cliente_id} conveniaId={exameAdm.colab.convenia_id}
                  faltando={exameAdm.faltando}
                  onSalvo={() => abrirExameAdm(exameAdm.colab)} />
                {exameAdm.historico.length > 0 && (
                  <div className="mt-3 small">
                    <div className="fw-semibold mb-1"><i className="fas fa-clock-rotate-left me-1 text-muted"></i>Pedidos anteriores</div>
                    {exameAdm.historico.map((h, i) => (
                      <div key={i} className="text-muted">{h.em} · {h.tipo}{h.data_exame ? ` · exame ${h.data_exame}` : ''} · para {h.para}{h.operador ? ` · por ${h.operador}` : ''}</div>
                    ))}
                  </div>
                )}
              </div>
              <div className="modal-footer bg-white flex-wrap gap-2">
                <div className="me-auto d-flex gap-2 flex-wrap">
                  <button type="button" className="btn btn-light border" onClick={verGuiaAdm}>
                    <i className="fas fa-file-pdf me-2 text-danger"></i>Ver guia (PDF)
                  </button>
                  <button type="button" className="btn btn-outline-primary" onClick={visualizarEmailAdm}
                    title="O e-mail como vai chegar à clínica, com a guia anexada">
                    <i className="fas fa-eye me-2"></i>Visualizar e-mail
                  </button>
                </div>
                {exameAdm.config?.clinica_portal && (
                  <>
                    <a className="btn btn-outline-secondary" href={exameAdm.config.clinica_portal} target="_blank" rel="noreferrer"
                      title="Algumas clínicas só recebem o pedido pelo site">
                      <i className="fas fa-arrow-up-right-from-square me-2"></i>Portal da clínica
                    </a>
                    <button type="button" className="btn btn-outline-primary" disabled={exameAdmEnviando} onClick={registrarExamePortalAdm}
                      title="Fez o pedido no site da clínica? Registra aqui e marca a tarefa como Solicitado">
                      <i className="fas fa-check me-2"></i>Pedido feito pelo portal
                    </button>
                  </>
                )}
                <button type="button" className="btn btn-secondary" onClick={() => setExameAdm(null)}>Cancelar</button>
                <button type="button" className="btn btn-primary" disabled={exameAdmEnviando || !exameAdm.operador_id || !exameAdm.config?.clinica_email || (exameAdm.faltando || []).length > 0}
                  onClick={visualizarEmailAdm} title="Abre a prévia; o envio é confirmado lá">
                  <i className="fas fa-eye me-2"></i>Ver prévia e enviar
                </button>
              </div>
            </div>
          </div>
          {/* prévia por cima do modal do exame: o Enviar de verdade fica aqui */}
          {emailPreviewAdm && (
            <div className="modal fade show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.55)', zIndex: 10003 }}
              onClick={(e) => { if (e.target === e.currentTarget && !exameAdmEnviando) fecharPreviewAdm(); }}>
              <div className="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable">
                <div className="modal-content shadow-lg">
                  <div className="modal-header bg-white">
                    <h5 className="modal-title" style={{ color: '#212529' }}>
                      <i className="fas fa-envelope-open-text me-2" style={{ color: '#00b5cc' }}></i>Prévia do e-mail à clínica
                    </h5>
                    <button type="button" className="btn-close" disabled={exameAdmEnviando} onClick={fecharPreviewAdm}></button>
                  </div>
                  <div className="modal-body p-0">
                    {emailPreviewAdm.carregando ? (
                      <div className="text-center py-5"><span className="spinner-border text-primary"></span>
                        <div className="text-muted small mt-2">Montando o e-mail e a guia…</div></div>
                    ) : (
                      <>
                        <div className="px-3 py-2 border-bottom small" style={{ background: '#f8f9fa', lineHeight: 1.7 }}>
                          <div><span className="text-muted" style={{ display: 'inline-block', width: 64 }}>De</span><b>{emailPreviewAdm.de}</b></div>
                          <div><span className="text-muted" style={{ display: 'inline-block', width: 64 }}>Para</span><b>{emailPreviewAdm.para}</b></div>
                          {emailPreviewAdm.cc && <div><span className="text-muted" style={{ display: 'inline-block', width: 64 }}>Cc</span>{emailPreviewAdm.cc}</div>}
                          {emailPreviewAdm.bcc && <div><span className="text-muted" style={{ display: 'inline-block', width: 64 }}>Cco</span>{emailPreviewAdm.bcc}</div>}
                          <div><span className="text-muted" style={{ display: 'inline-block', width: 64 }}>Assunto</span><b>{emailPreviewAdm.assunto}</b></div>
                          <div><span className="text-muted" style={{ display: 'inline-block', width: 64 }}>Anexo</span><i className="fas fa-paperclip me-1"></i>{emailPreviewAdm.anexo}</div>
                        </div>
                        <div className="row g-0">
                          <div className="col-lg-7 border-end">
                            <iframe title="E-mail" srcDoc={emailPreviewAdm.html} sandbox="" style={{ width: '100%', height: '58vh', border: 0, background: '#fff' }} />
                          </div>
                          <div className="col-lg-5">
                            <iframe title="Guia em PDF" src={emailPreviewAdm.pdfUrl} style={{ width: '100%', height: '58vh', border: 0 }} />
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                  <div className="modal-footer bg-white">
                    <button type="button" className="btn btn-secondary" disabled={exameAdmEnviando} onClick={fecharPreviewAdm}>
                      <i className="fas fa-arrow-left me-2"></i>Voltar e ajustar
                    </button>
                    <button type="button" className="btn btn-primary"
                      disabled={exameAdmEnviando || emailPreviewAdm.carregando || !exameAdm.operador_id || !exameAdm.config?.clinica_email || (exameAdm.faltando || []).length > 0}
                      onClick={enviarExameAdm}>
                      {exameAdmEnviando ? <><span className="spinner-border spinner-border-sm me-2"></span>Enviando…</> : <><i className="fas fa-paper-plane me-2"></i>Enviar à clínica</>}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {benefFin && (
        <div className="modal" tabIndex="-1" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 10050, overflowY: 'auto' }}>
          <div className="modal-dialog modal-lg modal-dialog-centered">
            <div className="modal-content shadow-lg">
              <div className="modal-header" style={{ background: '#fff', borderBottom: '1px solid #e9ecef' }}>
                <div>
                  <h5 className="modal-title mb-0 fw-bold" style={{ color: '#212529' }}>
                    <i className="fas fa-money-check-dollar me-2"></i>Benefícios ao financeiro
                  </h5>
                  <small style={{ color: '#6c757d' }}>{benefFin.dados.colaborador} · admissão em {benefFin.dados.admissao}</small>
                </div>
                <button type="button" className="btn-close" onClick={() => setBenefFin(null)} disabled={benefFin.enviando}></button>
              </div>
              <div className="modal-body">
                <table className="table table-sm align-middle mb-2" style={{ fontSize: '.85rem' }}>
                  <thead className="table-light">
                    <tr><th>Benefício</th><th className="text-center">Dias</th><th className="text-end">Valor por dia</th><th className="text-end">Total</th></tr>
                  </thead>
                  <tbody>
                    {(benefFin.dados.itens || []).map((i, k) => (
                      <tr key={k}>
                        <td><b>{i.beneficio}</b><div className="small text-muted">{i.detalhe}</div></td>
                        <td className="text-center">{i.dias ?? '—'}</td>
                        <td className="text-end">{i.valor_dia ? Number(i.valor_dia).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) : '—'}</td>
                        <td className="text-end">{Number(i.total).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</td>
                      </tr>
                    ))}
                    {!(benefFin.dados.itens || []).length && (
                      <tr><td colSpan={4} className="text-muted small text-center">Nenhum benefício a pagar para esta pessoa no mês da admissão.</td></tr>
                    )}
                  </tbody>
                  <tfoot>
                    <tr className="fw-bold"><td colSpan={3} className="text-end">Total a pagar</td>
                      <td className="text-end" style={{ color: '#116b47' }}>{Number(benefFin.dados.total || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</td></tr>
                  </tfoot>
                </table>
                {(benefFin.dados.avisos || []).map((a, k) => (
                  <div key={k} className="small text-warning-emphasis"><i className="fas fa-circle-info me-1"></i>{a}</div>
                ))}

                <div className="row g-2 mt-2">
                  <div className="col-md-6">
                    <label className="form-label small fw-semibold">Para (financeiro do cliente)</label>
                    <input type="text" className="form-control form-control-sm" placeholder="financeiro@cliente.com.br"
                      value={benefFin.para} onChange={(e) => mudarBenefFin('para', e.target.value)} />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label small fw-semibold">Cópia (opcional)</label>
                    <input type="text" className="form-control form-control-sm" value={benefFin.copia}
                      onChange={(e) => mudarBenefFin('copia', e.target.value)} />
                  </div>
                </div>
                <div className="d-flex align-items-end gap-2 mt-2 flex-wrap">
                  <div>
                    <label className="form-label small fw-semibold">Enviar como</label>
                    <select className="form-select form-select-sm" style={{ width: 'auto' }} value={benefFin.operador_id}
                      onChange={(e) => mudarBenefFin('operador_id', e.target.value)}>
                      <option value="">— escolha —</option>
                      {(benefFin.dados.operadores || []).map((o) => (
                        <option key={o.id} value={o.id} disabled={!o.pronto}>{o.nome}{o.pronto ? '' : ' (sem senha de app)'}</option>
                      ))}
                    </select>
                  </div>
                  <button type="button" className="btn btn-sm btn-outline-secondary" onClick={salvarEmailsBenefFin}
                    disabled={!benefFin.para.trim()} title="Guardar os e-mails do financeiro (valem também para o VA/VT mensal)">
                    <i className="fas fa-save me-1"></i>Salvar e-mails
                  </button>
                  <label className="btn btn-sm btn-outline-secondary mb-0" title="PDF opcional: boleto ou pedido complementar">
                    <i className="fas fa-paperclip me-1"></i>{benefFin.anexo ? benefFin.anexo.name : 'Anexar PDF (opcional)'}
                    <input type="file" accept="application/pdf,.pdf" hidden
                      onChange={(e) => { const f = e.target.files && e.target.files[0]; e.target.value = ''; if (f) mudarBenefFin('anexo', f); }} />
                  </label>
                  {benefFin.anexo && (
                    <button type="button" className="btn btn-sm btn-light border" onClick={() => mudarBenefFin('anexo', null)} title="Tirar o anexo">
                      <i className="fas fa-times text-danger"></i>
                    </button>
                  )}
                  {!benefFin.previa && (benefFin.dados.itens || []).length > 0 && (
                    <button type="button" className="btn btn-sm btn-outline-primary ms-auto" onClick={() => previaBenefFin(benefFin)} disabled={benefFin.carregando}>
                      <i className={`fas ${benefFin.carregando ? 'fa-spinner fa-spin' : 'fa-eye'} me-1`}></i>Atualizar prévia
                    </button>
                  )}
                </div>

                {benefFin.previa && (<>
                  <div className="small mt-3 mb-2">
                    <div><b>Assunto:</b> {benefFin.previa.assunto}</div>
                    <div><b>De:</b> {benefFin.previa.de}</div>
                    <div><b>Para:</b> {benefFin.previa.para || <span className="text-danger">informe o e-mail do financeiro</span>}</div>
                    {benefFin.previa.copia && <div><b>Cópia:</b> {benefFin.previa.copia}</div>}
                    <div><b>Anexo:</b> {benefFin.previa.anexo || 'nenhum'}</div>
                  </div>
                  <div className="border rounded" style={{ maxHeight: 320, overflow: 'auto' }}>
                    <iframe title="Prévia do e-mail de benefícios da admissão" srcDoc={benefFin.previa.corpo_previa || benefFin.previa.corpo_html}
                      sandbox="" style={{ width: '100%', height: 320, border: 0 }} />
                  </div>
                </>)}
                {(benefFin.dados.historico || []).length > 0 && (
                  <div className="small text-muted mt-2">
                    <i className="fas fa-clock-rotate-left me-1"></i>Já enviado: {benefFin.dados.historico.map((h) => `${h.em} para ${h.para}${h.por ? ` (${h.por})` : ''}`).join(' · ')}
                  </div>
                )}
              </div>
              <div className="modal-footer bg-light">
                <span className="me-auto small text-muted">Ao enviar, a tarefa “Benefícios ao financeiro” fica concluída.</span>
                <button className="btn btn-secondary" onClick={() => setBenefFin(null)} disabled={benefFin.enviando}>
                  <i className="fas fa-times me-2"></i>Fechar
                </button>
                <button className="btn btn-success" onClick={enviarBenefFin}
                  disabled={benefFin.enviando || !benefFin.previa || !benefFin.para.trim() || !benefFin.operador_id || !(benefFin.dados.itens || []).length}
                  title={!benefFin.previa ? 'Veja a prévia antes de enviar' : undefined}>
                  <i className={`fas ${benefFin.enviando ? 'fa-spinner fa-spin' : 'fa-paper-plane'} me-2`}></i>
                  {benefFin.enviando ? 'Enviando...' : 'Enviar ao financeiro'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {vaModal && (
        <div className="modal" tabIndex="-1"
          style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 10001 }}>
          <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: '560px' }}>
            <div className="modal-content shadow-lg">
              <div className="modal-header" style={{ backgroundColor: '#ffffff', borderBottom: '1px solid #e9ecef' }}>
                <div>
                  <h5 className="modal-title mb-0 fw-bold" style={{ color: '#212529' }}>
                    <i className="fas fa-utensils me-2"></i>Vale-alimentação da admissão
                  </h5>
                  <small style={{ color: '#6c757d' }}>
                    {vaModal.previa?.colaborador} · admitido em {vaModal.previa?.admissao}
                    {vaModal.previa?.vinculo && ` · ${vaModal.previa.vinculo}`}
                  </small>
                </div>
                <button type="button" className="btn-close"
                  onClick={() => setVaModal(null)} disabled={gerandoVa}></button>
              </div>

              <div className="modal-body">
                {vaModal.previa?.ja_pago && (
                  <div className="alert alert-warning small">
                    <i className="fas fa-triangle-exclamation me-2"></i>
                    Já existe crédito registrado para {vaModal.previa.competencia_rotulo}:{' '}
                    <b>{brl(vaModal.previa.ja_pago.valor)}</b> ({vaModal.previa.ja_pago.dias} dias)
                    em {vaModal.previa.ja_pago.pago_em}. Gerar de novo substitui esse registro.
                  </div>
                )}

                <div className="row g-3">
                  <div className="col-md-6">
                    <label className="form-label small fw-semibold">Pagar VA até</label>
                    <input type="date" className="form-control"
                      value={vaModal.ate}
                      min={vaModal.previa?.admissao?.split('/').reverse().join('-')}
                      onChange={(e) => recalcularVa('ate', e.target.value)} />
                    <div className="form-text">Conta dias úteis da admissão até esta data.</div>
                  </div>
                  <div className="col-md-6">
                    <label className="form-label small fw-semibold d-flex align-items-center gap-2">
                      <span>Como informar o valor</span>
                    </label>
                    <div className="btn-group btn-group-sm w-100 mb-2" role="group" aria-label="Modo do valor">
                      <button type="button"
                        className={`btn ${vaModal.modo !== 'integral' ? 'btn-primary' : 'btn-outline-primary'}`}
                        onClick={() => recalcularVa('modo', 'dia')}
                        title="Custo por dia útil × dias úteis do período">
                        <i className="fas fa-calendar-day me-1"></i>Por dia útil
                      </button>
                      <button type="button"
                        className={`btn ${vaModal.modo === 'integral' ? 'btn-primary' : 'btn-outline-primary'}`}
                        onClick={() => recalcularVa('modo', 'integral')}
                        title="Valor fechado do período, sem depender dos dias úteis">
                        <i className="fas fa-sack-dollar me-1"></i>Valor integral
                      </button>
                    </div>
                    {vaModal.modo === 'integral' ? (
                      <>
                        <input type="text" className="form-control"
                          placeholder="ex.: 660,00"
                          value={vaModal.valor_total}
                          onChange={(e) => recalcularVa('valor_total', e.target.value)} />
                        <div className="form-text">
                          Valor fechado do crédito, independente dos dias úteis.
                          {vaModal.previa?.custo_mes != null && ` Padrão mensal do vínculo: ${brl(vaModal.previa.custo_mes)}.`}
                        </div>
                      </>
                    ) : (
                      <>
                        <input type="text" className="form-control"
                          placeholder="ex.: 33,50"
                          value={vaModal.custo_dia}
                          onChange={(e) => recalcularVa('custo_dia', e.target.value)} />
                        <div className="form-text">
                          {vaModal.previa?.custo_individual
                            ? 'Valor individual deste colaborador (R$/dia).'
                            : `Custo por dia — padrão do vínculo ${vaModal.previa?.vinculo || ''} no Controle de VA.`}
                        </div>
                      </>
                    )}
                  </div>
                </div>

                <div className="mt-3 p-3 rounded" style={{ background: '#f2f4f8', border: '1px solid #dee2e6' }}>
                  <div className="d-flex justify-content-between">
                    <span className="text-muted small">Dias úteis no período{vaModal.modo === 'integral' ? ' (só informativo)' : ''}</span>
                    <b style={{ fontVariantNumeric: 'tabular-nums' }}>{vaModal.previa?.dias ?? '—'}</b>
                  </div>
                  {vaModal.modo === 'integral' && vaModal.previa?.custo_dia != null && (
                    <div className="d-flex justify-content-between mt-1">
                      <span className="text-muted small">Equivale por dia útil (registro)</span>
                      <b style={{ fontVariantNumeric: 'tabular-nums' }}>{brl(vaModal.previa.custo_dia)}</b>
                    </div>
                  )}
                  <div className="d-flex justify-content-between mt-1">
                    <span className="text-muted small">Competência do crédito</span>
                    <b>{vaModal.previa?.competencia_rotulo || '—'}</b>
                  </div>
                  <div className="d-flex justify-content-between mt-1">
                    <span className="text-muted small">Plataforma</span>
                    <b>{vaModal.previa?.plataforma || 'não cadastrada'}</b>
                  </div>
                  <hr className="my-2" />
                  <div className="d-flex justify-content-between align-items-center">
                    <span className="fw-semibold">Valor do crédito</span>
                    <span className="fs-5 fw-bold" style={{ color: '#116b47' }}>
                      {brl(vaModal.previa?.valor || 0)}
                    </span>
                  </div>
                </div>

                {!vaModal.previa?.formato && (
                  <div className="alert alert-danger small mt-3 mb-0">
                    <i className="fas fa-circle-exclamation me-2"></i>
                    Esta empresa não tem plataforma de VA com layout disponível — cadastre o
                    serviço de benefícios em BPO RH → Serviços.
                  </div>
                )}
                {!vaModal.previa?.tem_cpf && (
                  <div className="alert alert-danger small mt-3 mb-0">
                    <i className="fas fa-circle-exclamation me-2"></i>
                    Colaborador sem CPF no espelho da Convenia — o arquivo não pode ser gerado.
                  </div>
                )}

                <div className="alert alert-light border small mt-3 mb-0">
                  <i className="fas fa-circle-info me-2 text-primary"></i>
                  Ao baixar, o crédito é registrado como <b>pago</b> no Controle de VA desta
                  competência — o colaborador não entra de novo no arquivo do mês.
                </div>
              </div>

              <div className="modal-footer bg-light">
                <button className="btn btn-secondary" onClick={() => setVaModal(null)}
                  disabled={gerandoVa}>
                  <i className="fas fa-times me-2"></i>Cancelar
                </button>
                <button className="btn btn-success" onClick={gerarArquivoVa}
                  disabled={gerandoVa || !vaModal.previa?.formato || !vaModal.previa?.tem_cpf
                    || !(vaModal.previa?.valor > 0)}>
                  <i className={`fas ${gerandoVa ? 'fa-spinner fa-spin' : 'fa-file-arrow-down'} me-2`}></i>
                  {gerandoVa ? 'Gerando...' : 'Baixar arquivo e registrar'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ─── MODAL: PRÉVIA DO E-MAIL DE AVISO AO GESTOR ─── */}
      {avisoPrevia && avisoForm && (
        <div className="modal" tabIndex="-1"
          style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 10000 }}>
          <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
            <div className="modal-content shadow-lg">
              <div className="modal-header" style={{ backgroundColor: '#ffffff', borderBottom: '1px solid #e9ecef' }}>
                <div>
                  <h5 className="modal-title mb-0 fw-bold" style={{ color: '#212529' }}>
                    <i className="fas fa-paper-plane me-2" style={{ color: '#00b5cc' }}></i>
                    {avisoPrevia.titulo || 'Aviso ao Gestor'} — prévia do e-mail
                  </h5>
                  <small style={{ color: '#6c757d' }}>
                    {avisoPrevia.colaborador} · {avisoPrevia.cliente}
                  </small>
                </div>
                <button type="button" className="btn-close"
                  onClick={() => { setAvisoPrevia(null); setAvisoForm(null); }}></button>
              </div>

              <div className="modal-body">
                {!avisoPrevia.destinatario && (
                  <div className="alert alert-warning small">
                    <i className="fas fa-exclamation-triangle me-2"></i>
                    Não encontrei o e-mail de <strong>{avisoPrevia.paraQuem}</strong> — informe
                    manualmente abaixo.
                  </div>
                )}
                {avisoPrevia.destinatario && ['corporativo', 'alternativo'].includes(avisoPrevia.sem_email_pessoal) && (
                  <div className="alert alert-warning small">
                    <i className="fas fa-exclamation-triangle me-2"></i>
                    <strong>{avisoPrevia.colaborador || 'O colaborador'}</strong> não tem <strong>e-mail pessoal</strong> cadastrado
                    na Convenia — o "Para" veio com o e-mail <strong>{avisoPrevia.sem_email_pessoal}</strong>. Confira se é
                    esse mesmo ou peça o pessoal ao cliente/colaborador e atualize na Convenia.
                  </div>
                )}
                {avisoPrevia.campo === 'aviso_gestor' && avisoPrevia.destinatario && !avisoPrevia.solicitacao_bate && (
                  <div className="alert alert-warning small">
                    <i className="fas fa-exclamation-triangle me-2"></i>
                    O e-mail sugerido veio da <strong>última solicitação desta empresa</strong> (não
                    de uma solicitação com o nome deste colaborador). Confirme antes de enviar.
                  </div>
                )}

                <div className="row g-3 mb-3">
                  {avisoPrevia.campo === 'email_ponto' && (avisoPrevia.sistemas || []).length > 1 && (
                    <div className="col-12">
                      <label className="form-label small fw-bold mb-1">
                        <i className="fas fa-clock me-1 text-muted"></i>Modelo (sistema de ponto)
                      </label>
                      <div className="btn-group w-100" role="group" aria-label="Modelo do e-mail do ponto">
                        {avisoPrevia.sistemas.map(s => (
                          <button key={s.tipo} type="button"
                            className={`btn btn-sm ${avisoPrevia.sistema === s.tipo ? 'btn-primary' : 'btn-outline-primary'}`}
                            disabled={!!carregandoPrevia}
                            onClick={() => {
                              if (s.tipo === avisoPrevia.sistema) return;
                              abrirEmailAutomacao(avisoPrevia.colab, 'email_ponto', s.tipo,
                                { operador_id: avisoForm.operador_id, destinatario: avisoForm.destinatario });
                            }}>
                            {carregandoPrevia && avisoPrevia.sistema !== s.tipo
                              ? <span className="spinner-border spinner-border-sm me-1"></span> : null}
                            {s.rotulo}
                          </button>
                        ))}
                      </div>
                      <div className="form-text">
                        Esta empresa tem mais de um ponto: escolha o do colaborador. Trocar o modelo
                        descarta a edição do texto. Cada sistema guarda o seu próprio texto.
                      </div>
                    </div>
                  )}
                  <div className="col-md-6">
                    <label className="form-label small fw-bold mb-1">
                      <i className="fas fa-user-tie me-1 text-muted"></i>Enviar como (operador)
                    </label>
                    <select className="form-select" value={avisoForm.operador_id}
                      onChange={(e) => setAvisoForm(f => ({ ...f, operador_id: e.target.value }))}>
                      {(avisoPrevia.operadores || []).map(o => (
                        <option key={o.id} value={o.id} disabled={!o.pronto}>
                          {o.nome}{o.email ? ` — ${o.email}` : ''}{!o.pronto ? ' (sem e-mail/senha)' : ''}
                        </option>
                      ))}
                    </select>
                    <div className="form-text">Operador do cliente: {avisoPrevia.operador_cliente || '—'}</div>
                  </div>
                  <div className="col-md-6">
                    <label className="form-label small fw-bold mb-1">
                      <i className="fas fa-envelope me-1 text-muted"></i>Para ({avisoPrevia.paraQuem})
                    </label>
                    <input type="email" className="form-control" value={avisoForm.destinatario}
                      onChange={(e) => setAvisoForm(f => ({ ...f, destinatario: e.target.value }))} />
                  </div>
                  <div className="col-12">
                    <label className="form-label small fw-bold mb-1">
                      <i className="fas fa-heading me-1 text-muted"></i>Assunto
                    </label>
                    <input type="text" className="form-control" value={avisoForm.assunto}
                      onChange={(e) => setAvisoForm(f => ({ ...f, assunto: e.target.value }))} />
                  </div>
                </div>

                {avisoForm.conteudo_html ? (
                  <>
                    <div className="small fw-bold text-muted mb-1">
                      <i className="fas fa-pen me-1"></i>Texto da mensagem
                    </div>
                    <EditorEmail
                      valor={avisoForm.conteudo_html}
                      onChange={(v) => setAvisoForm(f => ({ ...f, conteudo_html: v }))}
                      altura={300}
                    />
                    <button type="button" className="btn btn-sm btn-light border mt-2"
                      onClick={() => setVerPreviaEmail(v => !v)}>
                      <i className={`fas ${verPreviaEmail ? 'fa-eye-slash' : 'fa-eye'} me-2`}></i>
                      {verPreviaEmail ? 'Esconder' : 'Ver'} como o e-mail vai ficar
                    </button>
                  </>
                ) : (
                  <div className="small fw-bold text-muted mb-1">
                    <i className="fas fa-eye me-1"></i>Prévia da mensagem
                  </div>
                )}
                {(!avisoForm.conteudo_html || verPreviaEmail) && (
                  <div className="border rounded mt-2" style={{ overflow: 'hidden' }}>
                    {/* sandbox="" é obrigatório: srcdoc herda a origem do app e o HTML
                        carrega dados do formulário público — sem isso um script ali
                        alcançaria o token no localStorage. O logo em data: URI segue ok. */}
                    <iframe
                      title="Prévia do e-mail"
                      srcDoc={avisoForm.conteudo_html
                        ? avisoForm.corpo_previa.replace(
                          /(<!--miolo-->)[\s\S]*(<!--\/miolo-->)/, `$1${avisoForm.conteudo_html}$2`)
                        : avisoForm.corpo_previa}
                      sandbox=""
                      style={{ width: '100%', height: '420px', border: 'none', background: '#f4f6fa' }}
                    />
                  </div>
                )}
              </div>

              <div className="modal-footer bg-light">
                <button className="btn btn-secondary me-auto"
                  onClick={() => { setAvisoPrevia(null); setAvisoForm(null); }}>
                  <i className="fas fa-times me-2"></i>Cancelar
                </button>
                <button
                  className="btn btn-success"
                  disabled={enviandoAviso || !avisoForm.destinatario ||
                    !(avisoPrevia.operadores || []).some(o => String(o.id) === String(avisoForm.operador_id) && o.pronto)}
                  onClick={enviarAvisoGestor}
                >
                  {enviandoAviso
                    ? <><span className="spinner-border spinner-border-sm me-2"></span>Enviando...</>
                    : <><i className="fas fa-paper-plane me-2"></i>Enviar e-mail</>}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ─── MODAL: SOLICITAÇÃO DE ADMISSÃO (editável) ─── */}
      {solicitacaoDetalhe && solForm && (
        <div
          className="modal"
          tabIndex="-1"
          style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 9999 }}
        >
          <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
            <div className="modal-content shadow-lg">
              <div className="modal-header" style={{ backgroundColor: '#ffffff', borderBottom: '1px solid #e9ecef' }}>
                <div>
                  <h5 className="modal-title mb-0 fw-bold" style={{ color: '#212529' }}>
                    <i className="fas fa-inbox me-2" style={{ color: '#f1be48' }}></i>
                    Solicitação de Admissão
                  </h5>
                  <small style={{ color: '#6c757d' }}>
                    <i className="fas fa-paper-plane me-1"></i>
                    Enviada por <strong>{solicitacaoDetalhe.email_solicitante}</strong> em {solicitacaoDetalhe.created_at}
                    {solicitacaoDetalhe.operador && <> · operador: <strong>{solicitacaoDetalhe.operador}</strong></>}
                  </small>
                </div>
                <button type="button" className="btn-close" onClick={() => setSolicitacaoDetalhe(null)}></button>
              </div>

              <div className="modal-body">
                {/* ── Colaborador ── */}
                <h6 className="fw-bold text-muted border-bottom pb-1 mb-3">
                  <i className="fas fa-user me-2"></i>Colaborador
                </h6>
                <div className="row g-3 mb-3">
                  <div className="col-md-6">
                    <label className="form-label small fw-bold mb-1">Nome completo <span className="text-danger">*</span></label>
                    <input type="text" className="form-control" value={solForm.colaborador} onChange={setSol('colaborador')} />
                  </div>
                  <div className="col-md-3">
                    <label className="form-label small fw-bold mb-1">E-mail pessoal</label>
                    <input type="email" className="form-control" value={solForm.email_colaborador} onChange={setSol('email_colaborador')} />
                  </div>
                  <div className="col-md-3">
                    <label className="form-label small fw-bold mb-1">Telefone</label>
                    <input type="text" className="form-control" value={solForm.telefone_colaborador} onChange={setSol('telefone_colaborador')} />
                  </div>
                </div>

                {/* ── Empresa e vaga ── */}
                <h6 className="fw-bold text-muted border-bottom pb-1 mb-3">
                  <i className="fas fa-building me-2"></i>Empresa e vaga
                </h6>
                <div className="row g-3 mb-3">
                  <div className="col-md-4">
                    <label className="form-label small fw-bold mb-1">Empresa <span className="text-danger">*</span></label>
                    <input type="text" className="form-control" value={solForm.empresa} onChange={setSol('empresa')} />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label small fw-bold mb-1">
                      Função
                      {refsCliente?.cargos?.length > 0 && (
                        <span className="text-muted fw-normal"> (cadastro da Convenia)</span>
                      )}
                    </label>
                    <input type="text" className="form-control" list="ref-cargos"
                      value={solForm.funcao} onChange={setSol('funcao')} />
                    <datalist id="ref-cargos">
                      {(refsCliente?.cargos || []).map(o => <option key={o.id} value={o.nome} />)}
                    </datalist>
                  </div>
                  <div className="col-md-4">
                    <label className="form-label small fw-bold mb-1">Salário</label>
                    <input type="text" className="form-control" value={solForm.salario} onChange={setSol('salario')} />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label small fw-bold mb-1">
                      Departamento
                      {refsCliente?.departamentos?.length > 0 && (
                        <span className="text-muted fw-normal"> (cadastro da Convenia)</span>
                      )}
                    </label>
                    <input type="text" className="form-control" list="ref-departamentos"
                      value={solForm.departamento} onChange={setSol('departamento')} />
                    <datalist id="ref-departamentos">
                      {(refsCliente?.departamentos || []).map(o => <option key={o.id} value={o.nome} />)}
                    </datalist>
                  </div>
                  <div className="col-md-4">
                    <label className="form-label small fw-bold mb-1">
                      Centro de custo
                      {refsCliente?.centros_custo?.length > 0 && (
                        <span className="text-muted fw-normal"> (cadastro da Convenia)</span>
                      )}
                    </label>
                    <input type="text" className="form-control" list="ref-centros"
                      value={solForm.centro_custo} onChange={setSol('centro_custo')} />
                    <datalist id="ref-centros">
                      {(refsCliente?.centros_custo || []).map(o => <option key={o.id} value={o.nome} />)}
                    </datalist>
                  </div>
                  <div className="col-md-4">
                    <label className="form-label small fw-bold mb-1">Gestor</label>
                    <input type="text" className="form-control" value={solForm.gestor} onChange={setSol('gestor')} />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label small fw-bold mb-1">Filial</label>
                    <input type="text" className="form-control" value={solForm.filial} onChange={setSol('filial')} />
                  </div>
                </div>

                {/* ── Contrato ── */}
                <h6 className="fw-bold text-muted border-bottom pb-1 mb-3">
                  <i className="fas fa-file-signature me-2"></i>Contrato
                </h6>
                <div className="row g-3 mb-3">
                  <div className="col-md-4">
                    <label className="form-label small fw-bold mb-1">Data de admissão</label>
                    <input type="date" className="form-control" value={solForm.data_admissao} onChange={setSol('data_admissao')} />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label small fw-bold mb-1">Vínculo</label>
                    <input type="text" className="form-control" list="ref-vinculos"
                      value={solForm.categoria_trabalhador} onChange={setSol('categoria_trabalhador')}
                      placeholder="CLT" />
                    <datalist id="ref-vinculos">
                      {['CLT', 'Sócio', 'Diretor Estatutário', 'Estágio', 'Aprendiz',
                        'Trabalhador Autônomo', 'Trabalhador Temporário', 'Trabalhador Rural',
                        'Pessoa Jurídica', 'Contrato Intermitente', 'Contrato por Tempo Determinado',
                        'Teletrabalho', 'Cooperado', 'Associado'].map(o => <option key={o} value={o} />)}
                    </datalist>
                  </div>
                  <div className="col-md-4">
                    <label className="form-label small fw-bold mb-1">Horário de trabalho</label>
                    <input type="text" className="form-control" value={solForm.horario_trabalho} onChange={setSol('horario_trabalho')} />
                  </div>
                  {/* Jornada e experiência: a API da Convenia NÃO grava esses dois —
                      ficam destacados aqui porque a operadora preenche na mão lá */}
                  <div className="col-md-4">
                    <label className="form-label small fw-bold mb-1">
                      Jornada (horas mensais)
                      <span className="badge bg-warning text-dark ms-2"
                        title="A API da Convenia não grava este campo — preencher manualmente lá">
                        manual na Convenia
                      </span>
                    </label>
                    {/* select com as jornadas do cliente; o valor atual entra na
                        lista mesmo que não esteja no espelho (solicitação antiga) */}
                    <select className="form-select border-warning"
                      value={solForm.horas_mensais} onChange={setSol('horas_mensais')}>
                      <option value="">Selecione...</option>
                      {[...new Set([
                        ...(refsCliente?.jornadas || []),
                        ...(solForm.horas_mensais ? [solForm.horas_mensais] : []),
                      ])].map(o => (
                        <option key={o} value={o}>{`${String(o).split(':')[0]} h/mês (${o})`}</option>
                      ))}
                    </select>
                  </div>
                  <div className="col-md-4">
                    <label className="form-label small fw-bold mb-1">
                      Período de experiência
                      <span className="badge bg-warning text-dark ms-2"
                        title="A API da Convenia não grava este campo — preencher manualmente lá">
                        manual na Convenia
                      </span>
                    </label>
                    {/* select: primeiro o que o cliente já usa (espelho), depois as
                        demais opções da Convenia; o valor atual sempre entra na lista */}
                    <select className="form-select border-warning"
                      value={solForm.periodo_experiencia} onChange={setSol('periodo_experiencia')}>
                      <option value="">Selecione...</option>
                      {(refsCliente?.periodos_experiencia || []).length > 0 && (
                        <optgroup label="Em uso neste cliente">
                          {refsCliente.periodos_experiencia.map(o => (
                            <option key={`uso-${o}`} value={o}>{o}</option>
                          ))}
                        </optgroup>
                      )}
                      <optgroup label="Demais opções da Convenia">
                        {[...new Set([
                          'Sem período de experiência', '1 x 15 dias', '1 x 30 dias', '1 x 45 dias',
                          '1 x 60 dias', '1 x 90 dias', '2 x 15 dias', '2 x 30 dias', '2 x 45 dias',
                          '30 + 60 dias',
                          ...(solForm.periodo_experiencia ? [solForm.periodo_experiencia] : []),
                        ])].filter(o => !(refsCliente?.periodos_experiencia || []).includes(o))
                          .map(o => <option key={o} value={o}>{o}</option>)}
                      </optgroup>
                    </select>
                  </div>
                  <div className="col-12">
                    <label className="form-label small fw-bold mb-1">Informações adicionais</label>
                    <textarea className="form-control" rows={3}
                      value={solForm.informacoes_adicionais} onChange={setSol('informacoes_adicionais')} />
                  </div>
                </div>

                {(solForm.horas_mensais || solForm.periodo_experiencia) && (
                  <div className="alert alert-warning border-start border-warning border-3 small">
                    <i className="fas fa-hand-point-right me-2"></i>
                    <strong>Preencher na mão na Convenia após criar</strong> (a API não grava):
                    {solForm.horas_mensais && <> Jornada <b>{solForm.horas_mensais}</b>{solForm.periodo_experiencia && ' · '}</>}
                    {solForm.periodo_experiencia && <> Período de experiência <b>{solForm.periodo_experiencia}</b></>}
                  </div>
                )}

                <div className="alert alert-info border-start border-info border-3 mb-0 small">
                  <i className="fas fa-info-circle me-2"></i>
                  Ajuste o que precisar e clique em <strong>Criar admissão na Convenia</strong>: o colaborador
                  recebe o e-mail de preenchimento e entra em <strong>"Aguardando preenchimento"</strong> —
                  a primeira etapa do processo. A solicitação é marcada como processada automaticamente.
                  {!solicitacaoDetalhe.bporh_cliente_id && (
                    <div className="text-danger mt-1">
                      <i className="fas fa-exclamation-triangle me-1"></i>
                      Esta empresa não está vinculada a um cliente da carteira — corrija o nome da empresa
                      e salve, ou cadastre o token em BPO RH → Clientes.
                    </div>
                  )}
                </div>
              </div>

              <div className="modal-footer bg-light">
                <div className="me-auto d-flex align-items-center gap-2 flex-wrap">
                  <button type="button" className="btn btn-secondary" onClick={() => setSolicitacaoDetalhe(null)}>
                    <i className="fas fa-times me-2"></i>
                    Fechar
                  </button>
                  {/* Exclusão em duas etapas: o clique arma a confirmação */}
                  {confirmandoExclusaoSol ? (
                    <>
                      <span className="small text-danger fw-bold">
                        <i className="fas fa-triangle-exclamation me-1"></i>
                        Descarta o pedido do formulário. Não afeta a Convenia.
                      </span>
                      <button type="button" className="btn btn-sm btn-danger"
                        onClick={excluirSolicitacao} disabled={excluindoSol}>
                        {excluindoSol
                          ? <span className="spinner-border spinner-border-sm"></span>
                          : <><i className="fas fa-trash me-1"></i>Confirmar exclusão</>}
                      </button>
                      <button type="button" className="btn btn-sm btn-secondary"
                        onClick={() => setConfirmandoExclusaoSol(false)} disabled={excluindoSol}>
                        Cancelar
                      </button>
                    </>
                  ) : (
                    <button type="button" className="btn btn-sm btn-outline-danger"
                      onClick={() => setConfirmandoExclusaoSol(true)}
                      title="Descarta esta solicitação (teste, duplicata ou envio errado do cliente)">
                      <i className="fas fa-trash me-2"></i>
                      Deletar solicitação
                    </button>
                  )}
                </div>
                <button
                  type="button"
                  className="btn btn-primary"
                  disabled={salvandoSol}
                  onClick={salvarSolicitacao}
                >
                  {salvandoSol ? (
                    <><span className="spinner-border spinner-border-sm me-2"></span>Salvando...</>
                  ) : (
                    <><i className="fas fa-save me-2"></i>Salvar alterações</>
                  )}
                </button>
                <button
                  type="button"
                  className="btn btn-success"
                  disabled={criandoConvenia || !solicitacaoDetalhe.bporh_cliente_id}
                  onClick={criarNaConvenia}
                  title={!solicitacaoDetalhe.bporh_cliente_id
                    ? 'Empresa não vinculada a um cliente com token da Convenia'
                    : 'Cria a admissão na Convenia e envia o e-mail ao colaborador'}
                >
                  {criandoConvenia ? (
                    <><span className="spinner-border spinner-border-sm me-2"></span>Enviando à Convenia...</>
                  ) : (
                    <><i className="fas fa-rocket me-2"></i>Criar admissão na Convenia</>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ─── MODAL: cadastrar colaborador no PontoMais (tarefa Ponto) ─── */}
      {pontoAdmit && (
        <div className="modal" tabIndex="-1"
          style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 10000 }}>
          <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: '640px' }}>
            <div className="modal-content shadow-lg">
              <div className="modal-header" style={{ backgroundColor: '#ffffff', borderBottom: '1px solid #e9ecef' }}>
                <div>
                  <h5 className="modal-title mb-0 fw-bold" style={{ color: '#212529' }}>
                    <i className="fas fa-user-plus me-2"></i>
                    Cadastrar no PontoMais
                  </h5>
                  <small style={{ color: '#6c757d' }}>
                    {empresaSelecionada?.cliente} — dados pré-preenchidos da Convenia
                  </small>
                </div>
                <button type="button" className="btn-close" onClick={() => setPontoAdmit(null)}></button>
              </div>
              <div className="modal-body">
                <div className="row g-3">
                  <div className="col-6">
                    <label className="form-label fw-bold small">Primeiro nome *</label>
                    <input type="text" className="form-control" value={pontoAdmit.first_name}
                      onChange={(e) => setPontoAdmit(m => ({ ...m, first_name: e.target.value }))} />
                  </div>
                  <div className="col-6">
                    <label className="form-label fw-bold small">Sobrenome *</label>
                    <input type="text" className="form-control" value={pontoAdmit.last_name}
                      onChange={(e) => setPontoAdmit(m => ({ ...m, last_name: e.target.value }))} />
                  </div>
                  <div className="col-6">
                    <label className="form-label fw-bold small">CPF *</label>
                    <input type="text" className="form-control" placeholder="000.000.000-00"
                      value={pontoAdmit.cpf}
                      onChange={(e) => setPontoAdmit(m => ({ ...m, cpf: e.target.value }))} />
                  </div>
                  <div className="col-6">
                    <label className="form-label fw-bold small">PIS *</label>
                    <input type="text" className="form-control" value={pontoAdmit.pis}
                      onChange={(e) => setPontoAdmit(m => ({ ...m, pis: e.target.value }))} />
                  </div>
                  <div className="col-6">
                    <label className="form-label fw-bold small">E-mail</label>
                    <input type="email" className="form-control" value={pontoAdmit.email}
                      onChange={(e) => setPontoAdmit(m => ({ ...m, email: e.target.value }))} />
                  </div>
                  <div className="col-6">
                    <label className="form-label fw-bold small">Matrícula</label>
                    <input type="text" className="form-control" value={pontoAdmit.matricula}
                      onChange={(e) => setPontoAdmit(m => ({ ...m, matricula: e.target.value }))} />
                  </div>
                  <div className="col-6">
                    <label className="form-label fw-bold small">Data de admissão *</label>
                    <input type="date" className="form-control" value={pontoAdmit.admission_date}
                      onChange={(e) => setPontoAdmit(m => ({ ...m, admission_date: e.target.value }))} />
                  </div>
                  <div className="col-6">
                    <label className="form-label fw-bold small">Início no controle de ponto</label>
                    <input type="date" className="form-control" value={pontoAdmit.initial_date}
                      onChange={(e) => setPontoAdmit(m => ({ ...m, initial_date: e.target.value }))} />
                    <small className="text-muted">Vazio = mesma data da admissão</small>
                  </div>
                  <div className="col-6">
                    <label className="form-label fw-bold small">Equipe *</label>
                    <select className="form-select" value={pontoAdmit.team_id}
                      onChange={(e) => setPontoAdmit(m => ({ ...m, team_id: e.target.value }))}>
                      <option value="">Selecione...</option>
                      {(pontoOpcoes?.equipes || []).map(o => (
                        <option key={o.id} value={o.id}>{o.nome}</option>
                      ))}
                    </select>
                  </div>
                  <div className="col-6">
                    <label className="form-label fw-bold small">
                      Cargo * <span className="text-muted fw-normal">(da Convenia)</span>
                    </label>
                    <select className={`form-select ${pontoAdmit.job_title_id ? 'border-success' : 'border-danger'}`}
                      value={pontoAdmit.job_title_id}
                      onChange={(e) => setPontoAdmit(m => ({ ...m, job_title_id: e.target.value }))}>
                      <option value="">Selecione...</option>
                      {(pontoOpcoes?.cargos || []).map(o => (
                        <option key={o.id} value={o.id}>{o.nome}</option>
                      ))}
                    </select>
                    <small className={pontoAdmit.job_title_id ? 'text-success' : 'text-danger'}>
                      <i className={`fas ${pontoAdmit.job_title_id ? 'fa-circle-check' : 'fa-circle-exclamation'} me-1`}></i>
                      Convenia: <b>{pontoAdmit.cargo_convenia || '(sem cargo)'}</b>
                    </small>
                  </div>

                  {/* Cargo da Convenia sem correspondência no PontoMais: cadastrar
                      lá com o mesmo nome e recarregar — não subimos sem isso. */}
                  {!pontoAdmit.job_title_id && (
                    <div className="col-12">
                      <div className="alert alert-warning border-start border-warning border-3 mb-0 small">
                        <div className="fw-bold mb-1">
                          <i className="fas fa-triangle-exclamation me-2"></i>
                          O cargo <b>{pontoAdmit.cargo_convenia || '(vazio na Convenia)'}</b> não
                          existe no PontoMais
                        </div>
                        Cadastre esse cargo no PontoMais do {empresaSelecionada?.cliente} com
                        exatamente esse nome e clique em <b>Recarregar cargos</b>.
                        {cargoParecido(pontoOpcoes?.cargos, pontoAdmit.cargo_convenia) && (
                          <div className="mt-1">
                            Lá existe algo parecido:{' '}
                            <b>{cargoParecido(pontoOpcoes?.cargos, pontoAdmit.cargo_convenia)}</b> —
                            se for o mesmo cargo, selecione acima; se não, cadastre o correto.
                          </div>
                        )}
                        <button type="button" className="btn btn-sm btn-outline-primary mt-2"
                          onClick={recarregarCargosPonto} disabled={carregandoPontoAdmit}>
                          <i className={`fas ${carregandoPontoAdmit ? 'fa-spinner fa-spin' : 'fa-rotate'} me-2`}></i>
                          Recarregar cargos
                        </button>
                      </div>
                    </div>
                  )}
                  <div className="col-6">
                    <label className="form-label fw-bold small">Turno (horário de trabalho) *</label>
                    <select className="form-select" value={pontoAdmit.shift_id}
                      onChange={(e) => setPontoAdmit(m => ({ ...m, shift_id: e.target.value }))}>
                      <option value="">Selecione...</option>
                      {(pontoOpcoes?.turnos || []).map(o => (
                        <option key={o.id} value={o.id}>{o.nome}</option>
                      ))}
                    </select>
                  </div>
                  <div className="col-6 d-flex align-items-end">
                    <label className="form-check-label d-inline-flex align-items-center gap-2 mb-2"
                      style={{ cursor: 'pointer' }}>
                      <input type="checkbox" className="form-check-input mt-0"
                        checked={pontoAdmit.is_clt}
                        onChange={(e) => setPontoAdmit(m => ({ ...m, is_clt: e.target.checked }))} />
                      <span className="fw-bold small">É CLT?</span>
                    </label>
                  </div>

                  {/* Registro por QR Code + PIN do relógio. O padrão vem de como a
                      conta do cliente já opera; hoje só o Rubeus usa os dois. */}
                  <div className="col-12">
                    <div className="border rounded p-3" style={{ backgroundColor: '#f7f9fc' }}>
                      <div className="fw-bold small mb-2" style={{ color: '#00205b' }}>
                        <i className="fas fa-qrcode me-2"></i>Registro de ponto
                      </div>
                      <div className="row g-3 align-items-end">
                        <div className="col-6">
                          <label className="form-check-label d-inline-flex align-items-center gap-2"
                            style={{ cursor: 'pointer' }}>
                            <input type="checkbox" className="form-check-input mt-0"
                              checked={pontoAdmit.use_qrcode}
                              onChange={(e) => setPontoAdmit(m => ({
                                ...m,
                                use_qrcode: e.target.checked,
                                // marcar o QR já traz o PIN da regra; desmarcar não
                                // apaga um PIN que o operador digitou de propósito
                                pin: e.target.checked && !m.pin ? pin4DoCpf(m.cpf) : m.pin,
                              }))} />
                            <span className="fw-bold small">Registrar por QR Code</span>
                          </label>
                          <div className="text-muted" style={{ fontSize: '.75rem' }}>
                            {pontoOpcoes?.padrao_registro?.base
                              ? `${pontoOpcoes.padrao_registro.com_qrcode} de `
                                + `${pontoOpcoes.padrao_registro.base} ativos do cliente usam QR`
                              : 'sem espelho para comparar'}
                          </div>
                        </div>
                        <div className="col-6">
                          <label className="form-label fw-bold small mb-1">PIN do relógio</label>
                          <div className="input-group">
                            <input type="text" className="form-control" inputMode="numeric"
                              maxLength={4} placeholder="4 dígitos"
                              value={pontoAdmit.pin}
                              onChange={(e) => setPontoAdmit(m => ({
                                ...m, pin: e.target.value.replace(/\D/g, '').slice(0, 4) }))} />
                            <button type="button" className="btn btn-outline-secondary"
                              title="Preencher com os 4 últimos dígitos do CPF"
                              onClick={() => setPontoAdmit(m => ({ ...m, pin: pin4DoCpf(m.cpf) }))}>
                              <i className="fas fa-id-card"></i>
                            </button>
                          </div>
                          <div className="text-muted" style={{ fontSize: '.75rem' }}>
                            Padrão: 4 últimos dígitos do CPF. Vazio = sem PIN.
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="col-12">
                    <div className="alert alert-light border small mb-0">
                      <i className="fas fa-circle-info me-2 text-primary"></i>
                      O acesso ao sistema é criado com <b>login = CPF</b> e a senha padrão
                      da operação (<b>{(empresaSelecionada?.cliente || 'Empresa').split(' ')[0]}@1234</b>).
                      Configuração de ponto/férias fica com o padrão da conta — ajuste no
                      portal do PontoMais se precisar.
                    </div>
                  </div>
                </div>
              </div>
              <div className="modal-footer bg-light">
                <button type="button" className="btn btn-secondary"
                  onClick={() => setPontoAdmit(null)} disabled={salvandoPontoAdmit}>
                  Cancelar
                </button>
                <button type="button" className="btn btn-success"
                  onClick={salvarPontoAdmit}
                  disabled={salvandoPontoAdmit || !pontoAdmit.job_title_id}
                  title={!pontoAdmit.job_title_id
                    ? 'Cadastre o cargo da Convenia no PontoMais antes de criar o colaborador'
                    : undefined}>
                  <i className={`fas ${salvandoPontoAdmit ? 'fa-spinner fa-spin' : 'fa-user-plus'} me-2`}></i>
                  {salvandoPontoAdmit ? 'Criando...' : 'Criar no PontoMais'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ─── MODAL: cadastrar funcionário no TiqueTaque (tarefa Ponto) ─── */}
      {ttAdmit && (
        <div className="modal" tabIndex="-1"
          style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 10000 }}>
          <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: '640px' }}>
            <div className="modal-content shadow-lg">
              <div className="modal-header" style={{ backgroundColor: '#ffffff', borderBottom: '1px solid #e9ecef' }}>
                <div>
                  <h5 className="modal-title mb-0 fw-bold" style={{ color: '#212529' }}>
                    <i className="fas fa-user-plus me-2"></i>
                    Cadastrar no TiqueTaque
                  </h5>
                  <small style={{ color: '#6c757d' }}>
                    {empresaSelecionada?.cliente} — dados pré-preenchidos da Convenia
                  </small>
                </div>
                <button type="button" className="btn-close" onClick={() => setTtAdmit(null)}></button>
              </div>
              <div className="modal-body">
                <div className="row g-3">
                  <div className="col-12">
                    <label className="form-label fw-bold small">Nome completo *</label>
                    <input type="text" className="form-control" value={ttAdmit.full_name}
                      onChange={(e) => setTtAdmit(m => ({ ...m, full_name: e.target.value }))} />
                  </div>
                  <div className="col-6">
                    <label className="form-label fw-bold small">CPF *</label>
                    <input type="text" className="form-control" placeholder="000.000.000-00"
                      value={ttAdmit.cpf}
                      onChange={(e) => setTtAdmit(m => ({ ...m, cpf: e.target.value }))} />
                  </div>
                  <div className="col-6">
                    <label className="form-label fw-bold small">NIS/PIS *</label>
                    <input type="text" className="form-control" value={ttAdmit.pis}
                      onChange={(e) => setTtAdmit(m => ({ ...m, pis: e.target.value }))} />
                  </div>
                  <div className="col-6">
                    <label className="form-label fw-bold small">E-mail</label>
                    <input type="email" className="form-control" value={ttAdmit.email}
                      onChange={(e) => setTtAdmit(m => ({ ...m, email: e.target.value }))} />
                  </div>
                  <div className="col-6">
                    <label className="form-label fw-bold small">Celular</label>
                    <input type="text" className="form-control" placeholder="(11) 99999-9999"
                      value={ttAdmit.celular}
                      onChange={(e) => setTtAdmit(m => ({ ...m, celular: e.target.value }))} />
                  </div>
                  <div className="col-6">
                    <label className="form-label fw-bold small">UF *</label>
                    <select className="form-select" value={ttAdmit.state}
                      onChange={(e) => {
                        const uf = e.target.value;
                        setTtAdmit(m => ({ ...m, state: uf, city: '' }));
                        carregarCidadesTt(uf, null);
                      }}>
                      <option value="">Selecione...</option>
                      {UFS.map(u => <option key={u} value={u}>{u}</option>)}
                    </select>
                  </div>
                  <div className="col-6">
                    <label className="form-label fw-bold small">Cidade *</label>
                    {ttCidades.length > 0 ? (
                      <select className="form-select" value={ttAdmit.city}
                        onChange={(e) => setTtAdmit(m => ({ ...m, city: e.target.value }))}>
                        <option value="">Selecione...</option>
                        {ttCidades.map(nome => <option key={nome} value={nome}>{nome}</option>)}
                      </select>
                    ) : (
                      <input type="text" className="form-control"
                        placeholder={ttAdmit.state ? 'Digite a cidade...' : 'Escolha a UF primeiro'}
                        value={ttAdmit.city}
                        onChange={(e) => setTtAdmit(m => ({ ...m, city: e.target.value }))} />
                    )}
                    <small className="text-muted">Usada para os feriados do colaborador</small>
                  </div>
                  <div className="col-6">
                    <label className="form-label fw-bold small">Data de admissão</label>
                    <input type="date" className="form-control" value={ttAdmit.admission_date}
                      onChange={(e) => setTtAdmit(m => ({ ...m, admission_date: e.target.value }))} />
                  </div>
                  <div className="col-6">
                    <label className="form-label fw-bold small">Início dos registros</label>
                    <input type="date" className="form-control" value={ttAdmit.records_start_date}
                      onChange={(e) => setTtAdmit(m => ({ ...m, records_start_date: e.target.value }))} />
                    <small className="text-muted">Vazio = mesma data da admissão</small>
                  </div>
                  <div className="col-6">
                    <label className="form-label fw-bold small">Data de nascimento</label>
                    <input type="date" className="form-control" value={ttAdmit.birth_date}
                      onChange={(e) => setTtAdmit(m => ({ ...m, birth_date: e.target.value }))} />
                  </div>
                  <div className="col-6">
                    <label className="form-label fw-bold small">Salário mensal (R$) *</label>
                    <input type="text" className="form-control" placeholder="3500,00"
                      value={ttAdmit.salario}
                      onChange={(e) => setTtAdmit(m => ({ ...m, salario: e.target.value }))} />
                  </div>
                  <div className="col-6">
                    <label className="form-label fw-bold small">Horas mensais *</label>
                    <input type="text" className="form-control" value={ttAdmit.horas_mensais}
                      onChange={(e) => setTtAdmit(m => ({ ...m, horas_mensais: e.target.value }))} />
                    <small className="text-muted">
                      Valor hora: <b>{valorHoraTt(ttAdmit) > 0
                        ? `R$ ${(valorHoraTt(ttAdmit) / 100).toFixed(2).replace('.', ',')}`
                        : '—'}</b> (salário ÷ horas)
                    </small>
                  </div>
                  <div className="col-6">
                    <label className="form-label fw-bold small">Empregador *</label>
                    <select className="form-select" value={ttAdmit.payment_source}
                      onChange={(e) => setTtAdmit(m => ({ ...m, payment_source: e.target.value }))}>
                      <option value="">Selecione...</option>
                      {(ttOpcoes?.empregadores || []).map(o => (
                        <option key={o.id} value={o.id}>{o.nome}</option>
                      ))}
                    </select>
                  </div>
                  <div className="col-6">
                    <label className="form-label fw-bold small">Escala de trabalho *</label>
                    <select className="form-select" value={ttAdmit.work_schedule}
                      onChange={(e) => setTtAdmit(m => ({ ...m, work_schedule: e.target.value }))}>
                      <option value="">Selecione...</option>
                      {(ttOpcoes?.escalas || []).map(o => (
                        <option key={o.id} value={o.id}>{o.nome}</option>
                      ))}
                    </select>
                  </div>
                  <div className="col-6">
                    <label className="form-label fw-bold small">Gestor</label>
                    <select className="form-select" value={ttAdmit.manager}
                      onChange={(e) => setTtAdmit(m => ({ ...m, manager: e.target.value }))}>
                      <option value="">Sem gestor definido</option>
                      {(ttOpcoes?.gestores || []).map(o => (
                        <option key={o.id} value={o.id}>{o.nome}</option>
                      ))}
                    </select>
                  </div>
                  <div className="col-6">
                    <label className="form-label fw-bold small">Unidade(s) de alocação *</label>
                    <MultiSelectBusca
                      options={ttOpcoes?.unidades || []}
                      selected={ttAdmit.sites}
                      onChange={(ids) => setTtAdmit(m => ({ ...m, sites: ids }))}
                      placeholder="Selecione..." />
                  </div>
                  <div className="col-6">
                    <label className="form-label fw-bold small">Definição de banco</label>
                    <select className="form-select" value={ttAdmit.hours_bank_configuration}
                      onChange={(e) => setTtAdmit(m => ({ ...m, hours_bank_configuration: e.target.value }))}>
                      <option value="">Sem definição de banco</option>
                      {(ttOpcoes?.bancos || []).map(o => (
                        <option key={o.id} value={o.id}>{o.nome}</option>
                      ))}
                    </select>
                  </div>
                  <div className="col-6">
                    <label className="form-label fw-bold small">Cargo <span className="text-warning">(portal)</span></label>
                    <input type="text" className="form-control" value={ttAdmit.job_role}
                      onChange={(e) => setTtAdmit(m => ({ ...m, job_role: e.target.value }))} />
                  </div>
                  <div className="col-6">
                    <label className="form-label fw-bold small">Departamento</label>
                    <input type="text" className="form-control" value={ttAdmit.department}
                      onChange={(e) => setTtAdmit(m => ({ ...m, department: e.target.value }))} />
                  </div>
                  <div className="col-12">
                    <label className="form-check-label d-inline-flex align-items-center gap-2"
                      style={{ cursor: 'pointer' }}>
                      <input type="checkbox" className="form-check-input mt-0"
                        checked={ttAdmit.send_app_link}
                        onChange={(e) => setTtAdmit(m => ({ ...m, send_app_link: e.target.checked }))} />
                      <span className="fw-bold small">
                        Enviar instruções do app por e-mail ao colaborador na criação
                      </span>
                    </label>
                  </div>
                  <div className="col-12">
                    <div className="alert alert-light border small mb-0">
                      <i className="fas fa-circle-info me-2 text-primary"></i>
                      As permissões padrão da operação são aplicadas automaticamente (registro app
                      e web com foto, facial, geolocalização obrigatória, assinatura de espelho...).
                    </div>
                  </div>
                </div>
              </div>
              <div className="modal-footer bg-light">
                <button type="button" className="btn btn-secondary"
                  onClick={() => setTtAdmit(null)} disabled={salvandoTtAdmit}>
                  Cancelar
                </button>
                <button type="button" className="btn btn-success"
                  onClick={salvarTtAdmit} disabled={salvandoTtAdmit}>
                  <i className={`fas ${salvandoTtAdmit ? 'fa-spinner fa-spin' : 'fa-user-plus'} me-2`}></i>
                  {salvandoTtAdmit ? 'Criando...' : 'Criar no TiqueTaque'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ─── MODAL: cadastrar pessoa no RHiD (tarefa Ponto) ─── */}
      {rhidAdmit && (
        <div className="modal" tabIndex="-1"
          style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 10000 }}>
          <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: '600px' }}>
            <div className="modal-content shadow-lg">
              <div className="modal-header" style={{ backgroundColor: '#ffffff', borderBottom: '1px solid #e9ecef' }}>
                <div>
                  <h5 className="modal-title mb-0 fw-bold" style={{ color: '#212529' }}>
                    <i className="fas fa-user-plus me-2"></i>
                    Cadastrar no RHiD
                  </h5>
                  <small style={{ color: '#6c757d' }}>
                    {empresaSelecionada?.cliente} — dados pré-preenchidos da Convenia
                  </small>
                </div>
                <button type="button" className="btn-close" onClick={() => setRhidAdmit(null)}></button>
              </div>
              <div className="modal-body">
                <div className="row g-3">
                  <div className="col-12">
                    <label className="form-label fw-bold small">Nome *</label>
                    <input type="text" className="form-control" value={rhidAdmit.name}
                      onChange={(e) => setRhidAdmit(m => ({ ...m, name: e.target.value }))} />
                  </div>
                  <div className="col-6">
                    <label className="form-label fw-bold small">CPF</label>
                    <input type="text" className="form-control" placeholder="000.000.000-00"
                      value={rhidAdmit.cpf}
                      onChange={(e) => setRhidAdmit(m => ({ ...m, cpf: e.target.value }))} />
                  </div>
                  <div className="col-6">
                    <label className="form-label fw-bold small">PIS *</label>
                    <input type="text" className="form-control"
                      style={!String(rhidAdmit.pis || '').replace(/\D/g, '')
                        ? { borderColor: '#dc3545' } : undefined}
                      value={rhidAdmit.pis}
                      onChange={(e) => setRhidAdmit(m => ({ ...m, pis: e.target.value }))} />
                    {!String(rhidAdmit.pis || '').replace(/\D/g, '') && (
                      <div className="form-text" style={{ color: '#a11d2b' }}>
                        <i className="fas fa-triangle-exclamation me-1"></i>
                        <b>Não está preenchido na Convenia.</b> O RHiD não cria sem PIS — peça o
                        número ao cliente e lance na Convenia. Para destravar agora, use o
                        provisório:
                        {rhidOpcoes?.pis_provisorio ? (
                          <button type="button" className="btn btn-sm btn-outline-danger ms-2 py-0"
                            title="Preenche com um PIS provisório de dígito válido, ainda não usado no RHiD deste cliente"
                            onClick={() => setRhidAdmit(m => ({ ...m, pis: rhidOpcoes.pis_provisorio }))}>
                            usar {pisFmt(rhidOpcoes.pis_provisorio)}
                          </button>
                        ) : (
                          <> abra o portal do RHiD em <i>Cadastros → Pessoas</i>, veja um PIS
                            provisório que o cliente já usa e repita o padrão dele aqui.
                          </>
                        )}
                      </div>
                    )}
                    {String(rhidAdmit.pis || '').replace(/\D/g, '') === rhidOpcoes?.pis_provisorio
                      && !!rhidOpcoes?.pis_provisorio && (
                      <div className="form-text" style={{ color: '#8a6d00' }}>
                        <i className="fas fa-hourglass-half me-1"></i>
                        PIS provisório — troque pelo definitivo na Convenia e no RHiD quando sair.
                      </div>
                    )}
                  </div>
                  <div className="col-6">
                    <label className="form-label fw-bold small">Matrícula</label>
                    <input type="text" className="form-control" value={rhidAdmit.registration}
                      onChange={(e) => setRhidAdmit(m => ({ ...m, registration: e.target.value }))} />
                  </div>
                  <div className="col-6 d-flex align-items-end">
                    <label className="form-check-label d-inline-flex align-items-center gap-2 mb-2"
                      style={{ cursor: 'pointer' }}>
                      <input type="checkbox" className="form-check-input mt-0"
                        checked={rhidAdmit.status}
                        onChange={(e) => setRhidAdmit(m => ({ ...m, status: e.target.checked }))} />
                      <span className="fw-bold small">Ativo</span>
                    </label>
                  </div>
                  <div className="col-6">
                    <label className="form-label fw-bold small">Empresa *</label>
                    <select className="form-select" value={rhidAdmit.idCompany}
                      onChange={(e) => setRhidAdmit(m => ({ ...m, idCompany: e.target.value, idDepartment: '' }))}>
                      <option value="">Selecione...</option>
                      {(rhidOpcoes?.empresas || []).map(o => (
                        <option key={o.id} value={o.id}>{o.nome}</option>
                      ))}
                    </select>
                  </div>
                  <div className="col-6">
                    <label className="form-label fw-bold small">Departamento</label>
                    <select className="form-select" value={rhidAdmit.idDepartment}
                      onChange={(e) => setRhidAdmit(m => ({ ...m, idDepartment: e.target.value }))}>
                      <option value="">Selecione...</option>
                      {(rhidOpcoes?.departamentos || [])
                        .filter(d => !rhidAdmit.idCompany || String(d.id_empresa) === String(rhidAdmit.idCompany))
                        .map(o => <option key={o.id} value={o.id}>{o.nome}</option>)}
                    </select>
                  </div>
                  <div className="col-6">
                    <label className="form-label fw-bold small">Cargo</label>
                    <select className="form-select" value={rhidAdmit.cargo}
                      onChange={(e) => setRhidAdmit(m => ({ ...m, cargo: e.target.value }))}>
                      <option value="">Selecione...</option>
                      {(rhidOpcoes?.cargos || [])
                        .filter(c => !rhidAdmit.idCompany || String(c.id_empresa) === String(rhidAdmit.idCompany) || !c.id_empresa)
                        .map(o => <option key={o.id} value={o.id}>{o.nome}</option>)}
                    </select>
                  </div>
                  <div className="col-6">
                    <label className="form-label fw-bold small">Centro de custo <span className="text-warning">(portal)</span></label>
                    <select className="form-select" value={rhidAdmit.centro_custo}
                      onChange={(e) => setRhidAdmit(m => ({ ...m, centro_custo: e.target.value }))}>
                      <option value="">Selecione...</option>
                      {(rhidOpcoes?.centros_custo || []).map(o => (
                        <option key={o.id} value={o.id}>{o.nome}</option>
                      ))}
                    </select>
                  </div>
                  <div className="col-6">
                    <label className="form-label fw-bold small">Superior direto <span className="text-warning">(portal)</span></label>
                    <select className="form-select" value={rhidAdmit.superior}
                      onChange={(e) => setRhidAdmit(m => ({ ...m, superior: e.target.value }))}>
                      <option value="">Selecione...</option>
                      {(rhidOpcoes?.superiores || []).map(o => (
                        <option key={o.id} value={o.id}>{o.nome}</option>
                      ))}
                    </select>
                  </div>
                  <div className="col-6">
                    <label className="form-label fw-bold small">Horário (escala) <span className="text-warning">(portal)</span></label>
                    <select className="form-select" value={rhidAdmit.horario}
                      onChange={(e) => {
                        const opt = (rhidOpcoes?.horarios || []).find(h => String(h.id) === e.target.value);
                        setRhidAdmit(m => ({ ...m, horario: e.target.value, horario_hc: opt ? opt.id_hc : '' }));
                      }}>
                      <option value="">Selecione...</option>
                      {(rhidOpcoes?.horarios || [])
                        .filter(h => !rhidAdmit.idCompany || String(h.id_empresa) === String(rhidAdmit.idCompany) || !h.id_empresa)
                        .map(o => <option key={o.id} value={o.id}>{o.nome}</option>)}
                    </select>
                  </div>
                  <div className="col-4">
                    <label className="form-label fw-bold small">Data da admissão <span className="text-warning">(portal)</span></label>
                    <input type="date" className="form-control" value={rhidAdmit.admission_date}
                      onChange={(e) => setRhidAdmit(m => ({ ...m, admission_date: e.target.value }))} />
                  </div>
                  <div className="col-4">
                    <label className="form-label fw-bold small">Número da folha</label>
                    <input type="text" className="form-control" value={rhidAdmit.num_folha}
                      onChange={(e) => setRhidAdmit(m => ({ ...m, num_folha: e.target.value }))} />
                  </div>
                  <div className="col-4">
                    <label className="form-label fw-bold small">CTPS</label>
                    <input type="text" className="form-control" value={rhidAdmit.ctps}
                      onChange={(e) => setRhidAdmit(m => ({ ...m, ctps: e.target.value }))} />
                  </div>
                  <div className="col-4">
                    <label className="form-label fw-bold small">PIN do relógio</label>
                    <input type="text" className="form-control" placeholder="ex: 1234"
                      value={rhidAdmit.password}
                      onChange={(e) => setRhidAdmit(m => ({ ...m, password: e.target.value }))} />
                    <small className="text-muted">Senha p/ bater ponto</small>
                  </div>
                  <div className="col-4">
                    <label className="form-label fw-bold small">Cartão RFID</label>
                    <input type="text" className="form-control" value={rhidAdmit.rfid}
                      onChange={(e) => setRhidAdmit(m => ({ ...m, rfid: e.target.value }))} />
                  </div>
                  <div className="col-4">
                    <label className="form-label fw-bold small">Crachá / código</label>
                    <input type="text" className="form-control" value={rhidAdmit.code}
                      onChange={(e) => setRhidAdmit(m => ({ ...m, code: e.target.value }))} />
                  </div>
                  <div className="col-12">
                    <label className="form-label fw-bold small">Observações (cartão de ponto)</label>
                    <textarea className="form-control" rows={2} value={rhidAdmit.obs}
                      onChange={(e) => setRhidAdmit(m => ({ ...m, obs: e.target.value }))} />
                  </div>
                  <div className="col-12">
                    <div className="alert alert-warning small mb-0">
                      <i className="fas fa-triangle-exclamation me-2"></i>
                      <b>A criação pela API grava:</b> nome, CPF, PIS, matrícula, empresa,
                      departamento, status, número da folha, CTPS, observações e PIN/cartão.<br />
                      <b>Você precisa finalizar no portal do RHiD:</b> cargo, centro de custo,
                      data de admissão, horário/escala, superior direto e foto/biometria — o
                      RHiD gerencia esses por histórico e não os aceita pela API. Os campos
                      marcados <b>(portal)</b> abaixo servem só de <b>referência</b> (já vêm da
                      Convenia) para você replicar lá.
                    </div>
                  </div>
                </div>
              </div>
              <div className="modal-footer bg-light">
                <button type="button" className="btn btn-secondary"
                  onClick={() => setRhidAdmit(null)} disabled={salvandoRhidAdmit}>
                  Cancelar
                </button>
                <button type="button" className="btn btn-success"
                  onClick={salvarRhidAdmit} disabled={salvandoRhidAdmit}>
                  <i className={`fas ${salvandoRhidAdmit ? 'fa-spinner fa-spin' : 'fa-user-plus'} me-2`}></i>
                  {salvandoRhidAdmit ? 'Criando...' : 'Criar no RHiD'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {avisoExameAdm && empresaSelecionada && (
        <AvisoExameColaborador tipo="admissional" clienteId={empresaSelecionada.cliente_id}
          colaborador={{ convenia_id: avisoExameAdm.convenia_id,
            nome: avisoExameAdm.nome_completo
              || [avisoExameAdm.nome, avisoExameAdm.sobrenome].filter(Boolean).join(' ') || avisoExameAdm.nome }}
          onFechar={() => setAvisoExameAdm(null)}
          onEnviado={(m) => { setAvisoExameAdm(null); setMensagem(`✅ ${m}`); }}
          onErro={(m) => setMensagem(`❌ ${m}`)}
          onAviso={(m) => setMensagem(`✅ ${m}`)} />
      )}
      {planoColab && empresaSelecionada && (
        <PlanoSaudeRegistro clienteId={empresaSelecionada.cliente_id}
          colaborador={{ convenia_id: planoColab.convenia_id,
            nome: [planoColab.nome, planoColab.sobrenome].filter(Boolean).join(' ') || planoColab.nome }}
          onFechar={() => { setPlanoColab(null); carregar(); }}
          onAviso={(m) => setMensagem(`✅ ${m}`)} />
      )}
    </div>
  );
};

export default BpoRhAdmissoes;
